import { gql } from '@apollo/client'

import {
  FRAGMENT_CALL_BEHAVIOR_FIELDS,
  FRAGMENT_CALL_SCORECARD_METHODOLOGY_FIELDS,
  FRAGMENT_TASK_FIELDS,
  FRAGMENT_USER_FIELDS,
  FRAGMENT_USER_DATES_ROLES_FIELDS,
  FRAGMENT_USER_OWNER_FIELDS,
  FRAGMENT_USER_AUTOCOMPLETE_FIELDS,
  FRAGMENT_PAGINATION_META_FIELDS,
  FRAGMENT_EXTERNAL_USER_AUTOCOMPLETE_WITH_ASSIGNMENTS_FIELDS,
  FRAGMENT_TEMPLATE_TASK_FIELDS,
  FRAGMENT_TEMPLATE_CATEGORY_FOLDER,
  FRAGMENT_TEMPLATE_TASK_DATE_SETTINGS,
  FRAGMENT_COMMUNICATION_SCHEDULES,
  FRAGMENT_COMMUNICATION_SCHEDULES_WITHOUT_PREVIEW,
  FRAGMENT_LOCATION_HIERARCHY_FIELDS,
  FRAGMENT_EVENT_WORKFLOW_FIELDS,
  FRAGMENT_GROUP_TASK_FIELDS,
  FRAGMENT_COHORT_EMAIL_FIELDS,
  FRAGMENT_COHORT_SCHEDULE_FIELDS,
  FRAGMENT_TEMPLATE_CATEGORY_WITH_CHILDREN,
  FRAGMENT_TEMPLATE_CATEGORY_FOR_SECTIONS,
  FRAGMENT_TEMPLATE_TASK_FOR_SECTION,
  FRAGMENT_TEMPLATE_CATEGORY_HIERARCHY_FOR_FILTERS,
  FRAGMENT_TEMPLATE_CATEGORY_HIERARCHY_FOR_ROLES_AND_DATES,
  FRAGMENT_SNIPPET_FIELDS,
  FRAGMENT_SNIPPET_CONDITIONS_FIELDS,
  FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS,
  FRAGMENT_BADGE_FIELDS,
  FRAGMENT_CERTIFICATE_ARTWORK_FIELDS,
  FRAGMENT_CERTIFICATE_FIELDS,
  FRAGMENT_AWARD_CONDITION_FIELDS,
  FRAGMENT_ASSIGNED_BADGE_FIELDS,
  FRAGMENT_USER_BADGE_FIELDS,
  FRAGMENT_SALES_AUTOMATION_SETTING_FIELDS,
  PUBLIC_EMPLOYEE_FORM_FIELDS,
} from 'graphql/fragments'

export const HAS_ERROR = gql`
  query HasError {
    hasError @client
  }
`

export const HAS_SUCCESS = gql`
  query HasSuccess {
    hasSuccess @client
  }
`

export const IS_LOGGED_IN = gql`
  query IsLoggedIn {
    isLoggedIn @client
  }
`

export const GET_LOCAL_CACHED_DATA = gql`
  query LocalCachedData {
    hasError
    hasSuccess
    isLoggedIn
  }
`

export const GET_CLIENT_CONFIG = gql`
  query ClientConfig {
    clientConfig {
      showBillingPortal
      showLocale
      showClientPages
      disableEmailTaskType
      ssoOrgId
      workosOrganizationId
      disablePermissions
      enableSessionTracking
      hrisIntegrationSystem
      showDocusignTab
      showNotificationOptOut
      showApiKeyTab
      showUpnAndEmployeeIdInputs
      enableFilterByTaxonomies
      enableTaxonomyOrMatching
      enableTaxonomyBlankFilter
      showDelegable
      showUserTemplateRoleAssignment
      showConfidentialNewHire
      hideExportEmployees
      enableMultipleTaxonomyTermsOnEmployee
      showAnalyticsOnboardingBuddy
      enableSendsafely
      showZoomIntegration
      disableContentLibrary
      enableContentHub
      enableSidekick
      enableInternalRooms
      enableFieldNotes
      enableCallScorecards
      customFields {
        key
        name
      }
      enableSelectHrisIntegrationSystem
      enableDocebo
      disableSingleTaskSurvey
      enableEditButton
      showCalendarResource
      multipleCalendarAccount
      alwaysShowBothDayTypes
      useExternalUsersForRoleAssignments
      slackEnterprise
      slackCanvasAvailable
      excludeSlackSendMessageToUser
      excludeSlackSendMessageToChannel
      excludeSlackChannelInvite
      excludeSlackGroupInvite
      excludeSlackKickUser
      customEmailService
      enableNewHome
      enableAiRoleplay
      enableReportAiChat
      enableStandaloneAssignment
      enableEnablementPartners
      aiChatModel
      enableRoleplayPromptOptimizer
      enableAiPersonaHandoff
      enableAiMultiPersona
      enableNotes
      hideExcludeSsoToggle
      meetingPrepRoleplayEnabled
      showCoursesToEmployees
      newCourseWindowMonths
    }
  }
`

export const GET_SLACK_INSTALL_PATH = gql`
  query SlackInstallPath {
    slackInstallPath {
      installPath
      reauthorizePath
      alreadyInstalled
      installedAt
      workspaceName
      connectedUsersCount
      capabilities {
        dmEmployees
        postToChannels
        customSender
        addToChannels
        joinPublicChannels
        createChannels
        channelCanvas
        removeFromChannels
      }
    }
  }
`

export const GET_WELCOME_EMAIL_SETTINGS = gql`
  query ClientWelcomeEmail($emailType: String!) {
    clientWelcomeEmail(emailType: $emailType) {
      clientId
      text
      subject
      daysBeforeStartDate
      timezone
      sendAt
      ccTo {
        label
        value
        type
      }
      from {
        label
        value
        type
      }
    }
  }
`

export const GET_CLIENT_BILLING_PORTAL = gql`
  query clientBillingPortal {
    clientBillingPortal {
      sessionUrl
    }
  }
`

export const GET_CLIENT_API_KEY = gql`
  query clientApiKey {
    clientApiKey {
      apiKey
    }
  }
`

export const GET_MCP_TOKENS = gql`
  query mcpTokens {
    mcpTokens {
      id
      name
      tokenPreview
      lastUsedAt
      createdAt
      allowedTools
      ownerId
      ownerName
      servers
    }
  }
`

export const GET_AVAILABLE_MCP_TOOLS = gql`
  query availableMcpTools {
    availableMcpTools {
      name
      description
      group
      readOnly
    }
  }
`

export const GET_CURRENT_USER = gql`
  query User {
    user {
      id
      isAdmin: isAdminByClient
      fullName
      email
      role {
        id
        name
        roleType
        permissions {
          id
          title
          entityType
          entityId
        }
      }
      clientId
      mainClient {
        id
        name
        isEmailFromVerified
        timezone
        blackoutDates
      }
      preferences {
        adminTrainingWatched
        showInactiveTemplates
        usersTableState
        templateTasksTableState
        templatePreviewFilters
        templatePreviewColumns
        templateListFilters
        showEnrollableTemplates
        templateFolderSidebarOpen
        templatesListColumns
        templateTasksColumns
        templatesListColumnWidths
        templateTasksColumnWidths
        enrollmentsColumns
        reportUserStatisticsColumns
        reportCourseStatisticsColumns
        enrollmentDateFilter
        includeTerminatedEmployees
        includeAutomationTasks
        globalReportUserStatisticsColumns
        globalReportTaskStatisticsColumns
        globalReportFilters
        courseReportCohortDates
        badgesAnalyticsColumnWidths
        badgesAnalyticsColumnVisibility
        badgesAnalyticsColumnOrder
        badgesAnalyticsFilters
        contentHubListFilters
        contentHubListColumns
      }
      clientAncestry {
        id
        name
      }
      impersonationContext {
        impersonating
        expiresAt
        impersonatedBy {
          id
          fullName
          email
        }
      }
      # ENGG-10414: flags Tangelo staff in product analytics (is_tangelo_staff).
      dsrSuperAdminSession
    }
  }
`

export const GET_USER_INFO_BY_EMAIL = gql`
  query UserInfo($email: String!) {
    userInfo(email: $email) {
      id
      email
      firstName
      lastName
      fullName
      isTemp
      clientId
      upn
      externalId
    }
  }
`

export const GET_USER_LOCATION_HIERARCHIES = gql`
  query UserLocationHierarchies($userId: ID!, $includeDeleted: Boolean) {
    userLocationHierarchies(userId: $userId, includeDeleted: $includeDeleted) {
      ownedLocationHierarchies {
        ...LocationHierarchyFields
      }
      assignedLocationHierarchies {
        ...LocationHierarchyFields
      }
      deletedLocationHierarchies {
        ...LocationHierarchyFields
      }
      eventLocationHierarchies {
        ...EventWorkflowFields
      }
    }
  }
  ${FRAGMENT_LOCATION_HIERARCHY_FIELDS}
  ${FRAGMENT_EVENT_WORKFLOW_FIELDS}
`

export const GET_LOCATION_HIERARCHIES = gql`
  query LocationHierarchies($limitToAssignmentsIfAvail: Boolean!) {
    locationHierarchies(limitToAssignmentsIfAvail: $limitToAssignmentsIfAvail) {
      id
      title
    }
  }
`

export const GET_TASK_ANALYTICS_HOME_QUERY = gql`
  query TasksAnalytics(
    $date: ISO8601Date!
    $range: String
    $isCustomer: Boolean
    $query: String
    $sort: SortInput
    $pagination: PaginationInput
  ) {
    tasksAnalytics(
      date: $date
      range: $range
      isCustomer: $isCustomer
      query: $query
      sort: $sort
      pagination: $pagination
    ) {
      columns
      tree
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_ANALYTICS_HOME_QUERY = gql`
  query SessionsLocations($startDate: ISO8601Date!) {
    sessionsLocations(startDate: $startDate) {
      assignedLocations {
        id
        fullHierarchyTitle
        state
        rootNode {
          owner {
            ...UserOwnerFields
          }
        }
      }
      managerialLocations {
        id
        fullHierarchyTitle
        state
        rootNode {
          owner {
            ...UserOwnerFields
          }
        }
      }
    }
    snippets {
      ...SnippetFields
    }
  }
  ${FRAGMENT_USER_OWNER_FIELDS}
  ${FRAGMENT_SNIPPET_FIELDS}
`

export const GET_TASKS = gql`
  query Tasks($locationHierarchyId: ID!) {
    tasks(locationHierarchyId: $locationHierarchyId) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

// ENGG-9885: the Employee-page tree's session chips (a pinned session task's
// lock, a picker's "Chose <date>"), fetched apart from GET_TASKS by
// useTaskSessionChoices. GraphQL fails a whole operation on an unknown field,
// and the admin deploys on merge while the backend deploys separately; in
// their own query, a backend without these fields (or a failing per-row
// resolver) only loses the chips instead of emptying the tree.
export const GET_TASK_SESSION_CHOICES = gql`
  query TaskSessionChoices($locationHierarchyId: ID!) {
    tasks(locationHierarchyId: $locationHierarchyId) {
      id
      # ENGG-10274: tells a session picker apart (see isSessionPicker). The
      # type too: a task switched off calendar keeps its calendarEventType.
      taskType
      calendarEventType
      pinnedSession {
        startAt
        timezone
        cancelled
      }
      pinnedByPickerTitle
      # ENGG-10286: an admin's move pins it, not the employee's choice.
      pinnedByMove
      selectedSession {
        title
        startAt
        timezone
        cancelled
      }
    }
  }
`

// Lazy per-task fetch for the Notifications status timeline (opened on demand,
// one task at a time — avoids an N+1 across the tree).
export const GET_TASK_NOTIFICATION_STATUSES = gql`
  query TaskNotificationStatuses($id: ID!) {
    task(id: $id) {
      id
      notificationStatuses {
        configId
        triggerType
        direction
        offsetValue
        offsetUnit
        fixedSendDateDetailed {
          date
          time
          timezone
        }
        mediums
        message
        status
        scheduledFor
        sentAt
        sentTo
        channels
        recipients {
          label
          value
          type
        }
      }
    }
  }
`

export const GET_LOCATION_HIERARCHY = gql`
  query LocationHierarchy($locationId: ID!) {
    locationHierarchy(locationId: $locationId) {
      id
      templateCategoryId
      fullHierarchyTitle
      rootNode {
        owner {
          ...UserOwnerFields
        }
      }
    }
  }
  ${FRAGMENT_USER_OWNER_FIELDS}
`

export const GET_CUSTOMERS = gql`
  query Customers {
    customers {
      id
      title
      state
    }
  }
`

export const GET_USER = gql`
  query User($id: ID!) {
    user(userId: $id) {
      ...UserFields
    }
  }
  ${FRAGMENT_USER_FIELDS}
`

export const GET_EMPLOYEE_INFORMATION = gql`
  query User($id: ID!) {
    user(userId: $id) {
      ...UserFields
    }
  }
  ${FRAGMENT_USER_FIELDS}
`

export const GET_BASIC_USER = gql`
  query User($id: ID!) {
    user(userId: $id) {
      id
      fullName
      email
      personalEmail
    }
  }
`

export const GET_USER_DATES_AND_ROLES = gql`
  query User($id: ID!) {
    user(userId: $id) {
      ...UserDatesAndRolesFields
    }
  }
  ${FRAGMENT_USER_DATES_ROLES_FIELDS}
`

export const GET_USERS_WITH_OWNED_LOCATIONS = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $filterModel: FilterModelInput
    $sortModel: [SortModelInput!]
  ) {
    users(
      query: $query
      pagination: $pagination
      filterModel: $filterModel
      sortModel: $sortModel
    ) {
      data {
        ...UserFields
        ownedTemplates {
          id
          internalCategory
        }
        bulkAssignErrorCount
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_USER_FIELDS}
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_USERS = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $filterModel: FilterModelInput
    $sortModel: [SortModelInput!]
  ) {
    users(
      query: $query
      pagination: $pagination
      filterModel: $filterModel
      sortModel: $sortModel
    ) {
      data {
        ...UserFields
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_USER_FIELDS}
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_USERS_IDS = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $filterModel: FilterModelInput
    $sortModel: [SortModelInput!]
  ) {
    users(
      query: $query
      pagination: $pagination
      filterModel: $filterModel
      sortModel: $sortModel
    ) {
      data {
        id
      }
    }
  }
`

// Lookup users by ID with only { id, email } selected — used to rehydrate
// the "Work Email" chips inside the Users filter modal from a `?users=` URL
// param, so an admin returning to a shareable link sees which users their
// filter actually resolved to.
export const GET_USERS_BY_IDS = gql`
  query UsersByIds($filterModel: FilterModelInput) {
    users(filterModel: $filterModel, pagination: { paginate: false }) {
      data {
        id
        email
      }
    }
  }
`

export const GET_USERS_FOR_BULK_TEMPLATE_ACTIONS = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $filterModel: FilterModelInput
    $sortModel: [SortModelInput!]
    $nonTerminated: Boolean
  ) {
    users(
      query: $query
      pagination: $pagination
      filterModel: $filterModel
      sortModel: $sortModel
      nonTerminated: $nonTerminated
    ) {
      data {
        id
        fullName
        email
      }
    }
  }
`

export const GET_USERS_WITH_BASIC_FIELDS = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $filterModel: FilterModelInput
    $sortModel: [SortModelInput!]
  ) {
    users(
      query: $query
      pagination: $pagination
      filterModel: $filterModel
      sortModel: $sortModel
    ) {
      data {
        id
        fullName
        email
      }
    }
  }
`

export const GET_ALL_USERS = gql`
  query Users($query: String) {
    users(query: $query, pagination: { paginate: false }) {
      data {
        ...UserFields
      }
    }
  }
  ${FRAGMENT_USER_FIELDS}
`

export const GET_USERS_AUTOCOMPLETE = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $nonTerminated: Boolean
  ) {
    users(
      query: $query
      pagination: $pagination
      nonTerminated: $nonTerminated
    ) {
      data {
        ...UserAutocompleteFields
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_USER_AUTOCOMPLETE_FIELDS}
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_USERS_WITH_SNIPPETS = gql`
  query Users(
    $query: String
    $pagination: PaginationInput
    $filterModel: FilterModelInput
    $sortModel: [SortModelInput!]
  ) {
    users(
      query: $query
      pagination: $pagination
      filterModel: $filterModel
      sortModel: $sortModel
    ) {
      data {
        id
        fullName
        email
        snippets {
          name
          snippetType
          value
        }
      }
    }
  }
`

export const GET_PAGES = gql`
  query Pages {
    contentPages(showAll: true) {
      id
      title
      taxonomyTermId
    }
  }
`

export const GET_PAGE = gql`
  query Pages($pageId: ID!) {
    contentPage(pageId: $pageId) {
      id
      slug
      title
      iconLibraryId
      iconUrl
      sections {
        id
        sectionType
        hidden
        content
        contentRaw
        pictureUrl
        icon1Url
        icon2Url
        icon3Url
        icon4Url
        icon5Url
        icon6Url
        icon7Url
        icon8Url
        icon9Url
        icon10Url
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_FOR_DUPLICATION = gql`
  query TemplateTasks($categoryId: ID!) {
    templateTasks(categoryId: $categoryId) {
      id
      internalName
      title
      inactive
      position
      templateCategory {
        id
        category
        internalCategory
        inactive
        position
        __typename
      }
      __typename
    }
  }
`

export const GET_TEMPLATE_TASKS = gql`
  query TemplateTasks($categoryId: ID!) {
    templateTasks(categoryId: $categoryId) {
      ...TemplateTaskFields
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

export const GET_ALL_TEMPLATE_TASKS = gql`
  query AllTemplateTasks($showInactive: Boolean) {
    allTemplateTasks(showInactive: $showInactive) {
      id
      title
      taskType
      internalName
      position
      questions {
        id
        title
        required
        description
        explanation
        order
        quizType
        updatedAt
        order
        templateTaskQuestionSectionId
        dependsOnQuestionOptionIds
        options {
          id
          order
          title
          description
          score
          correct
        }
      }
      templateCategory {
        id
        category
        internalCategory
        position
        inactive
        parent {
          id
          category
          internalCategory
          position
          inactive
        }
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_FOR_REPORTS = gql`
  query TemplateTasksForReports($showInactive: Boolean) {
    allTemplateTasks(showInactive: $showInactive) {
      id
      title
      taskType
      internalName
      position
      templateCategory {
        id
        category
        internalCategory
        position
        parent {
          id
          category
          internalCategory
          position
        }
      }
      questions {
        id
        title
        order
      }
    }
  }
`

export const SEARCH_TEMPLATE_TASKS = gql`
  query AllTemplateTasks(
    $query: String
    $showInactive: Boolean
    $taxonomyTerms: [ID!]
    $excludedTaxonomyTerms: [ID!]
    $taskTypes: [String!]
  ) {
    allTemplateTasks(
      query: $query
      showInactive: $showInactive
      taxonomyTerms: $taxonomyTerms
      excludedTaxonomyTerms: $excludedTaxonomyTerms
      taskTypes: $taskTypes
    ) {
      id
      title
      internalName
      workflow {
        id
        category
        internalCategory
      }
      section {
        id
        category
        internalCategory
      }
      taxonomyTerms {
        id
        title
        key
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
    }
  }
`

export const GET_DEPENDS_ON_TEMPLATE_TASKS = gql`
  query TemplateTasks($categoryId: ID!) {
    templateTasks(categoryId: $categoryId) {
      id
      title
      taskType
      calendarEventType
      internalName
      inactive
      draft
      templateCategory {
        id
        category
        internalCategory
        inactive
      }
      questions {
        id
        title
        quizType
        options {
          id
          title
          correct
          score
        }
      }
      chatPassingScore
      chatMaxScore
    }
  }
`

export const GET_TEMPLATE_TASK = gql`
  query TemplateTask($id: ID!) {
    templateTask(id: $id) {
      ...TemplateTaskFields
      coveredByCohortSchedule
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

// ENGG-9885: a SESSION_PICKER's saved sessions (its Depends On aside) with no
// upcoming date for the employee to choose. Its own query rather than a
// GET_TEMPLATE_TASK field, so the drawer refetches it after a save without
// re-seeding its form from a refetched task. A backend without the field
// rejects it, and the global error link then shows the error toast on every
// picker drawer, so the backend must be deployed first.
export const GET_UNSCHEDULED_SESSION_TEMPLATE_TASK_IDS = gql`
  query UnscheduledSessionTemplateTaskIds($id: ID!) {
    templateTask(id: $id) {
      id
      unscheduledSessionTemplateTaskIds
    }
  }
`

export const GET_TEMPLATE_CATEGORIES = gql`
  query TemplateCategories(
    $templateCategoryFolderId: ID
    $templateCategoriesWithoutFolder: Boolean
    $filters: TemplateCategoryFiltersInput
  ) {
    templateCategories(
      templateCategoryFolderId: $templateCategoryFolderId
      templateCategoriesWithoutFolder: $templateCategoriesWithoutFolder
      filters: $filters
    ) {
      id
      position
      category
      categoryRaw
      internalCategory
      notifyOnCantComplete {
        label
        value
        type
      }
      notifyOnFacilitatorDecline {
        label
        value
        type
      }
      createdAt
      inactive
      expireAfterDays
      timeToComplete
      templateTags {
        id
        name
        color
      }
      owner {
        ...UserFields
      }
      templateCategoryFolder {
        id
        title
      }
      disableNewAssignmentEmails
      disableTaskNotes
      disableCantComplete
      isEnrollable
      enrollmentDateCohortId
      enrollmentDateCohort {
        id
        name
        dateCohortDates {
          id
          name
          date
          toDate
          capacity
          enrolledCount
          availableSlots
        }
      }
      enrollmentCapacity
      autoReimport
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
      imageUrl
      description
      shortDescription
      whatYouWillLearn
      isFeatured
      isRecommended
      courseContentType {
        id
        name
      }
      courseFormat {
        id
        name
      }
      courseLevel {
        id
        name
      }
      courseSkills {
        id
        name
      }
      courseCategories {
        id
        name
      }
    }
  }
  ${FRAGMENT_USER_FIELDS}
`

export const GET_PAGINATED_TEMPLATE_CATEGORIES = gql`
  query PaginatedTemplateCategories(
    $templateCategoryFolderId: ID
    $templateCategoriesWithoutFolder: Boolean
    $filters: TemplateCategoryFiltersInput
    $filterModel: FilterModelInput
    $pagination: PaginationInput
  ) {
    paginatedTemplateCategories(
      templateCategoryFolderId: $templateCategoryFolderId
      templateCategoriesWithoutFolder: $templateCategoriesWithoutFolder
      filters: $filters
      filterModel: $filterModel
      pagination: $pagination
    ) {
      data {
        ...TemplateCategoryListFields
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS}
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_TEMPLATE_CATEGORIES_WITH_CHILDREN = gql`
  query TemplateCategories(
    $templateCategoryFolderId: ID
    $templateCategoriesWithoutFolder: Boolean
  ) {
    templateCategories(
      templateCategoryFolderId: $templateCategoryFolderId
      templateCategoriesWithoutFolder: $templateCategoriesWithoutFolder
    ) {
      id
      position
      category
      categoryRaw
      internalCategory
      notifyOnCantComplete {
        label
        value
        type
      }
      notifyOnFacilitatorDecline {
        label
        value
        type
      }
      createdAt
      inactive
      expireAfterDays
      timeToComplete
      templateTags {
        id
        name
        color
      }
      owner {
        ...UserFields
      }
      templateCategoryFolder {
        id
        title
      }
      disableNewAssignmentEmails
      disableTaskNotes
      disableCantComplete
      isEnrollable
      enrollmentDateCohortId
      enrollmentDateCohort {
        id
        name
        dateCohortDates {
          id
          name
          date
          toDate
          capacity
          enrolledCount
          availableSlots
        }
      }
      enrollmentCapacity
      autoReimport
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
      templateTasksCount(showInactive: true)
      children {
        id
        position
        category
        categoryRaw
        internalCategory
        templateTasksCount(showInactive: true)
        parent {
          id
          internalCategory
        }
      }
    }
  }
  ${FRAGMENT_USER_FIELDS}
`

export const GET_TEMPLATE_CATEGORY = gql`
  query TemplateCategories($templateCategoryId: ID!) {
    templateCategories(templateCategoryId: $templateCategoryId) {
      ...TemplateCategoryListFields
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS}
`

export const GET_LOCATION_HIERARCHY_NOTES = gql`
  query LocationHierarchyNotes($locationHierarchyId: ID!) {
    locationHierarchyNotes(locationHierarchyId: $locationHierarchyId)
  }
`

// Tasks under the given workflows whose title or internal name contains the
// text, with the section each sits in (employee drawer workflow search).
export const LOCATION_HIERARCHY_TASK_SEARCH = gql`
  query LocationHierarchyTaskSearch(
    $locationHierarchyIds: [ID!]!
    $query: String!
  ) {
    locationHierarchyTaskSearch(
      locationHierarchyIds: $locationHierarchyIds
      query: $query
    ) {
      taskId
      locationHierarchyId
    }
  }
`

export const GET_LOCATION_HIERARCHY_TASKS = gql`
  query LocationHierarchyTasks(
    $locationHierarchyId: ID!
    $includeChildLocations: Boolean
    $fromRootNode: Boolean
  ) {
    locationHierarchyTasks(
      locationHierarchyId: $locationHierarchyId
      includeChildLocations: $includeChildLocations
      fromRootNode: $fromRootNode
    ) {
      id
      title
      internalName
      taskType
      assignments {
        id
        userId
        templateRoleId
      }
      locationHierarchyId
    }
  }
`

export const GET_MULTIPLE_OPTIONS = gql`
  query MultipleOptions {
    multipleOptions {
      id
      clientId
      options {
        id
        option
      }
    }
  }
`

export const GET_TEMPLATE_ROLES = gql`
  query TemplateRoles($showHidden: Boolean, $onlyAssignable: Boolean) {
    templateRoles(showHidden: $showHidden, onlyAssignable: $onlyAssignable) {
      id
      name
      isAssignable
      slug
    }
  }
`

// Roles settings tab: lists every role with the objects that reference it and
// whether it is safe to delete.
// Roles settings tab: fast first load — just the role names. Usage + deletable
// are fetched per-row via GET_TEMPLATE_ROLE_USAGE so the expensive per-role
// reference counts don't block the initial render.
export const GET_TEMPLATE_ROLES_BASIC = gql`
  query TemplateRolesBasic {
    templateRoles(showHidden: true) {
      id
      name
      isManager
      isAssignable
    }
  }
`

// Async per-row load: the objects a single role is associated with (each sample
// carries enough identity to link to it) and whether it can be deleted.
export const GET_TEMPLATE_ROLE_USAGE = gql`
  query TemplateRoleUsage($id: ID!) {
    templateRole(id: $id) {
      id
      deletable
      usages {
        category
        count
        samples {
          id
          label
          kind
          parentId
        }
      }
    }
  }
`

export const GET_DATE_TYPES = gql`
  query DateTypes {
    dateTypes {
      id
      name
      baseDateTypeId
      baseDateType {
        id
        name
      }
      clientId
      slug
      targetDateCohort {
        id
        name
        # Needed to tell an independent cohort's target date type apart from a
        # regular one's: an independent cohort has no base date, so a "pick a
        # cohort date" control is wrong for it unless it has enrollment sessions.
        independent
        dateCohortDates {
          id
          name
          toDate
          date
          blackoutDates
        }
      }
    }
  }
`

export const DATE_TYPES_FOR_TEMPLATE_CATEGORY = gql`
  query DateTypesForTemplateCategory(
    $templateCategoryId: ID!
    $categoryId: ID
  ) {
    dateTypesForTemplateCategory(
      templateCategoryId: $templateCategoryId
      categoryId: $categoryId
    ) {
      id
      name
      dates {
        date
        dateName
        dateTypeId
        dateTypeName
        isSelected
      }
    }
  }
`

export const IS_DUE_DATE_TYPE_NON_TASK_RELATED = gql`
  query IsDueDateTypeNonTaskRelated($templateTaskId: ID!) {
    isDueDateTypeNonTaskRelated(templateTaskId: $templateTaskId)
  }
`

// ENGG-9052: step one of the two-step Force Import flow — the assign/due dates the import would
// calculate for the chosen employee, so the admin can confirm or override them before importing.
export const FORCE_IMPORT_TEMPLATE_TASK_DATES = gql`
  query ForceImportTemplateTaskDates($templateTaskId: ID!, $userId: ID!) {
    forceImportTemplateTaskDates(
      templateTaskId: $templateTaskId
      userId: $userId
    ) {
      assignDate {
        date
        time
        timezone
      }
      dueDate {
        date
        time
        timezone
      }
      assignDateMissing
      dueDateMissing
      hasAssignDateSetting
      hasDueDateSetting
      missingDateNames
    }
  }
`

// ENGG-8840: per-task date preview for the whole-section Force Import review step.
export const FORCE_IMPORT_TEMPLATE_SECTION_DATES = gql`
  query ForceImportTemplateSectionDates(
    $templateCategoryId: ID!
    $userId: ID!
  ) {
    forceImportTemplateSectionDates(
      templateCategoryId: $templateCategoryId
      userId: $userId
    ) {
      templateTaskId
      title
      hasDependency
      dependsOnTemplateTaskTitle
      assignDate {
        date
        time
        timezone
      }
      dueDate {
        date
        time
        timezone
      }
      assignDateMissing
      dueDateMissing
      hasAssignDateSetting
      hasDueDateSetting
      missingDateNames
    }
  }
`

export const TEMPLATE_CATEGORY_GROUP_EVENT_DATES = gql`
  query TemplateCategoryGroupEventDates(
    $templateCategoryId: ID!
    $categoryId: ID
    $dateFilter: [DateTypeFilterInput!]
  ) {
    templateCategoryGroupEventDates(
      templateCategoryId: $templateCategoryId
      categoryId: $categoryId
      dateFilter: $dateFilter
    ) {
      date
      dateName
      dateTypeId
      dateTypeName
      isSelected
    }
  }
`

export const TEMPLATE_CATEGORY_SECTIONS_BY_DATE_SUMMARY = gql`
  query TemplateCategorySectionsByDateSummary(
    $templateCategoryId: ID!
    $categoryId: ID
    $dateFilter: [DateTypeFilterInput!]
  ) {
    templateCategorySectionsByDateSummary(
      templateCategoryId: $templateCategoryId
      categoryId: $categoryId
      dateFilter: $dateFilter
    ) {
      selectedDatesUserCount
      dates {
        date
        dateName
        dateTypeId
        dateTypeName
        isSelected
      }
      groups {
        dateTypes {
          id
          name
        }
        userCount
        date
        dateName
        sections {
          date
          dateName
          section {
            id
            category
            position
          }
          taskCount
          userCount
        }
      }
    }
  }
`

export const GROUP_TASKS_FOR_SECTION = gql`
  query GroupTasksForSection(
    $templateCategoryId: ID!
    $date: ISO8601Date!
    $dateTypeIds: [ID!]!
  ) {
    groupTasksForSection(
      templateCategoryId: $templateCategoryId
      date: $date
      dateTypeIds: $dateTypeIds
    ) {
      section {
        id
        category
        position
      }
      date
      dateTypes {
        id
        name
      }
      taskCount
      entries {
        groupTask {
          id
          baseDateValue
          latestCalendarEvent {
            id
            title
            summary
            start
            startAt
            end
            duration
            timezone
            facilitators
            attendees {
              email
              name
            }
            status
          }
        }
        templateTask {
          id
          title
          internalName
        }
      }
    }
  }
`

export const USERS_BY_DATE_FOR_TEMPLATE_CATEGORY = gql`
  query UsersByDateForTemplateCategory(
    $templateCategoryId: ID!
    $categoryId: ID
    $pagination: PaginationInput
    $dateFilter: [DateTypeFilterInput!]!
  ) {
    usersByDateForTemplateCategory(
      templateCategoryId: $templateCategoryId
      categoryId: $categoryId
      pagination: $pagination
      dateFilter: $dateFilter
    ) {
      totalAssignedCount
      totalEligibleCount
      totalNotEligibleCount
      groups {
        date
        dateName
        dateTypeId
        totalUsers
        assigned {
          id
          firstName
          lastName
          fullName
          email
          userTaskStatus {
            label
            type
          }
        }
        eligible {
          id
          firstName
          lastName
          fullName
          email
          userTaskStatus {
            label
            type
          }
        }
        notEligible {
          id
          firstName
          lastName
          fullName
          email
          userTaskStatus {
            label
            type
          }
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const USERS_BY_DATE_FOR_TASK = gql`
  query UsersByDateForTask(
    $templateTaskId: ID!
    $dateTypeId: ID!
    $pagination: PaginationInput
    $dateFilter: [ISO8601Date!]
  ) {
    usersByDateForTask(
      templateTaskId: $templateTaskId
      dateTypeId: $dateTypeId
      pagination: $pagination
      dateFilter: $dateFilter
    ) {
      totalAssignedCount
      totalEligibleCount
      totalNotEligibleCount
      dates {
        date
        dateName
        dateTypeId
        dateTypeName
        isSelected
      }
      groups {
        date
        dateName
        dateTypeId
        totalUsers
        templateTask {
          id
          title
          internalName
        }
        assigned {
          id
          firstName
          lastName
          fullName
          email
          userTaskStatus {
            label
            type
          }
        }
        eligible {
          id
          firstName
          lastName
          fullName
          email
          userTaskStatus {
            label
            type
          }
        }
        notEligible {
          id
          firstName
          lastName
          fullName
          email
          userTaskStatus {
            label
            type
          }
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const USERS_FOR_TASK = gql`
  query UsersForTask(
    $templateTaskId: ID!
    $userIds: [ID!]
    $pagination: PaginationInput
  ) {
    usersForTask(
      templateTaskId: $templateTaskId
      userIds: $userIds
      pagination: $pagination
    ) {
      templateTask {
        id
        title
        internalName
      }
      assigned {
        id
        firstName
        lastName
        fullName
        email
        userTaskStatus {
          label
          type
        }
      }
      eligible {
        id
        firstName
        lastName
        fullName
        email
        userTaskStatus {
          label
          type
        }
      }
      notEligible {
        id
        firstName
        lastName
        fullName
        email
        userTaskStatus {
          label
          type
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const USERS_FOR_TASK_COUNTS = gql`
  query UsersForTaskCounts($templateTaskId: ID!) {
    usersForTaskCounts(templateTaskId: $templateTaskId) {
      totalAssignedCount
      totalEligibleCount
      totalNotEligibleCount
    }
  }
`

export const GROUP_TASK_DATE_GROUPS = gql`
  query GroupTaskDateGroups($templateTaskId: ID!, $dateFilter: [ISO8601Date!]) {
    groupTaskDateGroups(
      templateTaskId: $templateTaskId
      dateFilter: $dateFilter
    ) {
      dates {
        date
        dateName
        dateTypeId
        dateTypeName
        isSelected
      }
      groups {
        date
        dateName
        userCount
        groupTasks {
          id
          baseDateValue
          latestCalendarEvent {
            id
            title
            summary
            start
            startAt
            end
            duration
            timezone
            facilitators
            attendees {
              email
              name
            }
            status
            lockedFields
          }
        }
      }
    }
  }
`

export const GROUP_TASK_USERS = gql`
  query GroupTaskUsers(
    $groupTaskId: ID!
    $categoryId: ID
    $pagination: PaginationInput
  ) {
    groupTaskUsers(
      groupTaskId: $groupTaskId
      categoryId: $categoryId
      pagination: $pagination
    ) {
      groupTask {
        id
        baseDateValue
        latestCalendarEvent {
          id
          title
          summary
          start
          startAt
          end
          duration
          timezone
          facilitators
          attendees {
            email
            name
          }
          status
          lockedFields
        }
      }
      templateTask {
        id
        title
        internalName
        calendarId
      }
      users {
        id
        firstName
        lastName
        fullName
        email
        invitationStatus
        userTaskStatus {
          label
          type
        }
      }
      usersCount
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const DATE_TYPES_BY_GROUP_TASKS_FOR_TEMPLATE_CATEGORY = gql`
  query DateTypesByGroupTasksForTemplateCategory(
    $templateCategoryId: ID!
    $categoryId: ID
  ) {
    dateTypesByGroupTasksForTemplateCategory(
      templateCategoryId: $templateCategoryId
      categoryId: $categoryId
    ) {
      id
      name
    }
  }
`

export const GROUP_TASKS_BY_DATE_FOR_TEMPLATE_CATEGORY = gql`
  query GroupTasksByDateForTemplateCategory(
    $templateCategoryId: ID!
    $pagination: PaginationInput
    $dateFilter: [ISO8601Date!]
  ) {
    groupTasksByDateForTemplateCategory(
      templateCategoryId: $templateCategoryId
      pagination: $pagination
      dateFilter: $dateFilter
    ) {
      dates {
        date
        dateName
        dateTypeId
        dateTypeName
        isSelected
      }
      groups {
        date
        dateName
        sections {
          sectionId
          sectionName
          sectionPosition
          tasksCount
          usersCount
          tasks {
            assignedCount
            eligibleCount
            notEligibleCount
            assignedUninvitedCount
            groupTask {
              id
              baseDateValue
              latestCalendarEvent {
                id
                title
                summary
                start
                startAt
                end
                duration
                timezone
                facilitators
                attendees {
                  email
                  name
                }
                status
              }
            }
            templateTask {
              id
              title
              internalName
              calendarId
              templateTaskDateSetting {
                dateType {
                  id
                  name
                  baseDateType {
                    id
                    name
                    targetDateCohort {
                      id
                      dateCohortDates {
                        id
                        name
                        toDate
                        date
                        blackoutDates
                      }
                    }
                  }
                  targetDateCohort {
                    id
                    dateCohortDates {
                      id
                      name
                      toDate
                      date
                      blackoutDates
                    }
                  }
                }
              }
              assignDate {
                dateType {
                  id
                  name
                  baseDateType {
                    id
                    name
                    targetDateCohort {
                      id
                      dateCohortDates {
                        id
                        name
                        toDate
                        date
                        blackoutDates
                      }
                    }
                  }
                  targetDateCohort {
                    id
                    dateCohortDates {
                      id
                      name
                      toDate
                      date
                      blackoutDates
                    }
                  }
                }
              }
              inactive
              templateRoles {
                id
                name
              }
              ccTo {
                type
                value
                label
              }
              emailFrom {
                type
                value
                label
              }
              replyTo {
                type
                value
                label
              }
              notifyOnCantComplete {
                type
                value
                label
              }
              notifyOnComplete {
                type
                value
                label
              }
              taxonomyTerms {
                id
              }
            }

            assigned {
              id
              firstName
              lastName
              fullName
              email
              userTaskStatus {
                label
                type
              }
            }
            assignedUninvited {
              id
              firstName
              lastName
              fullName
              email
              userTaskStatus {
                label
                type
              }
            }
            eligible {
              id
              firstName
              lastName
              fullName
              email
              userTaskStatus {
                label
                type
              }
            }
            notEligible {
              id
              firstName
              lastName
              fullName
              email
              userTaskStatus {
                label
                type
              }
            }
          }
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const USERS_FOR_TEMPLATE_CATEGORY = gql`
  query UsersForTemplateCategory(
    $templateCategoryId: ID!
    $userIds: [ID!]
    $pagination: PaginationInput
  ) {
    usersForTemplateCategory(
      templateCategoryId: $templateCategoryId
      userIds: $userIds
      pagination: $pagination
    ) {
      assigned {
        id
        firstName
        lastName
        fullName
        email
      }
      eligible {
        id
        firstName
        lastName
        fullName
        email
      }
      notEligible {
        id
        firstName
        lastName
        fullName
        email
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const USERS_FOR_TEMPLATE_CATEGORY_COUNTS = gql`
  query UsersForTemplateCategoryCounts($templateCategoryId: ID!) {
    usersForTemplateCategoryCounts(templateCategoryId: $templateCategoryId) {
      totalAssignedCount
      totalEligibleCount
      totalNotEligibleCount
    }
  }
`
// Roles with read & write on a single folder. Scoped to one folder by id so it doesn't
// trigger the per-folder N+1 that requesting readWriteRoleIds across the whole list would.
export const GET_TEMPLATE_CATEGORY_FOLDER_ACCESS = gql`
  query TemplateCategoryFolderAccess($id: ID) {
    templateCategoryFolders(id: $id) {
      id
      readWriteRoleIds
    }
  }
`

export const GET_TEMPLATE_CATEGORY_FOLDERS = gql`
  query TemplateCategoryFolders($id: ID) {
    templateCategoryFolders(id: $id) {
      ...TemplateCategoryFolderFields
      templateCategories {
        id
        internalCategory
        inactive
        templateTags {
          id
        }
      }
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_FOLDER}
`

export const GET_TEMPLATE_CATEGORY_FOLDERS_WITH_COUNTS = gql`
  query TemplateCategoryFoldersWithCounts(
    $filters: TemplateCategoryFiltersInput
    $filterModel: FilterModelInput
  ) {
    templateCategoryFoldersWithCounts(
      filters: $filters
      filterModel: $filterModel
    ) {
      totalCount
      unassignedCount
      folders {
        id
        title
        position
        parentFolder {
          id
          title
        }
        subfolders {
          id
          title
          position
          templateCategoriesCount
        }
        templateCategoriesCount
      }
    }
  }
`

export const TEMPLATE_CATEGORY_HIERARCHY = gql`
  query TemplateCategoryHierarchy($id: ID!) {
    templateCategoryHierarchy(id: $id) {
      ...TemplateCategoryWithChildren
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_WITH_CHILDREN}
`
// ENGG-8443: $showInactive and $searchQuery have explicit defaults so
// refetchQueries call sites that only know the template id (e.g.
// "after delete section, refetch hierarchy") can supply just { id }
// without blowing up validation. For call sites that need to refresh
// the currently-subscribed variant, prefer
// apolloClient.refetchQueries({ include: [QUERY] }).
export const TEMPLATE_CATEGORY_HIERARCHY_FOR_TEMPLATE_SECTIONS = gql`
  query TemplateCategoryHierarchyForTemplateSections(
    $id: ID!
    $showInactive: Boolean = false
    $searchQuery: String = null
    $filterModel: FilterModelInput = null
  ) {
    templateCategoryHierarchy(id: $id, withTasks: false) {
      ...TemplateCategoryForSections
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_FOR_SECTIONS}
`

// ENGG-8443: per-section task fetch, fired lazily on accordion expand
// (TemplateSection) and eagerly for the no-children-fallback in
// TemplateSectionsHome. The same showInactive / searchQuery filters that
// the section count uses are pushed to the backend here so the list and
// the count stay consistent without any client-side filtering.
export const TEMPLATE_TASKS_BY_CATEGORY = gql`
  query TemplateTasksByCategory(
    $templateCategoryId: ID!
    $showInactive: Boolean = false
    $searchQuery: String = null
    $filterModel: FilterModelInput = null
  ) {
    templateTasksByCategory(
      templateCategoryId: $templateCategoryId
      showInactive: $showInactive
      searchQuery: $searchQuery
      filterModel: $filterModel
    ) {
      ...TemplateTaskForSection
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FOR_SECTION}
`

// Minimal dependency-edge query for the WHOLE template, used to draw
// cross-section dependency-chain badges. Per-section task lists load lazily on
// expand, so this is the only place we see every task's prerequisite at once.
// Deliberately fetches just id/dependsOnTemplateTaskId/position (no heavy task
// fields) and includes inactive tasks (withTasks defaults true, no filter) so
// the chain graph stays complete regardless of the list's current filter.
// Nested three levels deep to match the category hierarchy's realistic depth.
export const TEMPLATE_TASK_DEPENDENCIES = gql`
  query TemplateTaskDependencies($id: ID!) {
    templateCategoryHierarchy(id: $id) {
      id
      templateTasks {
        id
        internalName
        title
        dependsOnTemplateTaskId
        position
      }
      children {
        id
        templateTasks {
          id
          internalName
          title
          dependsOnTemplateTaskId
          position
        }
        children {
          id
          templateTasks {
            id
            internalName
            title
            dependsOnTemplateTaskId
            position
          }
        }
      }
    }
  }
`

export const TEMPLATE_CATEGORY_HIERARCHY_FOR_DOWNLOAD = gql`
  query TemplateCategoryHierarchyForDownload($id: ID!) {
    templateCategoryHierarchy(id: $id) {
      id
      internalCategory
      category
      inactive
      templateTasks {
        id
        title
        internalName
        description
        taskType
        position
        inactive
        important
        preBoarding
        offboarding
        isDelegable
        estimateInMins
        validAfterDueDateDays
        dependsOnTemplateTaskId
        calendarEventType
        calendarOwnerEmail
        calendarId
        calendarEventConferenceTool
        zoomOwnerEmail
        zoomTemplatePreset {
          id
          name
        }
        calendarEventDuration
        calendarEventLocation
        calendarEventGuestModify
        calendarEventOrganizerTemplateRole {
          id
          name
        }
        calendarEventSmartScheduling {
          enabled
        }
        calendarEventOptions {
          availability
          allDay
        }
        calendarConferenceOptions {
          __typename
          ... on ZoomMeetingOptions {
            security
            registrationRequired
            joinBeforeHost
            autoRecording
            muteUponEntry
            questionAndAnswer
            alternativeHosts
            zoomDescription
            templateId
            thirdPartySurvey
          }
          ... on ZoomWebinarOptions {
            altHosts
            altHostsEditPolls
            authAttendees
            authPanelists
            autoRecord
            emailInAttendeeReport
            hdScreenShare
            onDemand
            practiceSession
            qna
            recordLocation
            registrationRequired
            requirePasscode
            videoHost
            videoPanelists
            zoomDescription
            templateId
            thirdPartySurvey
          }
        }
        templateTaskDateSetting {
          days
          useBusinessDays
          relation
          dateType {
            name
          }
          time
          timezone
        }
        assignDate {
          days
          useBusinessDays
          relation
          dateType {
            name
          }
          time
          timezone
        }
        taxonomyTerms {
          id
          title
          taxonomyId
        }
        excludedTaxonomyTerms {
          id
          title
          taxonomyId
        }
        templateRoles {
          id
          name
        }
        notifyOnComplete {
          label
          value
          type
        }
        notifyOnCantComplete {
          label
          value
          type
        }
        emailFrom {
          label
          value
          type
        }
        replyTo {
          label
          value
          type
        }
        ccTo {
          label
          value
          type
        }
        calendarEventFacilitators {
          facilitators
          fallbackFacilitators
          notifyOnFacilitatorDecline
        }
        owner {
          id
          email
        }
        contentItem {
          id
          title
        }
        calendarResources {
          name
          email
        }
        calendarCategories {
          id
          name
          color
        }
        titleRaw
        descriptionRaw
        communicationSchedules {
          ...CommunicationSchedulesFieldsWithoutPreview
        }
      }
      children {
        id
        internalCategory
        category
        inactive
        templateTasks {
          id
          title
          internalName
          description
          taskType
          position
          inactive
          important
          preBoarding
          offboarding
          isDelegable
          estimateInMins
          validAfterDueDateDays
          dependsOnTemplateTaskId
          calendarEventType
          calendarOwnerEmail
          calendarId
          calendarEventConferenceTool
          zoomOwnerEmail
          zoomTemplatePreset {
            id
            name
          }
          calendarEventDuration
          calendarEventLocation
          calendarEventGuestModify
          calendarEventOrganizerTemplateRole {
            id
            name
          }
          calendarEventSmartScheduling {
            enabled
          }
          calendarEventOptions {
            availability
            allDay
          }
          calendarConferenceOptions {
            __typename
            ... on ZoomMeetingOptions {
              security
              registrationRequired
              joinBeforeHost
              autoRecording
              muteUponEntry
              questionAndAnswer
              alternativeHosts
              zoomDescription
              templateId
              thirdPartySurvey
            }
            ... on ZoomWebinarOptions {
              altHosts
              altHostsEditPolls
              authAttendees
              authPanelists
              autoRecord
              emailInAttendeeReport
              hdScreenShare
              onDemand
              practiceSession
              qna
              recordLocation
              registrationRequired
              requirePasscode
              videoHost
              videoPanelists
              zoomDescription
              templateId
              thirdPartySurvey
            }
          }
          templateTaskDateSetting {
            days
            useBusinessDays
            relation
            dateType {
              name
            }
            time
            timezone
          }
          assignDate {
            days
            useBusinessDays
            relation
            dateType {
              name
            }
            time
            timezone
          }
          taxonomyTerms {
            id
            title
            taxonomyId
          }
          excludedTaxonomyTerms {
            id
            title
            taxonomyId
          }
          templateRoles {
            id
            name
          }
          notifyOnComplete {
            label
            value
            type
          }
          notifyOnCantComplete {
            label
            value
            type
          }
          emailFrom {
            label
            value
            type
          }
          replyTo {
            label
            value
            type
          }
          ccTo {
            label
            value
            type
          }
          calendarEventFacilitators {
            facilitators
            fallbackFacilitators
            notifyOnFacilitatorDecline
          }
          owner {
            id
            email
          }
          contentItem {
            id
            title
          }
          calendarResources {
            name
            email
          }
          calendarCategories {
            id
            name
            color
          }
          titleRaw
          descriptionRaw
          communicationSchedules {
            ...CommunicationSchedulesFieldsWithoutPreview
          }
        }
      }
    }
  }
  ${FRAGMENT_COMMUNICATION_SCHEDULES_WITHOUT_PREVIEW}
`

export const TEMPLATE_CATEGORY_HIERARCHIES = gql`
  query TemplateCategoryHierarchies($ids: [ID!]!) {
    templateCategoryHierarchies(ids: $ids) {
      ...TemplateCategoryWithChildren
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_WITH_CHILDREN}
`

export const TEMPLATE_CATEGORY_HIERARCHIES_FOR_FILTERS = gql`
  query TemplateCategoryHierarchiesForFilters($ids: [ID!]!) {
    templateCategoryHierarchies(ids: $ids) {
      ...TemplateCategoryHierarchyForFilters
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_HIERARCHY_FOR_FILTERS}
`

export const TEMPLATE_CATEGORY_HIERARCHIES_FOR_ROLES_AND_DATES = gql`
  query TemplateCategoryHierarchiesForRolesAndDates($ids: [ID!]!) {
    templateCategoryHierarchies(ids: $ids) {
      ...TemplateCategoryHierarchyForRolesAndDates
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_HIERARCHY_FOR_ROLES_AND_DATES}
`

export const GET_TAXONOMIES = gql`
  query Taxonomies {
    taxonomies {
      id
      name
      key
      baseTaxonomyId
      editable
      isLocation
      slug
      source
      uniqueTerms
      position
    }
  }
`

export const GET_TAXONOMIES_WITH_TERMS = gql`
  query Taxonomies {
    taxonomies {
      id
      name
      key
      baseTaxonomyId
      editable
      isLocation
      taxonomyTerms {
        id
        title
      }
    }
  }
`

export const GET_TAXONOMY_TERM_HIERARCHY = gql`
  query TaxonomyTermHierarchy($taxonomyId: ID!) {
    taxonomyTermHierarchy(taxonomyId: $taxonomyId)
  }
`

// ENGG-9275: surfaces the "Blank/Empty" sentinel. Deliberately a SEPARATE
// document rather than an optional argument on the one above.
//
// `includeBlank` only exists on a backend carrying ENGG-9275. Sending it to one
// that doesn't fails the whole query — graphql-ruby rejects the unknown
// argument and then reports the variable as unused — which would break every
// taxonomy picker in the admin, not just the blank option. Keeping the original
// document as the default means an admin deploy that lands before the backend
// degrades to the pre-9275 tree instead of breaking.
//
// Safe to reach for only when the client flag is on, and that flag cannot be
// enabled until the backend supports it.
export const GET_TAXONOMY_TERM_HIERARCHY_WITH_BLANK = gql`
  query TaxonomyTermHierarchyWithBlank(
    $taxonomyId: ID!
    $includeBlank: Boolean
  ) {
    taxonomyTermHierarchy(taxonomyId: $taxonomyId, includeBlank: $includeBlank)
  }
`

export const GET_TAXONOMY_TERM_USAGE = gql`
  query TaxonomyTermUsage($taxonomyTermId: ID!) {
    taxonomyTermUsage(taxonomyTermId: $taxonomyTermId)
  }
`

export const GET_SNIPPETS = gql`
  query Snippets {
    snippets {
      ...SnippetFields
    }
  }
  ${FRAGMENT_SNIPPET_FIELDS}
`

export const GET_SNIPPETS_CONDITIONS = gql`
  query SnippetConditions($snippetId: ID!) {
    snippetConditions(snippetId: $snippetId) {
      ...SnippetConditionsFields
    }
  }
  ${FRAGMENT_SNIPPET_CONDITIONS_FIELDS}
`

export const GET_WORKOS_ADMIN_PORTAL_URL = gql`
  query WorkosAdminPortalRedirectUrl {
    workosAdminPortalRedirectUrl {
      redirectUrl
    }
  }
`

export const GET_METABASE_REDIRECT_URL = gql`
  query MetabaseRedirectUrl {
    metabaseRedirectUrl {
      token
    }
  }
`

export const EMPLOYEE_CREDITS_ANALYTICS = gql`
  query EmployeeCreditsAnalytics(
    $startDate: ISO8601Date
    $endDate: ISO8601Date
  ) {
    employeeCreditsAnalytics(startDate: $startDate, endDate: $endDate) {
      rows
      columns
      startDate
      endDate
    }
  }
`

export const GET_AUTHORIZATION_TOKENS = gql`
  query AuthorizationTokens {
    authorizationTokens {
      id
      systemOfRecord
      tokenIssuedAt
      tokenIssuer
      latestUserImportRun {
        status
        startedAt
        endedAt
        errorMessage
        totalRows
        processedRows
        skippedRows
        failedRows
        progressPercentage
      }
      latestTaxonomyImportRun {
        status
        startedAt
        endedAt
        errorMessage
        totalRows
        processedRows
        skippedRows
        failedRows
        progressPercentage
      }
      recentUserImportRuns {
        id
        status
        startedAt
        endedAt
        errorMessage
      }
      recentTaxonomyImportRuns {
        id
        status
        startedAt
        endedAt
        errorMessage
      }
    }
  }
`

export const GET_LATEST_IMPORT_RUNS = gql`
  query LatestImportRuns {
    latestImportRuns {
      clientId
      latestUserImportRun {
        id
        status
        startedAt
        endedAt
        errorMessage
        totalRows
        processedRows
        skippedRows
        failedRows
        progressPercentage
      }
      latestTaxonomyImportRun {
        id
        status
        startedAt
        endedAt
        errorMessage
        totalRows
        processedRows
        skippedRows
        failedRows
        progressPercentage
      }
      recentUserImportRuns {
        id
        status
        startedAt
        endedAt
        errorMessage
      }
      recentTaxonomyImportRuns {
        id
        status
        startedAt
        endedAt
        errorMessage
      }
    }
  }
`

export const GET_AUTO_IMPORT_RULES = gql`
  query AutoImportRules {
    autoImportRules {
      id
      name
      inactive
      offboarding
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
      templateCategory {
        id
        category
        internalCategory
      }
      autoImportRuleDateSetting {
        dateType {
          id
          name
          baseDateTypeId
        }
        fromDateDays
        fromDateRelation
        toDateDays
        toDateRelation
        recurrenceInterval
        includeBaseYear
      }
      rulesTemplateRolesUsers {
        userId
        user {
          id
          email
          firstName
          lastName
          fullName
        }
        templateRoleId
      }
    }
  }
`

export const GET_AUTO_IMPORT_TEMPLATE_ATTEMPTS = gql`
  query AutoImportTemplateAttempts(
    $autoImportRuleId: ID!
    $query: String
    $pagination: PaginationInput
  ) {
    autoImportTemplateAttempts(
      autoImportRuleId: $autoImportRuleId
      query: $query
      pagination: $pagination
    ) {
      data {
        id
        status
        createdAt
        user {
          id
          firstName
          lastName
          fullName
          email
        }
      }
      paginationMeta {
        page
        perPage
        totalPages
        totalCount
        nextPage
        prevPage
      }
    }
  }
`

export const GET_AUTO_IMPORT_RULE_CHANGES = gql`
  query AutoImportRuleChanges(
    $autoImportRuleId: ID!
    $pagination: PaginationInput
  ) {
    autoImportRuleChanges(
      autoImportRuleId: $autoImportRuleId
      pagination: $pagination
    ) {
      data {
        id
        event
        fieldCategory
        fieldName
        oldValue
        newValue
        createdAt
        actor {
          id
          firstName
          lastName
          email
        }
      }
      paginationMeta {
        page
        perPage
        totalPages
        totalCount
        nextPage
        prevPage
      }
    }
  }
`

export const GET_AUTO_IMPORT_RUN = gql`
  query AutoImportRun($id: ID!) {
    autoImportRun(id: $id) {
      id
      status
      phase
      progressPercentage
      phaseLabel
      startedAt
      finishedAt
      createdAt
      autoImportTemplateAttempts {
        id
        status
        templateName
        createdAt
        user {
          id
          firstName
          lastName
          fullName
          email
        }
      }
    }
  }
`

export const GET_CLIENT_SETTING = gql`
  query ClientSetting {
    clientSetting {
      taxonomyImportEnabled
      atsTaxonomyImportEnabled
      userImportEnabled
      atsUserImportEnabled
      docusign
      userImport {
        importJoining
        importLeaving
        importAllUsers
        joiningDays
        leavingDays
      }
      atsUserImport {
        importJoining
        importLeaving
        importAllUsers
        joiningDays
        leavingDays
      }
      calendarIntegration {
        type
        email
        timezone
        connected
        calendarId
        workdayStartTime
        meetingLengthMinutes
      }
      partnerEventsIntegration {
        calendarEmail
        calendarId
        calendarTimezone
        zoomEmail
      }
      partnerEventsCopy {
        key
        value
      }
      gmailIntegration {
        email
        connected
      }
      gmailAccounts {
        email
      }
      zoomAccounts {
        name
        email
        favorite
        webinar
        connectionStatus
      }
      calendarAccounts {
        email
        source
      }
      doceboAccounts {
        name
        email
        baseUrl
      }
      gotoAccounts {
        name
        email
      }
      availableLanguages {
        label
        value
      }
    }
  }
`

export const GET_GOOGLE_DRIVE_INSTALLATIONS = gql`
  query GoogleDriveInstallations {
    googleDriveInstallations {
      id
      email
      scopes
      hasDriveAccess
      clientId
      createdAt
      updatedAt
    }
  }
`

export const GET_GOOGLE_DRIVE_FOLDERS = gql`
  query GetGoogleDriveFolders($installId: ID!, $parentId: String) {
    googleDriveFolders(installId: $installId, parentId: $parentId) {
      id
      name
      parents
    }
  }
`

// ENGG-10257 — the folder-level taxonomyTerms are deprecated on the server
// (the audience lives on each item now), so they are no longer selected.
export const GET_GOOGLE_DRIVE_WHITELISTED_FOLDERS = gql`
  query GoogleDriveWhitelistedFolders($installationId: ID!) {
    googleDriveWhitelistedFolders(installationId: $installationId) {
      id
      folderId
      folderName
    }
  }
`

export const GET_SHAREPOINT_CONSENT_URL = gql`
  query SharepointConsentUrl {
    sharepointConsentUrl
  }
`

export const GET_SHAREPOINT_INSTALLATIONS = gql`
  query SharepointInstallations {
    sharepointInstallations {
      id
      email
      tenantId
      scopes
      hasSharepointAccess
      clientId
      createdAt
      updatedAt
    }
  }
`

export const GET_SHAREPOINT_FOLDER = gql`
  query SharepointFolder($installationId: ID!, $url: String!) {
    sharepointFolder(installationId: $installationId, url: $url) {
      siteId
      driveId
      itemId
      name
      webUrl
    }
  }
`

export const GET_SHAREPOINT_WHITELISTED_RESOURCES = gql`
  query SharepointWhitelistedResources($installationId: ID!) {
    sharepointWhitelistedResources(installationId: $installationId) {
      id
      resourceType
      siteId
      driveId
      itemId
      name
      webUrl
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
    }
  }
`

export const GET_SPEKIT_INSTALLATIONS = gql`
  query SpekitInstallations {
    spekitInstallations {
      id
      spekitUserEmail
      status
      createdAt
    }
  }
`

export const GET_NOTION_INSTALLATIONS = gql`
  query NotionInstallations {
    notionInstallations {
      id
      workspaceId
      workspaceName
      clientId
      createdAt
      updatedAt
    }
  }
`

export const GET_NOTION_RESOURCES = gql`
  query NotionResources($installationId: ID!, $query: String) {
    notionResources(installationId: $installationId, query: $query) {
      notionId
      resourceType
      name
      url
    }
  }
`

export const GET_NOTION_WHITELISTED_RESOURCES = gql`
  query NotionWhitelistedResources($installationId: ID!) {
    notionWhitelistedResources(installationId: $installationId) {
      id
      notionId
      resourceType
      name
    }
  }
`

export const GET_CONFLUENCE_INSTALLATIONS = gql`
  query ConfluenceInstallations {
    confluenceInstallations {
      id
      siteUrl
      siteName
      email
      clientId
      createdAt
      updatedAt
    }
  }
`

export const GET_CONFLUENCE_SPACES = gql`
  query ConfluenceSpaces($installationId: ID!) {
    confluenceSpaces(installationId: $installationId) {
      spaceId
      spaceKey
      name
      url
    }
  }
`

export const GET_CONFLUENCE_WHITELISTED_SPACES = gql`
  query ConfluenceWhitelistedSpaces($installationId: ID!) {
    confluenceWhitelistedSpaces(installationId: $installationId) {
      id
      spaceId
      spaceKey
      name
    }
  }
`

export const GET_GONG_INSTALLATIONS = gql`
  query GongInstallations {
    gongInstallations {
      id
      apiBaseUrl
      accountName
      clientId
      createdAt
      updatedAt
    }
  }
`

export const GET_GONG_WORKSPACES = gql`
  query GongWorkspaces($installationId: ID!) {
    gongWorkspaces(installationId: $installationId) {
      workspaceId
      name
      description
    }
  }
`

export const GET_GONG_WHITELISTED_WORKSPACES = gql`
  query GongWhitelistedWorkspaces($installationId: ID!) {
    gongWhitelistedWorkspaces(installationId: $installationId) {
      id
      workspaceId
      name
    }
  }
`

export const GET_RESOURCES_HIERARCHY = gql`
  query ResourcesHierarchy($isEditing: Boolean) {
    resourcesHierarchy(isEditing: $isEditing) {
      id
      title
      titleRaw
      position
      parentId
      inactive
      children {
        id
      }
      resources {
        id
        title
        titleRaw
        url
        position
        inactive
        offboarding
        preboarding
        favorite
        iconUrl
        iconLibraryId
        description
        taxonomyTerms {
          id
          title
          taxonomyId
        }
        excludedTaxonomyTerms {
          id
          title
          taxonomyId
        }
        templateRoles {
          id
          name
        }
      }
    }
  }
`

export const GET_PERMISSIONS = gql`
  query Permissions {
    permissions {
      id
      title
      children {
        id
        title
        folderTitle
        children {
          id
          title
        }
        subfolders {
          id
          title
          children {
            id
            title
          }
        }
      }
    }
  }
`

export const GET_ROLE_PERMISSIONS = gql`
  query RolePermissions($roleId: ID!) {
    rolePermissions(roleId: $roleId) {
      visibility
      permissions {
        id
        title
      }
    }
  }
`

export const GET_ROLES = gql`
  query Roles {
    roles {
      id
      name
      roleType
    }
  }
`

export const GET_ROLE_USERS = gql`
  query Role($roleId: ID!) {
    role(roleId: $roleId) {
      name
      users {
        fullName
        email
      }
    }
  }
`

export const GET_MERGE_TOKEN = gql`
  query MergeToken {
    mergeToken
  }
`

export const GET_USERS_WITHOUT_TEMPLATE_TASK = gql`
  query UsersWithoutTemplateTask($templateTaskId: ID!, $userIds: [ID!]) {
    usersWithoutTemplateTask(
      templateTaskId: $templateTaskId
      userIds: $userIds
    ) {
      id
      email
      firstName
      lastName
      fullName
    }
  }
`

export const GET_CALENDAR_EVENTS = gql`
  query CalendarEvents($calendarId: String!, $calendarOwner: String!) {
    calendarEvents(calendarId: $calendarId, calendarOwner: $calendarOwner) {
      id
      name
      calendarId
      calendarOwnerEmail
      isRecurring
      nextOccurrences
    }
  }
`

export const GET_CALENDARS = gql`
  query Calendars($email: String) {
    calendars(email: $email) {
      id
      name
      # Lets the Events settings snapshot the chosen calendar's zone at pick time
      # instead of guessing it later. Null when the provider reports none.
      timezone
    }
  }
`

export const GET_CALENDAR_DEBUG_EVENTS = gql`
  query CalendarDebugEvents(
    $calendarId: String!
    $calendarOwnerEmail: String!
    $from: ISO8601DateTime!
    $to: ISO8601DateTime!
    $includeDeleted: Boolean
  ) {
    calendarDebugEvents(
      calendarId: $calendarId
      calendarOwnerEmail: $calendarOwnerEmail
      from: $from
      to: $to
      includeDeleted: $includeDeleted
    ) {
      id
      title
      start
      end
      duration
      timezone
      organizer
      creator
      status
      location
      isTangeloManaged
      isDeleted
      deletedAt
      attendees {
        name
        email
      }
      tangeloInfo {
        calendarEventId
        referenceType
        referenceId
        taskId
        templateTaskId
        templateTaskTitle
        templateCategoryId
        templateCategoryName
        workflowOwnerName
        workflowOwnerEmail
        groupTaskId
        groupTaskDueDate
      }
    }
  }
`

export const GET_CALENDAR_EVENT_HISTORY = gql`
  query CalendarEventHistory(
    $eventId: String!
    $calendarId: String!
    $calendarOwnerEmail: String!
  ) {
    calendarEventHistory(
      eventId: $eventId
      calendarId: $calendarId
      calendarOwnerEmail: $calendarOwnerEmail
    ) {
      googleEvent {
        id
        title
        status
        creator
        organizer
        created
        updated
        start
        end
        attendees {
          name
          email
        }
        location
      }
      tangeloRecords {
        id
        referenceType
        referenceId
        createdAt
        updatedAt
        deletedAt
        organizer
        startAt
        endAt
        metadata
      }
      auditLog {
        actorEmail
        eventName
        timestamp
        eventTitle
      }
    }
  }
`

export const GET_NEXT_OCCURRENCE_DATES = gql`
  query CalendarEventOccurrences(
    $calendarEvents: [TemplateTaskCalendarEventInput!]!
  ) {
    calendarEventOccurrences(calendarEvents: $calendarEvents)
  }
`

export const GET_DOCUSIGN_TEMPLATES = gql`
  query DocusignTemplates {
    docusignTemplates {
      id
      name
    }
  }
`

export const GET_ZOOM_MEETING_TEMPLATES = gql`
  query ZoomMeetingTemplates($ownerEmail: String) {
    zoomMeetingTemplates(ownerEmail: $ownerEmail) {
      id
      name
      type
    }
  }
`

export const GET_ZOOM_WEBINAR_TEMPLATES = gql`
  query ZoomWebinarTemplates($ownerEmail: String!) {
    zoomWebinarTemplates(ownerEmail: $ownerEmail) {
      id
      name
      type
    }
  }
`

export const GET_NOTIFICATION_TYPES = gql`
  query NotificationTypes {
    notificationTypes {
      title
      type
      typeId: id
      denied
    }
  }
`

export const GET_TAGS = gql`
  query TemplateTags {
    templateTags {
      color
      id
      name
    }
  }
`

export const GET_TEMPLATE_ROLE_USERS = gql`
  query TemplateRoleUsers {
    templateRoleUsers {
      id
      priority
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
      rulesTemplateRolesUsers {
        userId
        user {
          id
          email
          firstName
          lastName
          fullName
        }
        templateRoleId
      }
      targetTaxonomyTerms {
        id
        title
        taxonomyId
        taxonomy {
          id
          name
        }
      }
      dateSettings {
        dateType {
          id
          name
        }
        dateTypeId
        fromDate
        toDate
      }
    }
  }
`

export const GET_TEMPLATE_ROLE_USER_ELIGIBLE_USERS = gql`
  query GetEligibleUsers($templateRoleUserId: ID!) {
    templateRoleUserEligibleUsers(templateRoleUserId: $templateRoleUserId) {
      id
      email
    }
  }
`

export const GET_EXTERNAL_USERS = gql`
  query ExternalUsers($query: String, $pagination: PaginationInput) {
    externalUsers(query: $query, pagination: $pagination) {
      data {
        ...UserAutocompleteFields
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_EXTERNAL_USER_AUTOCOMPLETE_WITH_ASSIGNMENTS_FIELDS}
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_ICON_LIBRARY = gql`
  query IconLibrary {
    iconLibrary {
      id
      name
      url
      editable
    }
  }
`

export const GET_USER_DEFAULT_TEMPLATE_ROLE_USERS = gql`
  query User($id: ID!) {
    user(userId: $id) {
      defaultTemplateRolesUsers {
        templateRoleId
        userId
      }
    }
  }
`

export const ANALYTICS_SURVEY = gql`
  query AnalyticsSurvey {
    analyticsSurvey
  }
`

export const ANALYTICS_NEW_SURVEY = gql`
  query AnalyticsNewSurvey(
    $userIds: [ID!]
    $templateIds: [ID!]
    $taskIds: [ID!]
  ) {
    analyticsNewSurvey(
      userIds: $userIds
      templateIds: $templateIds
      taskIds: $taskIds
    )
  }
`

export const SURVEY_TEMPLATE_CATEGORIES = gql`
  query SurveyTemplateCategories {
    surveyTemplateCategories {
      id
      name
      tasks {
        id
        name
        # ENGG-9776: the QuestionSet behind the task. The Content Rollup views
        # build their content filter from these and resolve a choice back to
        # task ids, so no analytics query needs a new argument.
        contentId
        contentName
        # ENGG-9899: only library content belongs in the Content filter.
        inContentLibrary
      }
    }
  }
`

// ENGG-9857: the Template Rollup and Employee View tabs used to share one
// untyped analyticsScorecard blob that carried both tabs' data on every load.
// Each tab now asks for its own shape, so the schema — not hand-written Ruby —
// decides what comes back, and template / task / employee filters run on the
// server. Date ranges stay client-side; they are UI-level.
export const SCORECARD_TEMPLATE_ROLLUP = gql`
  query ScorecardTemplateRollup(
    $templateCategoryIds: [ID!]
    $templateTaskIds: [ID!]
    $userIds: [ID!]
  ) {
    scorecardTemplateRollup(
      templateCategoryIds: $templateCategoryIds
      templateTaskIds: $templateTaskIds
      userIds: $userIds
    ) {
      sectionId
      sectionTitle
      templateId
      templateTitle
      tasks {
        id
        taskTitle
      }
      employees {
        ownerId
        ownerFullName
        scorerId
        scorerFullName
        scorerNames
        taskType
        employeeStartDate
        completionDate
        scores {
          surveyId
          submissionScore
          submissionTotalScore
        }
      }
    }
  }
`

export const SCORECARD_EMPLOYEE_ANSWERS = gql`
  query ScorecardEmployeeAnswers(
    $templateCategoryIds: [ID!]
    $templateTaskIds: [ID!]
    $userIds: [ID!]
  ) {
    scorecardEmployeeAnswers(
      templateCategoryIds: $templateCategoryIds
      templateTaskIds: $templateTaskIds
      userIds: $userIds
    ) {
      surveys {
        id
        title
        sectionId
        taskTitle
        questions {
          id
          title
          required
          questionType
          sectionId
          sectionTitle
          options {
            id
            title
            score
          }
        }
      }
      assignments {
        ownerId
        ownerFullName
        ownerEmail
        assigneeId
        assigneeFullName
        assigneeEmail
        surveyId
        surveyTitle
        taskType
        employeeStartDate
        completionDate
        score
        totalScore
        answers {
          templateTaskQuestionId
          templateTaskQuestionOptionId
          comment
          optionScore
        }
      }
    }
  }
`

// Unfiltered on purpose: these fill the filter dropdowns, which must keep
// offering every option regardless of what is currently selected.
export const SCORECARD_FILTER_OPTIONS = gql`
  query ScorecardFilterOptions {
    scorecardFilterOptions {
      templates {
        id
        title
      }
      sections {
        id
        templateId
        title
      }
      tasks {
        id
        title
      }
      employees {
        id
        email
      }
    }
  }
`

export const SCORECARD_TEMPLATE_CATEGORIES = gql`
  query ScorecardTemplateCategories {
    scorecardTemplateCategories {
      id
      name
      tasks {
        id
        name
        taskType
        contentId
        contentName
        inContentLibrary
      }
    }
  }
`

export const SCORECARD_USER_ANALYTICS = gql`
  query ScorecardUserAnalytics(
    $templateCategoryIds: [ID!]
    $templateTaskIds: [ID!]
    $userIds: [ID!]
  ) {
    scorecardUserAnalytics(
      templateCategoryIds: $templateCategoryIds
      templateTaskIds: $templateTaskIds
      userIds: $userIds
    ) {
      taskType
      # ENGG-5125: surveyId is the template task and repeats across an employee's
      # duplicate instances of the same template, so rows are keyed on this.
      instanceTaskId
      surveyCategoryId
      ownerId
      ownerFullName
      ownerEmail
      assigneeIds
      assigneeFullNames
      assigneeEmails
      surveyId
      surveyTitle
      # ENGG-9776: the QuestionSet behind the task. Several tasks share one when
      # the definition lives in the Content Library — the Content Rollup tab
      # groups on it.
      contentId
      contentName
      inContentLibrary
      highestScore
      highestScorePercentage
      latestScore
      latestScorePercentage
      completionDate
      status
      attemptCount
      highestScoreChatSessionId
      hasHighestScoreTranscription
      hasHighestScoreRating
      highestScoreTimeSpentSeconds
      latestScoreChatSessionId
      hasLatestScoreTranscription
      hasLatestScoreRating
      latestScoreTimeSpentSeconds
      totalTimeSpentSeconds
      hasHighestAudio
      hasLatestAudio
      hasHighestVideo
      hasLatestVideo
      hasHighestScreenShareVideo
      hasHighestUserCameraVideo
      hasLatestScreenShareVideo
      hasLatestUserCameraVideo
    }
  }
`

export const SCORECARD_SUBMISSION_DETAILS = gql`
  query ScorecardSubmissionDetails(
    $templateCategoryId: ID
    $templateTaskId: ID
    $ownerId: ID
  ) {
    scorecardSubmissionDetails(
      templateCategoryId: $templateCategoryId
      templateTaskId: $templateTaskId
      ownerId: $ownerId
    ) {
      taskType
      ownerId
      ownerFullName
      ownerEmail
      assigneeId
      assigneeFullName
      assigneeEmail
      templateCategoryId
      templateTaskId
      taskId
      templateTaskTitle
      templateTaskSubmissionId
      templateTaskSubmissionScore
      templateTaskSubmissionTotalScore
      attemptNumber
      attemptDate
      chatSessionId
      hasTranscription
      hasRating
      submissionTimeSpentSeconds
      hasAudio
      hasVideo
      hasScreenShareVideo
      hasUserCameraVideo
      questions {
        templateTaskQuestionId
        templateTaskQuestionTitle
        templateTaskQuestionOptionMaxScore
        options {
          id
          order
          title
          description
          score
        }
        answer {
          templateQuestionOptionId
          templateTaskQuestionOptionId
          templateQuestionOptionAnswerTitle
          templateQuestionOptionAnswerAnswer
          templateQuestionOptionAnswerScore
          # ENGG-9998: the written feedback behind the pick (the reviewer's on a
          # reviewer-scored attempt, the AI's otherwise).
          templateQuestionOptionAnswerComment
        }
        # ENGG-9660: every nominated scorer's answer to this question. Empty on a
        # scorecard task and on a roleplay no reviewer has scored, so the
        # single-scorer rendering above stays the fallback.
        scorerAnswers {
          scoredByUserId
          scorerFullName
          templateTaskQuestionOptionId
          score
          # ENGG-9998: that scorer's written feedback for the question.
          comment
        }
      }
      # ENGG-9998: the attempt the rep submitted for review. On a reviewer-scored
      # roleplay only this attempt carries the task's score; the rest are
      # practice, which the question matrix must not pick as the employee's score.
      submittedForScoring
      # ENGG-9660: how many reviewers are nominated, scored or not. The breakdown
      # below shows while an attempt is partly scored, which scorerSubmissions'
      # length alone cannot tell apart from a single-scorer roleplay.
      expectedScorerCount
      # ENGG-9660: per-scorer totals behind templateTaskSubmissionScore, which is
      # one number (their average, or the newest scorer's).
      scorerSubmissions {
        submissionId
        scoredByUserId
        scorerFullName
        scorerEmail
        score
        totalScore
        scoredAt
      }
    }
  }
`

// ENGG-8736: admin Roleplay Analytics flat feed (paginated + filterable).
export const ADMIN_ROLEPLAY_ANALYTICS = gql`
  query AdminRoleplayAnalytics(
    # ENGG-9558: the CSV export reuses this document, so the row shape lives in
    # one place. It skips the KPI aggregate, which it never reads.
    $skipStats: Boolean! = false
    $dateFrom: ISO8601DateTime
    $dateTo: ISO8601DateTime
    $userIds: [ID!]
    $templateTaskIds: [ID!]
    $scoreMin: Int
    $scoreMax: Int
    $realismMin: Int
    $realismMax: Int
    $search: String
    $sortKey: String
    $sortDir: String
    $page: Int
    $perPage: Int
  ) {
    adminRoleplayAnalytics(
      dateFrom: $dateFrom
      dateTo: $dateTo
      userIds: $userIds
      templateTaskIds: $templateTaskIds
      scoreMin: $scoreMin
      scoreMax: $scoreMax
      realismMin: $realismMin
      realismMax: $realismMax
      search: $search
      sortKey: $sortKey
      sortDir: $sortDir
      page: $page
      perPage: $perPage
    ) {
      totalCount
      stats @skip(if: $skipStats) {
        totalAttempts
        completedCount
        repCount
        avgScorePct
        avgRealism
      }
      rows {
        eventId
        chatSessionId
        userId
        userFullName
        userEmail
        templateTaskId
        templateCategoryId
        taskTitle
        # ENGG-9776: the QuestionSet behind the task — the same unit the Content
        # Library lists under AI Roleplay Scorecards.
        contentId
        contentName
        inContentLibrary
        attemptNumber
        completedAt
        score
        totalScore
        chatMaxScore
        chatPassingScore
        passed
        scorePercentage
        # ENGG-9998: ai | practice | awaiting_review | reviewed — whether the
        # score above is the attempt's final score on a reviewer-scored roleplay.
        scoringStatus
        realismScore
        userRealismScore
        durationSeconds
        completed
        hasAudio
        hasVideo
        hasScreenShareVideo
        hasUserCameraVideo
        hasTranscription
      }
    }
  }
`

// ENGG-8736: roleplay attempts grouped by rep (Grouped-by-user layout).
export const ADMIN_ROLEPLAY_GROUPED = gql`
  query AdminRoleplayGrouped(
    $dateFrom: ISO8601DateTime
    $dateTo: ISO8601DateTime
    $userIds: [ID!]
    $templateTaskIds: [ID!]
    $scoreMin: Int
    $scoreMax: Int
    $realismMin: Int
    $realismMax: Int
    $search: String
    $page: Int
    $perPage: Int
  ) {
    adminRoleplayGrouped(
      dateFrom: $dateFrom
      dateTo: $dateTo
      userIds: $userIds
      templateTaskIds: $templateTaskIds
      scoreMin: $scoreMin
      scoreMax: $scoreMax
      realismMin: $realismMin
      realismMax: $realismMax
      search: $search
      page: $page
      perPage: $perPage
    ) {
      totalCount
      groups {
        userId
        userFullName
        userEmail
        attempts
        completed
        bestScorePct
        lastCompletedAt
        rows {
          eventId
          chatSessionId
          userId
          userFullName
          userEmail
          templateTaskId
          templateCategoryId
          taskTitle
          contentId
          contentName
          inContentLibrary
          attemptNumber
          completedAt
          score
          totalScore
          chatMaxScore
          chatPassingScore
          passed
          scorePercentage
          scoringStatus
          realismScore
          userRealismScore
          durationSeconds
          completed
          hasAudio
          hasVideo
          hasScreenShareVideo
          hasUserCameraVideo
          hasTranscription
        }
      }
    }
  }
`

// ENGG-9905: the Template Rollup View's data, typed. Replaces the analyticsQuiz
// JSON blob, which carried every answered option in the client (146 MB and a
// 504 on the largest one) although the grid reads one score per employee/quiz.
export const QUIZ_TEMPLATE_ROLLUP = gql`
  query QuizTemplateRollup {
    quizTemplateRollup {
      sectionId
      sectionTitle
      tasks {
        id
        taskTitle
      }
      employees {
        ownerId
        ownerFullName
        completionDate
        scores {
          surveyId
          submissionScore
          submissionTotalScore
        }
      }
    }
  }
`

export const QUIZ_TEMPLATE_CATEGORIES = gql`
  query QuizTemplateCategories {
    quizTemplateCategories {
      id
      name
      tasks {
        id
        name
        contentId
        contentName
        inContentLibrary
      }
    }
  }
`

// ENGG-9456: flat feed of individual quiz attempts for the Quiz > Stream tab.
// The row selection is shared with the QuizSubmissionsChannel payload — keep the
// two in step when adding a field (see BroadcastQuizSubmissionJob).
export const ADMIN_QUIZ_ANALYTICS = gql`
  query AdminQuizAnalytics(
    # ENGG-9558: the CSV export reuses this document, so the row shape lives in
    # one place. It skips the KPI aggregate, which it never reads.
    $skipStats: Boolean! = false
    $dateFrom: ISO8601DateTime
    $dateTo: ISO8601DateTime
    $userIds: [ID!]
    $templateTaskIds: [ID!]
    $templateCategoryIds: [ID!]
    $scoreMin: Int
    $scoreMax: Int
    $passed: Boolean
    $search: String
    $sortKey: String
    $sortDir: String
    $page: Int
    $perPage: Int
  ) {
    adminQuizAnalytics(
      dateFrom: $dateFrom
      dateTo: $dateTo
      userIds: $userIds
      templateTaskIds: $templateTaskIds
      templateCategoryIds: $templateCategoryIds
      scoreMin: $scoreMin
      scoreMax: $scoreMax
      passed: $passed
      search: $search
      sortKey: $sortKey
      sortDir: $sortDir
      page: $page
      perPage: $perPage
    ) {
      totalCount
      stats @skip(if: $skipStats) {
        totalAttempts
        passedCount
        employeeCount
        quizCount
        avgScorePct
      }
      rows {
        submissionId
        eventId
        userId
        userFullName
        userEmail
        templateTaskId
        templateCategoryId
        taskTitle
        workflowName
        attemptNumber
        retriesAllowed
        requiredToPass
        submittedAt
        scorePercentage
        correctCount
        requiredCount
        passed
        eventType
      }
    }
  }
`

export const QUIZ_USER_ANALYTICS = gql`
  query QuizUserAnalytics(
    $templateCategoryIds: [ID!]
    $templateTaskIds: [ID!]
    $userIds: [ID!]
  ) {
    quizUserAnalytics(
      templateCategoryIds: $templateCategoryIds
      templateTaskIds: $templateTaskIds
      userIds: $userIds
    ) {
      taskType
      # ENGG-5125: surveyId is the template task and repeats across an employee's
      # duplicate instances of the same template, so rows are keyed on this.
      instanceTaskId
      surveyCategoryId
      ownerId
      ownerFullName
      ownerEmail
      assigneeIds
      assigneeFullNames
      assigneeEmails
      surveyId
      surveyTitle
      # ENGG-9776: the QuestionSet behind the task. Several tasks share one when
      # the definition lives in the Content Library — the Content Rollup tab
      # groups on it.
      contentId
      contentName
      inContentLibrary
      highestScore
      highestScorePercentage
      latestScore
      latestScorePercentage
      completionDate
      status
      attemptCount
    }
  }
`

export const QUIZ_QUESTION_ANALYTICS = gql`
  query QuizQuestionAnalytics(
    $templateCategoryIds: [ID!]
    $templateTaskIds: [ID!]
    $userIds: [ID!]
  ) {
    quizQuestionAnalytics(
      templateCategoryIds: $templateCategoryIds
      templateTaskIds: $templateTaskIds
      userIds: $userIds
    ) {
      templateTaskId
      templateTaskInternalName
      templateTaskQuestionId
      templateTaskQuestionTitle
      correctCount
      totalCount
      correctPercentage
      incorrectPercentage
    }
  }
`

export const QUIZ_SUBMISSION_DETAILS = gql`
  query QuizSubmissionDetails(
    $templateCategoryId: ID
    $templateTaskId: ID
    $ownerId: ID
  ) {
    quizSubmissionDetails(
      templateCategoryId: $templateCategoryId
      templateTaskId: $templateTaskId
      ownerId: $ownerId
    ) {
      # ENGG-9905: only what QuizSubmissionDetailsModal reads. The owner and
      # task are the modal's own props, so the response need not repeat them.
      assigneeId
      templateTaskSubmissionScore
      templateTaskSubmissionTotalScore
      attemptNumber
      attemptDate
      questions {
        templateTaskQuestionTitle
        answers {
          templateQuestionOptionAnswerTitle
        }
      }
    }
  }
`

// Variables for the surfaces that MANAGE segments and therefore need them in the
// list: the cohort settings page and the employee modal's segment picker.
// Exported so `useQuery` and every `refetchQueries` entry share one object —
// Apollo keys the cache by variables, so a refetch with different variables
// silently updates a different cache entry and the list never refreshes.
export const DATE_COHORTS_WITH_SEGMENTS = { includeSegments: true }

export const GET_DATE_COHORTS = gql`
  query DateCohorts($includeSegments: Boolean) {
    dateCohorts(includeSegments: $includeSegments) {
      id
      name
      description
      independent
      segment
      cohortType
      customFieldDefinitions
      dateTypeId
      dateType {
        id
        name
      }
      targetDateType {
        id
        name
      }
      dateCohortDates {
        id
        fromDate
        toDate
        date
        name
        blackoutDates
        customFieldValues
        capacity
        enrolledCount
        availableSlots
      }
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
      # ENGG-9486: a segment is populated by a background reconcile, so a
      # memberCount of 0 can mean "still being matched" rather than "nobody
      # matches". This says which.
      memberSync {
        dateCohortId
        state
        inProgress
        progressPercentage
        phaseLabel
      }
    }
  }
`

export const TEMPLATE_TASK_HTML_DESCRIPTION = gql`
  query TemplateTaskHTMLDescription($templateTaskId: ID!) {
    templateTaskHtmlDescription(templateTaskId: $templateTaskId) {
      id
      title
      description
    }
  }
`

export const SLACK_CHANNELS = gql`
  query SlackChannels {
    slackChannels {
      id
      name
    }
  }
`

export const SLACK_TEAMS = gql`
  query SlackTeams {
    slackTeams {
      id
      name
    }
  }
`

export const GET_CONTENT_ITEMS = gql`
  query ContentItems {
    contentItems {
      id
      title
      description
      descriptionRaw
      position
      contentItemsSectionId
    }
  }
`

export const GET_CONTENT_ITEMS_SECTIONS = gql`
  query ContentItemsSections {
    contentItemsSections(sectionType: "content_item") {
      id
      title
      position
      parentId
      contentItems {
        id
        title
        description
        descriptionRaw
        position
        contentItemsSectionId
        templatesCount
      }
    }
  }
`

// Same endpoint (`contentItemsSections`) but scoped by sectionType to the
// Question Library tabs. The response carries QuestionSets under each
// section instead of ContentItems. Callers pass the task type as
// `templateTaskType` (UI-consistent naming); we pipe it to the backend
// `sectionType` arg.
export const GET_QUESTION_LIBRARY_SECTIONS = gql`
  query QuestionLibrarySections($templateTaskType: String!) {
    contentItemsSections(sectionType: $templateTaskType) {
      id
      title
      position
      sectionType
      parentId
      questionSets {
        id
        name
        templateTaskType
        position
        contentItemsSectionId
        templatesCount
      }
    }
  }
`

// Content Library "Zoom Templates" tab: presets grouped into shared sections
// (section_type = ZOOM_TEMPLATE), mirroring the question-library tabs.
export const GET_ZOOM_TEMPLATE_SECTIONS = gql`
  query ZoomTemplateSections {
    contentItemsSections(sectionType: "ZOOM_TEMPLATE") {
      id
      title
      position
      sectionType
      zoomTemplatePresets {
        id
        name
        conferenceTool
        position
        contentItemsSectionId
        settings {
          __typename
          ... on ZoomMeetingOptions {
            security
            registrationRequired
            joinBeforeHost
            autoRecording
            muteUponEntry
            questionAndAnswer
            alternativeHosts
            zoomDescription
            templateId
            thirdPartySurvey
          }
          ... on ZoomWebinarOptions {
            altHosts
            altHostsEditPolls
            authAttendees
            authPanelists
            autoRecord
            emailInAttendeeReport
            hdScreenShare
            onDemand
            practiceSession
            qna
            recordLocation
            registrationRequired
            requirePasscode
            videoHost
            videoPanelists
            zoomDescription
            templateId
            thirdPartySurvey
          }
        }
      }
    }
  }
`

// Content Library "SCORM" tab: SCORM courses grouped into shared sections
// (section_type = SCORM), mirroring the question-library / zoom tabs.
export const GET_SCORM_LIBRARY_SECTIONS = gql`
  query ScormLibrarySections {
    contentItemsSections(sectionType: "SCORM") {
      id
      title
      position
      sectionType
      parentId
      scormCourses {
        id
        title
        uploadState
        uploadError
        filename
        contentItemsSectionId
        templatesCount
      }
    }
  }
`

export const GET_VIDEO_LIBRARY_SECTIONS = gql`
  query VideoLibrarySections {
    contentItemsSections(sectionType: "VIDEO") {
      id
      title
      position
      sectionType
      parentId
      videos {
        id
        title
        videoUrl
        filename
        contentItemsSectionId
        templatesCount
      }
    }
  }
`

// Flat list backing the "Zoom Template" selector on the template task page.
export const GET_ZOOM_TEMPLATE_PRESETS = gql`
  query ZoomTemplatePresets($conferenceTool: String) {
    zoomTemplatePresets(conferenceTool: $conferenceTool) {
      id
      name
      conferenceTool
      settings {
        __typename
        ... on ZoomMeetingOptions {
          security
          registrationRequired
          joinBeforeHost
          autoRecording
          muteUponEntry
          questionAndAnswer
          alternativeHosts
          zoomDescription
          templateId
          thirdPartySurvey
        }
        ... on ZoomWebinarOptions {
          altHosts
          altHostsEditPolls
          authAttendees
          authPanelists
          autoRecord
          emailInAttendeeReport
          hdScreenShare
          onDemand
          practiceSession
          qna
          recordLocation
          registrationRequired
          requirePasscode
          videoHost
          videoPanelists
          zoomDescription
          templateId
          thirdPartySurvey
        }
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_FROM_CONTENT_ITEM = gql`
  query TemplateTasksFromContentItem($contentItemId: ID!) {
    templateTasksFromContentItem(contentItemId: $contentItemId) {
      id
      title
      internalName
      templateCategory {
        id
        ancestry
        category
        internalCategory
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_FROM_SCORM_COURSE = gql`
  query TemplateTasksFromScormCourse($scormCourseId: ID!) {
    templateTasksFromScormCourse(scormCourseId: $scormCourseId) {
      id
      title
      internalName
      templateCategory {
        id
        ancestry
        category
        internalCategory
      }
    }
  }
`

// Content Library "AI Roleplay" tab (ENGG-10143). `title` aliases libraryTitle
// so the shared media-library components (which read item.title) work as-is.
export const GET_AI_ROLEPLAY_LIBRARY_SECTIONS = gql`
  query AiRoleplayLibrarySections {
    contentItemsSections(sectionType: "AI_ROLEPLAY") {
      id
      title
      position
      sectionType
      parentId
      aiRoleplays {
        id
        title: libraryTitle
        name
        description
        position
        contentItemsSectionId
        inContentLibrary
        templateTasksCount
        chatPersonaId
        aiRoleplayTypeId
        questionSetId
        chatInstructions
        personaName
        personaPersonality
        tags
        updatedAt
        chatPersona {
          id
          name
        }
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_FROM_AI_ROLEPLAY = gql`
  query TemplateTasksFromAiRoleplay($aiRoleplayId: ID!) {
    templateTasksFromAiRoleplay(aiRoleplayId: $aiRoleplayId) {
      id
      title
      internalName
      templateCategory {
        id
        ancestry
        category
        internalCategory
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_FROM_VIDEO = gql`
  query TemplateTasksFromVideo($videoId: ID!) {
    templateTasksFromVideo(videoId: $videoId) {
      id
      title
      internalName
      templateCategory {
        id
        ancestry
        category
        internalCategory
      }
    }
  }
`

// ENGG-10144: where a question-library template (Survey/Quiz/Scorecard) is used.
export const GET_TEMPLATE_TASKS_FROM_QUESTION_SET = gql`
  query TemplateTasksFromQuestionSet($questionSetId: ID!) {
    templateTasksFromQuestionSet(questionSetId: $questionSetId) {
      id
      title
      internalName
      templateCategory {
        id
        ancestry
        category
        internalCategory
      }
    }
  }
`

// ENGG-10254: progress of a running publish, polled by the Template and
// Template Task pages. The template one lists each publish on screen (oldest
// first, empty when none); the task one is the latest publish, or null.
export const TEMPLATE_PUBLISH_PROGRESSES = gql`
  query TemplatePublishProgresses($templateCategoryId: ID!) {
    templatePublishProgresses(templateCategoryId: $templateCategoryId) {
      id
      inProgress
      completedCount
      totalCount
      failedCount
      reimportOnly
    }
  }
`

export const TEMPLATE_TASK_PUBLISH_PROGRESS = gql`
  query TemplateTaskPublishProgress($templateTaskId: ID!) {
    templateTaskPublishProgress(templateTaskId: $templateTaskId) {
      inProgress
      completedCount
      totalCount
      failedCount
    }
  }
`

// ENGG-10318: a grid "Publish all" run, counted in template tasks.
export const PUBLISH_ALL_PROGRESS = gql`
  query PublishAllProgress($runId: ID!) {
    publishAllProgress(runId: $runId) {
      id
      inProgress
      completedCount
      totalCount
      failedCount
    }
  }
`

export const GET_BULK_ASSIGN_TEMPLATES = gql`
  query BulkAssignTemplates($userId: ID) {
    bulkAssignTemplates(userId: $userId) {
      imports {
        id
        status
        createdAt
        finishedAt
        reimport
        user {
          id
          fullName
          email
        }
        assignments {
          id
          user {
            id
            fullName
            email
          }
          templateCategory {
            id
            category
            internalCategory
          }
          error
          createdAt
        }
      }
      reimports {
        id
        status
        createdAt
        finishedAt
        reimport
        length
        user {
          id
          fullName
          email
        }
        errors {
          id
          user {
            id
            fullName
            email
          }
          templateCategory {
            id
            category
            internalCategory
          }
          error
          createdAt
        }
        workflows {
          id
          category
          internalCategory
        }
      }
      deletes {
        id
        status
        createdAt
        finishedAt
        user {
          id
          fullName
          email
        }
        assignments {
          id
          user {
            id
            fullName
            email
          }
          templateCategory {
            id
            category
            internalCategory
          }
          error
          createdAt
        }
      }
    }
  }
`

export const GET_BULK_ASSIGN_ERRORS = gql`
  query BulkAssignErrors($userId: ID!) {
    bulkAssignErrors(userId: $userId) {
      id
      status
      createdAt
      finishedAt
      reimport
      user {
        id
        fullName
        email
      }
      assignments {
        id
        user {
          id
          fullName
          email
        }
        templateCategory {
          id
          category
          internalCategory
        }
        error
        createdAt
      }
    }
  }
`

export const GET_BULK_ASSIGN_IMPORTS = gql`
  query BulkAssignImports(
    $pagination: PaginationInput
    $filters: BulkAssignFiltersInput
  ) {
    bulkAssignImports(pagination: $pagination, filters: $filters) {
      data {
        id
        status
        createdAt
        finishedAt
        reimport
        filters
        progressPercentage
        completedCount
        totalCount
        user {
          id
          fullName
          email
        }
        assignments {
          id
          user {
            id
            fullName
            email
          }
          templateCategory {
            id
            category
            internalCategory
          }
          error
          createdAt
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_BULK_ASSIGN_REIMPORTS = gql`
  query BulkAssignReimports(
    $pagination: PaginationInput
    $filters: BulkAssignFiltersInput
  ) {
    bulkAssignReimports(pagination: $pagination, filters: $filters) {
      data {
        id
        status
        createdAt
        finishedAt
        reimport
        filters
        length
        progressPercentage
        completedCount
        totalCount
        user {
          id
          fullName
          email
        }
        errors {
          id
          user {
            id
            fullName
            email
          }
          templateCategory {
            id
            category
            internalCategory
          }
          error
          createdAt
        }
        workflows {
          id
          category
          internalCategory
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_BULK_ASSIGN_DELETES = gql`
  query BulkAssignDeletes(
    $pagination: PaginationInput
    $filters: BulkAssignFiltersInput
  ) {
    bulkAssignDeletes(pagination: $pagination, filters: $filters) {
      data {
        id
        status
        createdAt
        finishedAt
        filters
        progressPercentage
        completedCount
        totalCount
        user {
          id
          fullName
          email
        }
        assignments {
          id
          user {
            id
            fullName
            email
          }
          templateCategory {
            id
            category
            internalCategory
          }
          error
          createdAt
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

// Only users who have actually run a bulk assignment, so the "Created by"
// filter stays a short list instead of the whole employee directory.
export const GET_BULK_ASSIGN_CREATORS = gql`
  query BulkAssignCreators {
    bulkAssignCreators {
      id
      email
      fullName
    }
  }
`

export const GET_BULK_ASSIGN_TEMPLATE = gql`
  query BulkAssignTemplate($id: ID!) {
    bulkAssignTemplate(id: $id) {
      id
      status
      createdAt
      finishedAt
      reimport
      filters
      user {
        id
        fullName
        email
      }
      assignments {
        id
        user {
          id
          fullName
          email
        }
        templateCategory {
          id
          category
          internalCategory
        }
        error
        createdAt
      }
    }
  }
`

export const GET_BULK_ASSIGN_CERTIFICATION_PREVIEW = gql`
  query BulkAssignCertificationPreview(
    $userIds: [ID!]!
    $certificateIds: [ID!]!
  ) {
    bulkAssignCertificationPreview(
      userIds: $userIds
      certificateIds: $certificateIds
    ) {
      userId
      certificateId
      issuedAt
      scheduled
      source
    }
  }
`

export const GET_BULK_ASSIGN_BADGE_PREVIEW = gql`
  query BulkAssignBadgePreview($userIds: [ID!]!, $badgeIds: [ID!]!) {
    bulkAssignBadgePreview(userIds: $userIds, badgeIds: $badgeIds) {
      userId
      badgeId
      issuedAt
      scheduled
      source
    }
  }
`

export const GET_BULK_ASSIGN_CERTIFICATIONS = gql`
  query BulkAssignCertifications($pagination: PaginationInput) {
    bulkAssignCertifications(pagination: $pagination) {
      data {
        id
        status
        createdAt
        finishedAt
        issuedAt
        filters
        user {
          id
          fullName
          email
        }
        assignments {
          id
          user {
            id
            fullName
            email
          }
          certificate {
            id
            name
          }
          error
          skipped
          skippedReason
          finishedAt
          createdAt
          userCertification {
            id
            processing
            scheduled
          }
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_BULK_ASSIGN_BADGES_LIST = gql`
  query BulkAssignBadgesList($pagination: PaginationInput) {
    bulkAssignBadgesList(pagination: $pagination) {
      data {
        id
        status
        createdAt
        finishedAt
        issuedAt
        filters
        progressPercentage
        user {
          id
          fullName
          email
        }
        assignments {
          id
          user {
            id
            fullName
            email
          }
          badge {
            id
            name
            title
          }
          error
          skipped
          skippedReason
          finishedAt
          createdAt
          userBadge {
            id
            processing
            scheduled
          }
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

// ENGG-9764 — employees holding the template; a republish prompt still makes
// sense for a task nobody holds when these owners may now match it.
export const GET_TEMPLATE_ACTIVE_WORKFLOWS_COUNT = gql`
  query TemplateActiveWorkflowsCount($templateCategoryId: ID!) {
    templateCategory(templateCategoryId: $templateCategoryId) {
      id
      activeWorkflowsCount
    }
  }
`

export const GET_USERS_WITH_TEMPLATE_TASK = gql`
  query UsersWithTemplateTask($id: ID!, $userIds: [ID!]) {
    usersWithTemplateTask(id: $id, userIds: $userIds) {
      id
      email
      firstName
      lastName
      fullName
      startDate
    }
  }
`

export const GET_DOCEBO_COURSES = gql`
  query DoceboCourses {
    doceboCourses {
      id
      name
    }
  }
`

export const GET_CLIENT_CONFIGURATIONS = gql`
  query Configuration {
    configuration {
      clientLogo
      faviconUrl
      appName
      slackSenderName
      slackSenderIconUrl
      backgroundUrl
      welcomeIconUrl
      primaryColor
      fontColor
      iconColor
      cantCompleteAnnouncement
      categoryColors
      emailHeaderIconId
      emailFooterIconId
      emailHeaderUrl
      emailFooterUrl
      emailBackgroundColor
      timezone
      emailBorderColor
      emailFrom
      emailFromName
      emailLaunchButtonColor
      emailLaunchButtonTextColor
      emailLaunchButtonText
      emailLaunchButtonPath
      emailSupportText
      courseFallbackImageUrl
      courseErrorImageUrl
      linkedinOrganizationId
      linkedinCompanyPageUrl
      certIssuingAuthority
      clientName
    }
  }
`

export const GET_USERS_WITH_TEMPLATE_CATEGORY = gql`
  query UsersWithTemplateCategory($templateCategoryId: ID!, $userIds: [ID!]) {
    usersWithTemplateCategory(
      templateCategoryId: $templateCategoryId
      userIds: $userIds
    ) {
      id
      email
      firstName
      lastName
      fullName
      startDate
    }
  }
`

export const GET_USERS_WITHOUT_TEMPLATE_CATEGORY = gql`
  query UsersWithoutTemplateCategoryQuery(
    $templateCategoryId: ID!
    $userIds: [ID!]
  ) {
    usersWithoutTemplateCategory(
      templateCategoryId: $templateCategoryId
      userIds: $userIds
    ) {
      id
      email
      firstName
      lastName
      fullName
      startDate
    }
  }
`

export const GET_CLIENT_SECRET = gql`
  query ClientSecret($issuer: String!) {
    clientSecret(issuer: $issuer) {
      id
      issuer
      identifier
      secretHint
      data
    }
  }
`

export const GET_SCORM_COURSE = gql`
  query ScormCourse($id: ID!) {
    scormCourse(id: $id) {
      id
      title
      uploadState
      uploadError
      filename
    }
  }
`

export const SCORM_COURSE_PREVIEW_URL = gql`
  query ScormCoursePreviewUrl($templateTaskId: ID, $scormCourseId: ID) {
    scormCoursePreviewUrl(
      templateTaskId: $templateTaskId
      scormCourseId: $scormCourseId
    )
  }
`

export const GET_TEMPLATE_TASKS_CONNECTED_TO_DATE_TYPE = gql`
  query TemplateTasksConnectedToDateType($dateTypeId: ID!) {
    templateTasksConnectedToDateType(dateTypeId: $dateTypeId) {
      id
    }
  }
`

export const GET_TEMPLATE_TASK_OVERRIDE_RULES = gql`
  query TemplateTaskOverrideRules($templateTaskId: ID!) {
    templateTaskOverrideRules(templateTaskId: $templateTaskId) {
      id
      userGroup {
        userGroupConditions {
          id
          entityId
          value
        }
      }
      overrideValues {
        id
        value
        taskPropertyName
      }
    }
  }
`

// ENGG-10286: the date each session move points at (TemplateTaskOverrideRulesModal).
// Apart from GET_TEMPLATE_TASK_OVERRIDE_RULES and asked only when a rule holds a
// move, so a build that ships before the backend never sends the new field (an
// unknown field fails the whole query).
export const GET_TEMPLATE_TASK_SESSION_MOVES = gql`
  query TemplateTaskSessionMoves($templateTaskId: ID!) {
    templateTaskOverrideRules(templateTaskId: $templateTaskId) {
      id
      overrideValues {
        id
        taskPropertyName
        movedToSession {
          startAt
          timezone
          cancelled
        }
      }
    }
  }
`

// ENGG-10286: the cohort dates an override rule keeps a session off (a move or
// Block Visibility), for the Events tab's note on a date with no session
// (useDeletedSessionsByDate).
export const GET_TEMPLATE_TASK_DELETED_SESSIONS = gql`
  query TemplateTaskDeletedSessions($templateTaskId: ID!) {
    templateTaskOverrideRules(templateTaskId: $templateTaskId) {
      id
      userGroup {
        userGroupConditions {
          id
          entityId
          value
        }
      }
      overrideValues {
        id
        taskPropertyName
        value
        movedToSession {
          startAt
          timezone
          cancelled
        }
      }
    }
  }
`

export const GET_REPORTS = gql`
  query Reports {
    reports {
      id
      name
      message
      config
      latestReportJob {
        state
        createdAt
      }
      reportDateSetting {
        dateType {
          id
          name
          baseDateType {
            id
            name
          }
        }
        days
        relation
        useBusinessDays
      }
      oneEmployeePerEmail
      viewConfig
      rrule
      folderId
      filterByTemplateAssignment
      sendAtTime
      sendAtTimezone
      scheduleEndAt
      createdAt
      createdBy
    }
  }
`

// Lean listing for the workflow-analytics reports panel. When
// `templateCategoryId` is passed, returns only reports whose stored scope
// contains that template (single-template Reports tab). When omitted,
// returns every workflow report for the client (Global Reports tab).
// Returns only the fields the panel + edit modal read (no viewConfig, no
// latestReportJob, no columns_order blob).
export const GET_WORKFLOW_REPORTS = gql`
  query WorkflowReports($templateCategoryId: ID) {
    workflowReports(templateCategoryId: $templateCategoryId) {
      id
      name
      message
      config
      rrule
      sendAtTime
      sendAtTimezone
      scheduleEndAt
      oneEmployeePerEmail
      reportDateSetting {
        dateType {
          id
          name
          baseDateType {
            id
            name
          }
        }
        days
        relation
        useBusinessDays
      }
    }
  }
`

// Populates the task filter in the workflow (email) report modal. Covers
// every selected template in one round trip — GET_TEMPLATE_TASKS is
// per-category and drags in a much larger field set. `templateCategory` is
// the task's *section* (sections are template categories); its `parent` is
// the root template the task belongs to.
export const GET_TEMPLATE_TASKS_FOR_WORKFLOW_REPORT = gql`
  query TemplateTasksForWorkflowReport($templateCategoryIds: [ID!]!) {
    templateTasksForWorkflowReport(templateCategoryIds: $templateCategoryIds) {
      id
      title
      taskType
      inactive
      excludedFromReports
      templateCategoryId
      templateCategory {
        id
        internalCategory
        parent {
          id
          internalCategory
        }
      }
    }
  }
`

export const GET_REPORT_FOLDERS = gql`
  query ReportFolders {
    reportFolders {
      id
      name
      parentId
      children {
        id
        name
        parentId
        reportsCount
      }
      reportsCount
    }
  }
`

export const GET_LATEST_REPORT_JOB = gql`
  query LatestReportJob($id: ID!) {
    latestReportJob(id: $id) {
      id
      state
      data
    }
  }
`

// Lean variant that omits the (potentially large) generated `data` payload.
// Used by ReportJobSubscriber to catch up on a report's current job state when
// the ActionCable subscription connects, in case the one-shot completion
// broadcast fired before the socket was listening (ENGG-9578).
export const GET_LATEST_REPORT_JOB_STATE = gql`
  query LatestReportJobState($id: ID!) {
    latestReportJob(id: $id) {
      id
      state
    }
  }
`

export const GET_REPORT_EMAILS = gql`
  query ReportEmails($reportId: ID!) {
    reportEmails(reportId: $reportId) {
      id
      status
      userEmail
      createdAt
      updatedAt
      reportNotificationId
      user {
        id
        email
        firstName
        lastName
        fullName
      }
    }
  }
`

export const GET_REPORT_EMAIL_RECIPIENTS = gql`
  query ReportEmailRecipients($id: ID!) {
    reportEmailRecipients(id: $id) {
      totalCount
      recipients {
        email
        name
        source
        employees {
          email
          name
        }
      }
    }
  }
`

export const CUSTOM_REPORT_RECIPIENT_EMAIL_PREVIEW = gql`
  query CustomReportRecipientEmailPreview($id: ID!, $email: String!) {
    customReportRecipientEmailPreview(id: $id, email: $email) {
      skipped
      skippedReason
      subject
      html
      attachments {
        filename
        contentType
        contentBase64
      }
    }
  }
`

export const WORKFLOW_REPORT_RECIPIENT_EMAIL_PREVIEW = gql`
  query WorkflowReportRecipientEmailPreview($id: ID!, $email: String!) {
    workflowReportRecipientEmailPreview(id: $id, email: $email) {
      skipped
      skippedReason
      subject
      html
      attachments {
        filename
        contentType
        contentBase64
      }
    }
  }
`

export const GET_REPORT_AI_CHAT_SESSION = gql`
  query ReportAiChatSession($id: ID, $reportId: ID) {
    reportAiChatSession(id: $id, reportId: $reportId) {
      id
      reportId
      name
      prompt
      feedback
      status
      messages {
        id
        role
        content
        fileUrls
        fileNames
        createdAt
      }
      createdAt
    }
  }
`

export const GET_REPORT_AI_CHAT_SESSIONS = gql`
  query ReportAiChatSessions($reportId: ID!) {
    reportAiChatSessions(reportId: $reportId) {
      id
      reportId
      name
      prompt
      status
      createdAt
    }
  }
`

export const GET_REPORT_AI_SUGGESTION_SET = gql`
  query ReportAiSuggestionSet($reportId: ID!) {
    reportAiSuggestionSet(reportId: $reportId) {
      id
      reportId
      status
      suggestions {
        id
        content
        position
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_TEMPLATE_TASK_CHANGES = gql`
  query TemplateTaskChanges($templateTaskId: ID!) {
    templateTaskChanges(templateTaskId: $templateTaskId) {
      id
      user {
        id
        fullName
      }
      property
      change
      createdAt
    }
  }
`

// Custom roleplay instruction versions, newest first. The list carries only
// metadata; the text for a diff is fetched per version.
export const GET_TEMPLATE_TASK_INSTRUCTION_VERSIONS = gql`
  query TemplateTaskInstructionVersions($templateTaskId: ID!) {
    templateTaskInstructionVersions(templateTaskId: $templateTaskId) {
      id
      createdAt
      addedWordCount
      removedWordCount
      user {
        id
        fullName
      }
    }
  }
`

export const GET_TEMPLATE_TASK_INSTRUCTION_VERSION = gql`
  query TemplateTaskInstructionVersion($templateTaskId: ID!, $versionId: ID!) {
    templateTaskInstructionVersion(
      templateTaskId: $templateTaskId
      versionId: $versionId
    ) {
      id
      oldText
      newText
    }
  }
`

export const GET_CALENDAR_RESOURCES = gql`
  query CalendarResources {
    calendarResources {
      id
      name
      email
    }
  }
`

export const GET_CALENDAR_CATEGORIES = gql`
  query CalendarCategories {
    calendarCategories {
      id
      name
      color
    }
  }
`

export const GET_PREVIEW_TASKS = gql`
  query previewTasks(
    $dateFilters: [PreviewTaskDateFilterInput!]!
    $templateCategoryIds: [String!]
    $taskTypes: [String!]
    $taxonomyTermIds: [String!]
    $templateRoleIds: [String!]
    $previewAsUserId: ID
    $includeUndatedTasks: Boolean
  ) {
    previewTasks(
      dateFilters: $dateFilters
      templateCategoryIds: $templateCategoryIds
      taskTypes: $taskTypes
      taxonomyTermIds: $taxonomyTermIds
      templateRoleIds: $templateRoleIds
      previewAsUserId: $previewAsUserId
      includeUndatedTasks: $includeUndatedTasks
    ) {
      id
      title
      internalName
      description
      dueDate
      dueDateType
      fixedDueDateDetailed {
        date
        time
        timezone
      }
      taskType
      templateCategoryId
      start
      end
      state
      sectionName
      categoryName
      taxonomyTerms {
        id
        title
        key
        taxonomyId
      }
      templateTaskDateSetting {
        ...TemplateTaskDateSettingsFields
      }
      assignDate {
        ...TemplateTaskDateSettingsFields
      }
      templateRoles {
        id
        name
      }
      owner {
        id
        fullName
        email
      }
      inactive
      draft
      preBoarding
      offboarding
      important
      calendarEventOptions {
        allDay
      }
    }
  }

  ${FRAGMENT_TEMPLATE_TASK_DATE_SETTINGS}
`

export const GET_GROUP_TASK_FILTERS = gql`
  query GroupTaskFilters($templateCategoryId: ID!) {
    groupTaskFilters(templateCategoryId: $templateCategoryId) {
      dateId
      dateName
      dateValue
      __typename
    }
  }
`

export const GET_GROUP_TASKS = gql`
  query GroupTasks(
    $templateCategoryId: ID!
    $filter: [GroupTaskFilterInput!]!
    $templateTaskIds: [ID!]
  ) {
    groupTasks(
      templateCategoryId: $templateCategoryId
      filter: $filter
      templateTaskIds: $templateTaskIds
    ) {
      ...GroupTaskFields
      calendarEvent {
        title
        start
        startAt
      }
    }
  }

  ${FRAGMENT_GROUP_TASK_FIELDS}
`

export const GET_GROUP_TASK = gql`
  query GroupTask($id: ID!) {
    groupTask(id: $id) {
      ...GroupTaskFields
      latestCalendarEvent {
        id
        title
        summary
        attendees {
          email
          name
        }
        start
        startAt
        end
        duration
        organizer
        creator
        timezone
        status
        location
        facilitators
        resources
      }
    }
  }
  ${FRAGMENT_GROUP_TASK_FIELDS}
`

// ENGG-10286: the later date the Events tab's delete dialog offers to move a
// deleted date's employees to. Asked only once "Permanently delete" is ticked
// (GroupEvents), and in its own document, so a backend without these fields
// fails only this query: the option shows "could not be loaded" and the global
// error snackbar appears, but the delete itself still works.
export const GET_GROUP_TASK_NEXT_SESSION = gql`
  query GroupTaskNextSession($id: ID!) {
    groupTask(id: $id) {
      id
      baseDateType {
        id
      }
      nextSession {
        groupTaskId
        startAt
        timezone
        cancelled
      }
      nextSessionUnavailableReason
    }
  }
`

export const GET_QUESTION_SECTIONS = gql`
  query QuestionSections($questionSetId: ID!) {
    templateTaskQuestionSections(questionSetId: $questionSetId) {
      id
      title
      description
      position
    }
  }
`

export const GET_QUESTION_SET = gql`
  query QuestionSet($id: ID!) {
    questionSet(id: $id) {
      id
      name
      templateTaskType
      position
      contentItemsSectionId
      amountOfRequiredQuestions
      amountOfRetriesAllowed
      templateTaskQuestions {
        id
        title
        required
        description
        explanation
        order
        quizType
        updatedAt
        templateTaskQuestionSectionId
        dependsOnQuestionOptionIds
        metadata
        options {
          id
          title
          correct
          description
          score
          order
          taxonomyId
          dateTypeId
          roleId
          propertyName
          autoLock
        }
      }
    }
  }
`

export const GET_CHAT_SESSION = gql`
  query ChatSession($id: ID!) {
    chatSession(id: $id) {
      id
      audioUrl
    }
  }
`

// ENGG-8736: chat session detail for the Roleplay Analytics drawer
// (transcript turns + AI realism breakdown).
// ENGG-8805 adds callStatus/disconnectReason (why the call ended).
// ENGG-9056 adds callEndedBy/elevenlabsEndCallReason (normalized ending).
export const ROLEPLAY_CHAT_SESSION = gql`
  query RoleplayChatSession($id: ID!) {
    chatSession(id: $id) {
      id
      audioUrl
      transcription
      realismScore
      callStatus
      disconnectReason
      callEndedBy
      elevenlabsEndCallReason
      # ENGG-9512: the Overall Score verdict the employee sees on their
      # scorecard. Null on attempts scored before the model was asked for it.
      overallSummary
    }
  }
`

export const GET_INSTALLED_SLACK_USERS = gql`
  query InstalledSlackUsers {
    installedSlackUsers {
      users {
        id
        email
        firstName
        lastName
        fullName
        avatarUrl
      }
      defaultUser {
        id
        email
        firstName
        lastName
        fullName
      }
    }
  }
`

export const GET_PERSONAS = gql`
  query GetPersonas {
    chatPersonas {
      id
      name
      jobTitle
      company
      voice
      demeanor
      additionalBackground
      responseLength
      smallTalk
      talkingSpeed
      englishOnly
      fillerWords
      concerns
      moodTone
      tags
      chatPersonaAvatar {
        id
        imageUrl
      }
    }
  }
`

export const GET_SLACK_GROUPS = gql`
  query SlackGroups {
    slackGroups {
      id
      dueDate
      channelId
      dateTypeValue
      inviteeCount
      facilitatorEmails
      templateTask {
        id
        title
        internalName
        workflow {
          id
        }
      }
      dateType {
        id
        name
      }
      errors {
        id
        errorType
        rawError
        userFriendlyError
        retryable
        createdAt
        task {
          id
          title
        }
      }
      postedMessageHtml
      postedMessageAt
      sentFollowUps {
        id
        sentAt
        sentMessage
        communicationSchedule {
          id
          title
        }
      }
    }
  }
`

export const GET_SLACK_CHANNEL_MEMBERS = gql`
  query SlackChannelMembers($slackGroupId: ID!) {
    slackChannelMembers(slackGroupId: $slackGroupId) {
      slackUserId
      email
      name
      isBot
    }
  }
`

export const GET_SLACK_COMMUNICATION_SCHEDULES = gql`
  query communicationSchedules($slackGroupId: ID!) {
    communicationSchedules(slackGroupId: $slackGroupId) {
      ...CommunicationSchedulesFields
    }
  }
  ${FRAGMENT_COMMUNICATION_SCHEDULES}
`

export const GET_TEMPLATE_TASK_COMMUNICATION_SCHEDULES = gql`
  query TemplateTaskCommunicationSchedules($id: ID!) {
    templateTask(id: $id) {
      id
      communicationSchedules {
        ...CommunicationSchedulesFieldsWithoutPreview
      }
    }
  }
  ${FRAGMENT_COMMUNICATION_SCHEDULES_WITHOUT_PREVIEW}
`

export const GET_SLACK_CHANNELS_INFO = gql`
  query SlackChannelsInfo($channelIds: [ID!]!) {
    slackChannelsInfo(channelIds: $channelIds) {
      id
      name
      isArchived
    }
  }
`

export const GET_SLACK_GROUP_INVITEES = gql`
  query SlackGroupInvitees($slackGroupId: ID!) {
    slackGroupInvitees(slackGroupId: $slackGroupId) {
      task {
        id
        title
      }
      users {
        id
        firstName
        lastName
        fullName
        email
      }
      errors {
        id
        errorType
        rawError
        userFriendlyError
        retryable
        createdAt
      }
    }
  }
`

export const UPSERT_COMMUNICATION_SCHEDULE_OVERRIDE = gql`
  mutation UpsertCommunicationScheduleOverride(
    $slackGroupId: ID!
    $communicationScheduleId: ID!
    $detailed: DetailedDateTimeInput!
  ) {
    upsertCommunicationScheduleOverride(
      slackGroupId: $slackGroupId
      communicationScheduleId: $communicationScheduleId
      detailed: $detailed
    ) {
      success
    }
  }
`

export const REMOVE_COMMUNICATION_SCHEDULE_OVERRIDE = gql`
  mutation RemoveCommunicationScheduleOverride(
    $slackGroupId: ID!
    $communicationScheduleId: ID!
  ) {
    removeCommunicationScheduleOverride(
      slackGroupId: $slackGroupId
      communicationScheduleId: $communicationScheduleId
    ) {
      success
    }
  }
`

export const GET_AI_ROLEPLAY_TYPES = gql`
  query GetAiRoleplayTypes {
    aiRoleplayTypes {
      id
      name
      description
      key
      wizardInstructions
      wizardScorecardInstructions
      wizardPopulateScorecardInstructions
      wizardPopulatePromptInstructions
      wizardPopulateDescriptionInstructions
    }
  }
`

export const GET_COHORT_EMAILS = gql`
  ${FRAGMENT_COHORT_EMAIL_FIELDS}
  query CohortEmails($dateCohortId: ID!) {
    cohortEmails(dateCohortId: $dateCohortId) {
      ...CohortEmailFields
      id
    }
  }
`

export const GET_COHORT_SCHEDULES = gql`
  ${FRAGMENT_COHORT_SCHEDULE_FIELDS}
  query CohortSchedules($dateCohortId: ID!) {
    cohortSchedules(dateCohortId: $dateCohortId) {
      ...CohortScheduleFields
    }
  }
`

// Triggers covering a given template task (directly or via an ancestor
// template). Powers the "Manage triggers" modal on the template-task page.
// Selects the cohort name on top of the shared fields so the modal can label
// each trigger by its cohort.
export const GET_TEMPLATE_TASK_COHORT_SCHEDULES = gql`
  ${FRAGMENT_COHORT_SCHEDULE_FIELDS}
  query TemplateTaskCohortSchedules($templateTaskId: ID!) {
    templateTaskCohortSchedules(templateTaskId: $templateTaskId) {
      ...CohortScheduleFields
      dateCohort {
        id
        name
      }
    }
  }
`

export const GET_ELIGIBLE_TEMPLATE_TASKS_FOR_COHORT_SCHEDULE = gql`
  query EligibleTemplateTasksForCohortSchedule($dateCohortId: ID!) {
    eligibleTemplateTasksForCohortSchedule(dateCohortId: $dateCohortId) {
      id
      title
      internalName
    }
  }
`

export const GET_ELIGIBLE_TEMPLATE_CATEGORIES_FOR_COHORT_SCHEDULE = gql`
  query EligibleTemplateCategoriesForCohortSchedule($dateCohortId: ID!) {
    eligibleTemplateCategoriesForCohortSchedule(dateCohortId: $dateCohortId) {
      id
      category
      internalCategory
    }
  }
`

export const GET_COHORT_SCHEDULE_STATUS_FOR_DATE = gql`
  query CohortScheduleStatusForDate($dateCohortDateId: ID!) {
    cohortScheduleStatusForDate(dateCohortDateId: $dateCohortDateId) {
      status
      scheduledAt
      latestFiredAt
      latestError
      forced
      forcedByName
      schedule {
        id
        relation
        days
        useBusinessDays
        fireAtTime
        timezone
        active
        templateTasks {
          id
          title
          internalName
        }
        templateCategories {
          id
          category
          internalCategory
        }
        dateCohort {
          id
          name
          dateType {
            id
            name
          }
        }
      }
    }
  }
`

export const GET_COHORT_SCHEDULE_EVENTS = gql`
  query CohortScheduleEvents($cohortScheduleId: ID!, $limit: Int) {
    cohortScheduleEvents(cohortScheduleId: $cohortScheduleId, limit: $limit) {
      id
      status
      scheduledAt
      firedAt
      error
      groupTaskId
      templateTask {
        id
        title
        internalName
      }
      dateCohortDate {
        id
        date
        toDate
        name
      }
    }
  }
`

export const GET_TEMPLATE_TASKS_GROUPED_BY_TEMPLATE_FOR_COHORT_SCHEDULE = gql`
  query TemplateTasksGroupedByTemplateForCohortSchedule($dateCohortId: ID!) {
    templateTasksGroupedByTemplateForCohortSchedule(
      dateCohortId: $dateCohortId
    ) {
      template {
        id
        category
        internalCategory
      }
      sections {
        sectionPath
        tasks {
          id
          title
          internalName
        }
      }
    }
  }
`

export const GET_AVAILABLE_USER_LIST_FIELDS = gql`
  query AvailableUserListFields {
    availableUserListFields {
      attributes {
        type
        key
        label
      }
      customFields {
        type
        key
        label
      }
      taxonomies {
        type
        taxonomyId
        label
      }
      dateTypes {
        type
        dateTypeId
        label
      }
    }
  }
`

export const GET_AI_ROLEPLAY_FILES = gql`
  query GetAiRoleplayFiles {
    aiRoleplayFiles {
      id
      name
      url
      processedTextUrl
      processingStatus
    }
  }
`

export const GET_AI_ROLEPLAY = gql`
  query GetAiRoleplay($id: ID!) {
    aiRoleplay(id: $id) {
      id
      clientId
      name
      contentItemsSectionId
      inContentLibrary
      chatPersonaId
      aiRoleplayTypeId
      questionSetId
      chatInstructions
      botOpenerEnabled
      instructions
      guidelines
      successCriteria
      timeLimit
      screenShareEnabled
      subtitlesEnabled
      aiEndedRoleplayEnabled
      personaHandoffEnabled
      personaHandoffCondition
      personaHandoffPrompt
      personaHandoffOverridePrompt
      personaHandoffOpeningLine
      personaHandoffPersona {
        id
        kind
        chatPersonaId
        name
        voice
        role
        organization
        personality
        moodTone
        responseLength
        talkingSpeed
        smallTalk
        englishOnly
        fillerWords
        background
        concerns
        position
        markupLabel
        avatarUrl
      }
      personaPanelEnabled
      personaMode
      personas {
        id
        kind
        chatPersonaId
        name
        voice
        role
        organization
        personality
        moodTone
        responseLength
        talkingSpeed
        smallTalk
        englishOnly
        fillerWords
        background
        concerns
        position
        markupLabel
        avatarUrl
      }
      audioTags {
        tag
        description
      }
      description
      learningObjectives
      displayImageUrl
      published
      tags
      openingLines {
        id
        line
        position
      }
      closingLines {
        id
        line
        definition
        lineType
        position
      }
      keyTopics {
        id
        topic
        description
        successCriteria
        mustCover
        position
      }
      keyTerms {
        id
        term
        definition
        exampleUsage
        pronunciation
        position
      }
      checkpoints {
        id
        timeSeconds
        description
        position
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_TEMPLATE_TASK_AI_ROLEPLAY_PROMPT = gql`
  query GetTemplateTaskAiRoleplayPrompt($templateTaskId: ID!) {
    templateTaskAiRoleplayPrompt(templateTaskId: $templateTaskId)
  }
`

export const GET_PERSONA = gql`
  query GetPersona($id: ID!) {
    chatPersona(id: $id) {
      id
      name
      jobTitle
      company
      voice
      demeanor
      additionalBackground
      responseLength
      smallTalk
      talkingSpeed
      englishOnly
      fillerWords
      concerns
      moodTone
      tags
      linkedRoleplayTypeKeys
      chatPersonaAvatar {
        id
        imageUrl
      }
    }
  }
`

// ENGG-9520 — the client's own practice stages, in order, with each one's
// default scorecard. Empty until an admin adds one.
export const GET_PRACTICE_CUSTOM_STAGES = gql`
  query GetPracticeCustomStages {
    practiceCustomStages {
      id
      name
      position
      scorecardTemplate {
        id
        name
      }
    }
  }
`
// ENGG-9520 — every open HubSpot deal stage and the practice scorecard it
// defaults to. `connected`/`error` say why the list might be empty.
export const GET_PRACTICE_STAGE_SCORECARDS = gql`
  query GetPracticeStageScorecards {
    practiceStageScorecards {
      connected
      error
      stages {
        pipelineId
        pipelineLabel
        stageId
        stageLabel
        scorecardTemplate {
          id
          name
        }
      }
    }
  }
`

export const GET_SCORECARD_TEMPLATES = gql`
  query GetScorecardTemplates {
    scorecardTemplates {
      id
      name
      description
      tags
      templateTaskQuestionSections {
        id
        title
        position
      }
      templateTaskQuestions {
        id
        title
        description
        explanation
        required
        quizType
        order
        templateTaskQuestionSectionId
        options {
          id
          title
          description
          score
          correct
          order
        }
      }
    }
  }
`

export const GET_SCORECARD_TEMPLATE = gql`
  query GetScorecardTemplate($id: ID!) {
    scorecardTemplate(id: $id) {
      id
      name
      description
      tags
      templateTaskQuestionSections {
        id
        title
        position
      }
      templateTaskQuestions {
        id
        title
        description
        explanation
        required
        quizType
        order
        templateTaskQuestionSectionId
        options {
          id
          title
          description
          score
          correct
          order
        }
      }
    }
  }
`

export const GET_AI_ROLEPLAYS = gql`
  query GetAiRoleplays {
    aiRoleplays {
      id
      clientId
      botOpenerEnabled
      instructions
      guidelines
      successCriteria
      timeLimit
      screenShareEnabled
      subtitlesEnabled
      aiEndedRoleplayEnabled
      audioTags {
        tag
        description
      }
      description
      learningObjectives
      displayImageUrl
      published
      tags
      personaName
      personaPersonality
      createdAt
      updatedAt
    }
  }
`

export const GET_KEY_TERMS_TEMPLATES = gql`
  query GetKeyTermsTemplates {
    keyTermsTemplates {
      id
      name
      description
      tags
      keyTermsTemplateItems {
        id
        term
        definition
        exampleUsage
        pronunciation
        position
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_KEY_TERMS_TEMPLATE = gql`
  query GetKeyTermsTemplate($id: ID!) {
    keyTermsTemplate(id: $id) {
      id
      name
      description
      tags
      keyTermsTemplateItems {
        id
        term
        definition
        exampleUsage
        pronunciation
        position
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_CLIENT_PRONUNCIATION_TERMS = gql`
  query GetClientPronunciationTerms {
    clientPronunciationTerms {
      id
      term
      pronunciation
      position
    }
  }
`

export const GET_COHORT_EMAIL_BY_DATE = gql`
  query CohortEmailByDate($cohortEmailId: ID!, $dateCohortDateId: ID!) {
    cohortEmailByDate(
      cohortEmailId: $cohortEmailId
      dateCohortDateId: $dateCohortDateId
    ) {
      id
      title
      description
      clientId
      dateCohortId
      scheduledAtPreview
      dateCohort {
        id
        name
        dateType {
          id
          name
        }
      }
      relation
      days
      useBusinessDays
      messageSendAt
      timezone
      active
      createdAt
      updatedAt
      cohortEmailHistories {
        id
        sentAt
        scheduledAt
        error
      }
    }
  }
`

export const GET_DATE_COHORT_DATE_ELIGIBLE_USERS = gql`
  query DateCohortDateEligibleUsers(
    $dateCohortId: ID!
    $from: ISO8601Date!
    $to: ISO8601Date!
  ) {
    dateCohortDateEligibleUsers(
      dateCohortId: $dateCohortId
      fromDate: $from
      toDate: $to
    ) {
      id
      firstName
      lastName
      fullName
      email
    }
  }
`
// Deliberately its own query rather than a field on FRAGMENT_USER_FIELDS: that
// fragment is shared with GET_USERS, which powers the paginated employee grid,
// and cohortMemberships is a join per employee. Only the profile drawer needs it.
export const GET_USER_SEGMENT_MEMBERSHIPS = gql`
  query UserSegmentMemberships($id: ID!) {
    user(userId: $id) {
      id
      cohortMemberships {
        id
        source
        addedAt
        dateCohort {
          id
          name
        }
      }
    }
  }
`

// Segments have no dates, so membership is explicit rather than derived from a
// UserDate. Current members and the taxonomy-eligible set come back together so
// the sync confirmation can show the delta before writing. canSync is false when
// the segment has no included taxonomy terms, in which case eligibleUsers is
// empty and the backend refuses to sync — the panel has to say why.
// What saving a segment's edited filters would do, BEFORE it is saved. Takes the
// proposed terms rather than reading the stored ones — once they are persisted
// there is nothing left to confirm.
export const GET_SEGMENT_SYNC_PREVIEW = gql`
  query SegmentSyncPreview(
    $dateCohortId: ID!
    $taxonomyTermIds: [ID!]
    $excludedTaxonomyTermIds: [ID!]
  ) {
    segmentSyncPreview(
      dateCohortId: $dateCohortId
      taxonomyTermIds: $taxonomyTermIds
      excludedTaxonomyTermIds: $excludedTaxonomyTermIds
    ) {
      unfiltered
      added {
        id
        fullName
        email
      }
      removed {
        id
        fullName
        email
      }
      impactedEvents {
        id
        name
        sessionCount
      }
    }
  }
`

// Deliberately does NOT select eligibleUsers (ENGG-9486). Resolving that field
// costs one taxonomy match per employee in the client — the same pass the
// reconcile runs, and 99% of this query's cost. Only the sync confirmation
// dialog needs it, so it is fetched separately when that dialog opens; see
// GET_SEGMENT_ELIGIBLE_USERS. Selecting it here made the post-sync refetch as
// slow as the sync itself, and Apollo holds the previous result while a refetch
// is in flight — so the members list sat reading "no employees" long after the
// progress indicator had gone.
export const GET_SEGMENT_MEMBERSHIP = gql`
  query SegmentMembership($dateCohortId: ID!) {
    segmentMembership(dateCohortId: $dateCohortId) {
      dateCohortId
      canSync
      members {
        source
        addedAt
        user {
          id
          fullName
          email
        }
      }
      # ENGG-9486: a segment is populated by a background reconcile, so a
      # memberCount of 0 can mean "still being matched" rather than "nobody
      # matches". This says which.
      memberSync {
        dateCohortId
        state
        inProgress
        progressPercentage
        phaseLabel
      }
    }
  }
`

// Who the segment's filters match right now — the expensive half of
// segmentMembership, split out so it is only paid when the sync confirmation
// dialog is actually opened (ENGG-9486).
export const GET_SEGMENT_ELIGIBLE_USERS = gql`
  query SegmentEligibleUsers($dateCohortId: ID!) {
    segmentMembership(dateCohortId: $dateCohortId) {
      dateCohortId
      eligibleUsers {
        id
        fullName
        email
      }
    }
  }
`

export const GET_SCORECARD_REVIEWER_TOKENS = gql`
  query ScorecardReviewerTokens {
    scorecardReviewerTokens {
      id
      token
      templateTask {
        id
        title
        internalName
        workflow {
          id
        }
      }
      createdAt
    }
  }
`

export const GET_USER_DEFAULT_VALUES = gql`
  query UserDefaultValues($userId: ID, $attributes: UpdateEmployeeInput!) {
    userDefaultValues(userId: $userId, attributes: $attributes) {
      defaultTaxonomyTerms {
        id
        title
        taxonomyId
        taxonomy {
          id
          name
        }
      }
      defaultTemplateRoleUsers {
        templateRoleId
        templateRole {
          id
          name
        }
        assignee {
          id
          fullName
        }
      }
    }
  }
`

export const PREVIEW_TASK_SHARES = gql`
  query PreviewTaskShares {
    previewTaskShares {
      id
      name
      token
      shareUrl
      createdAt
      filters
    }
  }
`

// ENGG-9567: existing public share links for a template's Reports page.
export const TEMPLATE_REPORT_SHARES = gql`
  query TemplateReportShares {
    templateReportShares {
      id
      token
      shareUrl
      createdAt
      filters
      createdByName
    }
  }
`

// ENGG-10347: the public share page's HTML, rendered for the signed-in admin
// without creating a share link ("View Report").
export const TEMPLATE_REPORT_HTML = gql`
  query TemplateReportHtml($input: CreateTemplateReportShareInput!) {
    templateReportHtml(input: $input)
  }
`

export const GET_PROMPTS = gql`
  query Prompts {
    prompts {
      category
      subcategories {
        subcategory
        prompt
      }
    }
  }
`

export const GET_AI_ASSISTANT_DEFAULT_PROMPTS = gql`
  query AiAssistantDefaultPrompts {
    aiAssistantDefaultPrompts {
      subcategory
      prompt
    }
  }
`

export const CSV_IMPORT_JOBS = gql`
  query CsvImportJobs($targetResource: String!, $targetResourceId: ID) {
    csvImportJobs(
      targetResource: $targetResource
      targetResourceId: $targetResourceId
    ) {
      id
      callerId
      clientId
      status
      rawError
      initiatorType
      targetResource
      targetResourceId
      channelId
      startedAt
      finishedAt
      durationMs
      createdAt
      updatedAt
      rawCsv
      totalRows
      processedRows
      errorRows
      progressPercentage
      caller {
        id
        email
        fullName
      }
    }
  }
`

export const GET_LATEST_VIDEO_ANALYSIS_JOB = gql`
  query LatestVideoAnalysisJob($templateTaskId: ID!) {
    latestVideoAnalysisJob(templateTaskId: $templateTaskId) {
      id
      state
      videoUrl
      prompt
      templateTaskId
      eventId
      result
      errorMessage
      startedAt
      completedAt
      failedAt
      createdAt
      updatedAt
      duration
      clientId
      templateTask {
        id
        title
      }
    }
  }
`

export const GET_BLACKOUT_DATES = gql`
  query BlackoutDates {
    blackoutDates {
      id
      type
      labels
      blackoutDates
    }
  }
`

export const GET_SFTP_CREDENTIALS = gql`
  query SftpCredentials {
    sftpCredentials {
      success
      host
      username
      password
      homeDirectory
      alias
      error
    }
  }
`

export const GET_SFTP_PGP_KEY = gql`
  query SftpPgpKey {
    sftpPgpKey {
      id
      publicKey
      fingerprint
      displayFingerprint
      keyId
      uid
      revokedAt
      createdAt
    }
  }
`

export const GET_CONNECTORS = gql`
  query Connectors($includeDatasets: Boolean) {
    connectors(includeDatasets: $includeDatasets) {
      providerKey
      displayName
      connected
      connectedAt
      credentialFields {
        key
        label
        secret
        help
      }
      nonSecretCredentials
      datasets {
        id
        name
        selected
      }
      datasetsError
    }
  }
`

export const GET_SFTP_FILES = gql`
  query SftpFiles {
    sftpFiles {
      success
      error
      files {
        id
        name
        path
        size
        modifiedAt
        isDirectory
        processingEnabled
        terminateOrRescindMissingEmployees
        updateExistingEmployeesOnly
        headerMappings {
          originalHeader
          mappedHeader
          skipEmptyValues
        }
        compositeMappings {
          targetHeader
          sourceHeaders
          mode
        }
        importFilters {
          column
          filterType
          value
          startDate
          endDate
          operator
        }
        sftpFileSettingId
        latestVersionPulledAt
        latestVersionChecksum
        latestVersionRowCount
        terminateOrRescindMissingEmployees
        versionCount
      }
    }
  }
`

export const GET_SFTP_FILE_VERSIONS = gql`
  query SftpFileVersions($sftpFileSettingId: ID, $limit: Int) {
    sftpFileVersions(sftpFileSettingId: $sftpFileSettingId, limit: $limit) {
      id
      filePath
      checksum
      fileSize
      rowCount
      pulledAt
      createdAt
      sftpFileSettingId
      importRunId
      fileUrl
    }
  }
`

export const GET_SFTP_FILE_HEADERS = gql`
  query SftpFileHeaders($filePath: String!) {
    sftpFileHeaders(filePath: $filePath) {
      success
      error
      headers
    }
  }
`

export const GET_TASKS_GROUPED_BY_DUE_DATE = gql`
  query GetTasksGroupedByDueDate(
    $templateTaskIds: [ID!]
    $fromDate: ISO8601Date
    $toDate: ISO8601Date
    $taskTypes: [String!]
  ) {
    tasksGroupedByDueDate(
      templateTaskIds: $templateTaskIds
      fromDate: $fromDate
      toDate: $toDate
      taskTypes: $taskTypes
    ) {
      dueDate
      templateTaskGroups {
        templateTask {
          id
          internalName
          templateTaskDateSetting {
            days
            relation
            useBusinessDays
            time
            timezone
            dateType {
              id
              name
            }
          }
        }
        workflow {
          id
          internalCategory
          category
        }
        section {
          id
          internalCategory
          category
        }
        tasks {
          id
          title
          dueDate
          taskType
          frequencyType
          status
          validAfterDueDateDays
          assignments {
            id
          }
          lastError {
            error
            level
            createdAt
          }
          lastEvent {
            eventType
            countsAsCompletion
            completedAt
            createdAt
            isProcessing
            scheduledAt
            employeeNotInvited
            employeeRemoved
            meetingOutcomes
          }
        }
      }
    }
  }
`

export const GET_SFTP_FILE_CONTENT = gql`
  query SftpFileContent($filePath: String!) {
    sftpFileContent(filePath: $filePath) {
      success
      error
      content
      filename
      size
      contentType
    }
  }
`

export const GET_SFTP_PROCESSED_PREVIEW = gql`
  query SftpProcessedPreview($filePath: String!, $limit: Int) {
    sftpProcessedPreview(filePath: $filePath, limit: $limit) {
      success
      error
      columns
      rows
      includedCount
      excludedCount
      totalRows
      truncated
    }
  }
`

export const GET_SFTP_FILE_VERSION_CONTENT = gql`
  query SftpFileVersionContent($sftpFileVersionId: ID!) {
    sftpFileVersionContent(sftpFileVersionId: $sftpFileVersionId) {
      success
      error
      content
      filename
      size
      contentType
    }
  }
`

export const GET_AVAILABLE_DATES_FOR_DATE_TYPE = gql`
  query AvailableDatesForDateType($dateTypeId: ID!, $offset: Int, $limit: Int) {
    availableDatesForDateType(
      dateTypeId: $dateTypeId
      offset: $offset
      limit: $limit
    ) {
      dateTypeId
      dateTypeName
      isCohort
      totalCount
      dates {
        date
        dateName
      }
    }
  }
`

export const GET_TASKS_GROUPED_BY_COHORT_DATE = gql`
  query GetTasksGroupedByCohortDate(
    $cohortDateFilters: [CohortDateFilterInput!]!
    $templateTaskIds: [ID!]
    $taskTypes: [String!]
  ) {
    tasksGroupedByCohortDate(
      cohortDateFilters: $cohortDateFilters
      templateTaskIds: $templateTaskIds
      taskTypes: $taskTypes
    ) {
      dateTypeId
      cohortDate
      templateTaskGroups {
        templateTaskId
        templateTask {
          id
          title
          internalName
          templateTaskDateSetting {
            days
            relation
            useBusinessDays
            time
            timezone
            dateType {
              id
              name
            }
          }
        }
        workflow {
          id
          internalCategory
          category
        }
        section {
          id
          internalCategory
          category
        }
        taskType
        assignmentCount
        errorCount
        earliestDueDate
        latestDueDate
        earliestDueAt
      }
    }
  }
`

// The Slack half of a cohort date's automation status: the channels
// provisioned for the selected (date type, date) pairs, with their creation
// outcome, invite errors, welcome message and follow-up timeline.
//
// `followUps` merges sent history with still-scheduled sends, and computing an
// unsent send time runs per-channel date math on the server, so this is
// deliberately scoped to the handful of channels on screen rather than reusing
// the unfiltered `slackGroups` query behind Settings -> Slack.
export const GET_SLACK_GROUPS_BY_COHORT_DATE = gql`
  query GetSlackGroupsByCohortDate(
    $cohortDateFilters: [CohortDateFilterInput!]!
  ) {
    slackGroupsByCohortDate(cohortDateFilters: $cohortDateFilters) {
      id
      channelId
      createdAt
      dateTypeId
      dateTypeValue
      inviteeCount
      facilitatorEmails
      postedMessageAt
      postedMessageHtml
      templateTask {
        id
        title
        internalName
        workflow {
          id
        }
        templateTaskDateSetting {
          time
          timezone
        }
      }
      dateType {
        id
        name
      }
      errors {
        id
        errorType
        rawError
        userFriendlyError
        retryable
        createdAt
      }
      followUps {
        key
        title
        scheduledAt
        sentAt
        sentMessage
        error
        retryable
        isOverridden
        timezone
      }
    }
  }
`

// Per-task detail for a single template task within a cohort date.
// Fetched lazily when an admin opens the "X assignments" modal, so the list
// query above only needs to return counts (not thousands of task rows).
// Filtered by the cohort (dateTypeId + date) to match the list's counts.
export const GET_TASKS_FOR_TEMPLATE_TASK_COHORT_DATE = gql`
  query GetTasksForTemplateTaskCohortDate(
    $templateTaskId: ID!
    $dateTypeId: ID!
    $date: ISO8601Date!
  ) {
    tasksForTemplateTaskCohortDate(
      templateTaskId: $templateTaskId
      dateTypeId: $dateTypeId
      date: $date
    ) {
      id
      title
      dueDate
      taskType
      frequencyType
      status
      validAfterDueDateDays
      locationHierarchyId
      locationHierarchy {
        isTopLevelNode
        owner {
          id
          firstName
          lastName
          fullName
          email
        }
        parent {
          owner {
            id
            firstName
            lastName
            fullName
            email
          }
        }
      }
      assignments {
        id
        user {
          id
          firstName
          lastName
          fullName
          email
        }
      }
      lastError {
        error
        level
        createdAt
      }
      lastEvent {
        eventType
        countsAsCompletion
        score
        totalScore
        completedAt
        createdAt
        isProcessing
        scheduledAt
        employeeNotInvited
        employeeRemoved
        meetingOutcomes
      }
    }
  }
`

// Query to fetch tasks with assignments for a specific template task on a given due date
// Used when clicking on "X assignments" to show user details and task completion status
export const GET_TASKS_BY_TEMPLATE_TASK_AND_DUE_DATE = gql`
  query GetTasksByTemplateTaskAndDueDate(
    $templateTaskId: ID!
    $dueDate: ISO8601Date
  ) {
    tasksByTemplateTaskAndDueDate(
      templateTaskId: $templateTaskId
      dueDate: $dueDate
    ) {
      id
      title
      taskType
      status
      dueDate
      validAfterDueDateDays
      lastEvent {
        eventType
        countsAsCompletion
        completedAt
        createdAt
        isProcessing
        scheduledAt
        employeeNotInvited
        employeeRemoved
        meetingOutcomes
      }
      lastError {
        error
        level
        createdAt
      }
      assignments {
        id
        user {
          id
          firstName
          lastName
          fullName
          email
        }
      }
    }
  }
`

export const GET_USER_CHANGE_LOGS = gql`
  query UserChangeLogs(
    $userId: ID!
    $pagination: PaginationInput
    $search: String
  ) {
    userChangeLogs(userId: $userId, pagination: $pagination, search: $search) {
      data {
        id
        source
        fieldCategory
        fieldName
        oldValue
        newValue
        createdAt
        actor {
          id
          firstName
          lastName
          fullName
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

// ENGG-10149: a task's timeline, worded by the backend. One page at a time;
// the modal appends pages as the admin loads more.
export const GET_TASK_CHANGE_LOGS = gql`
  query TaskChangeLogs($taskId: ID!, $pagination: PaginationInput) {
    taskChangeLogs(taskId: $taskId, pagination: $pagination) {
      historyStartsAt
      data {
        id
        kind
        event
        occurredAt
        actor {
          id
          fullName
          email
        }
        processLabel
        summary
        detail
        cause {
          text
          link {
            kind
            id
            label
          }
        }
        changes {
          field
          label
          beforeText
          afterText
        }
        outcome {
          channel
          facts {
            label
            value
          }
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_CALENDAR_ATTENDEE_REMOVALS = gql`
  query CalendarAttendeeRemovals($userId: ID!, $pagination: PaginationInput) {
    calendarAttendeeRemovals(userId: $userId, pagination: $pagination) {
      data {
        id
        eventTitle
        eventStartAt
        removedEmail
        reason
        terminationDate
        removedAt
        removedSource
        restoredAt
        restoredSource
        status
        removedBy {
          id
          fullName
        }
        restoredBy {
          id
          fullName
        }
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_UNIQUE_USER_DATES_FOR_DATE_TYPE = gql`
  query UniqueUserDatesForDateType($dateTypeId: ID!) {
    uniqueUserDatesForDateType(dateTypeId: $dateTypeId) {
      dateType {
        id
        name
        slug
      }
      options {
        label
        value
      }
    }
  }
`

export const GET_REPORT_AI_PROMPTS = gql`
  query ReportAiPrompts {
    reportAiPrompts {
      id
      promptType
      content
    }
  }
`

export const GET_SUPER_ADMIN_REPORT_NOTIFICATIONS = gql`
  query SuperAdminReportNotifications {
    superAdminReportNotifications {
      superAdmins {
        id
        fullName
        email
      }
      reports {
        id
        title
        description
        frequency
        disabledUserIds
        fixedRecipients
      }
    }
  }
`

export const GET_IP_ALLOWLIST = gql`
  query IpAllowlist {
    ipAllowlist
  }
`

export const GET_AI_ASSISTANT_SESSIONS = gql`
  query AiAssistantSessions($status: String) {
    aiAssistantSessions(status: $status) {
      id
      title
      status
      model
      hasActionTarget
      actionTargetId
      actionTargetType
      actionTargetName
      actionTargetDeleted
      isEmpty
      createdAt
      updatedAt
    }
  }
`

export const GET_AI_ASSISTANT_SESSION = gql`
  query AiAssistantSession($id: ID!) {
    aiAssistantSession(id: $id) {
      id
      title
      status
      model
      isProcessing
      brainEnabled
      createdAt
      updatedAt
      messages {
        id
        role
        content
        toolCalls
        toolCallId
        toolName
        metadata
        createdAt
      }
    }
  }
`

export const CHECK_EMPLOYEE_ELIGIBILITY = gql`
  query CheckEmployeeEligibility($templateTaskId: ID!, $email: String!) {
    checkEmployeeEligibility(templateTaskId: $templateTaskId, email: $email) {
      userFound
      userName
      userEmail
      templateName
      hasTemplate
      templateEligible
      templateReasons
      autoImportRules {
        ruleName
        eligible
        reasons
      }
      taskName
      hasTask
      taskEligible
      taskReasons
      userTaxonomyTerms {
        taxonomyName
        terms
        excluded
      }
      taskTaxonomyTerms {
        taxonomyName
        terms
        excluded
      }
    }
  }
`

export const CHECK_AUTO_IMPORT_RULE_ELIGIBILITY = gql`
  query CheckAutoImportRuleEligibility(
    $autoImportRuleId: ID!
    $email: String!
  ) {
    checkAutoImportRuleEligibility(
      autoImportRuleId: $autoImportRuleId
      email: $email
    ) {
      userFound
      userName
      userEmail
      ruleName
      eligible
      reasons
      templateName
      hasTemplate
      dateWindowFrom
      dateWindowTo
      dateTypeName
      userDateValue
      dateEligible
      taxonomyEligible
      taxonomyReasons
      userTaxonomyTerms {
        taxonomyName
        terms
        excluded
      }
      ruleTaxonomyTerms {
        taxonomyName
        terms
        excluded
      }
    }
  }
`

export const GET_AUTO_IMPORT_ASSIGNMENT_PREVIEW = gql`
  query GetAutoImportAssignmentPreview(
    $autoImportRuleId: ID!
    $query: String
    $pagination: PaginationInput
  ) {
    autoImportAssignmentPreview(
      autoImportRuleId: $autoImportRuleId
      query: $query
      pagination: $pagination
    ) {
      dateTypeName
      data {
        userId
        userName
        userEmail
        anchorDate
        projectedDate
        status
        eligible
        employeeState
        reasons
      }
      paginationMeta {
        ...PaginationMetaFields
      }
    }
  }
  ${FRAGMENT_PAGINATION_META_FIELDS}
`

export const GET_ACTION_TARGET = gql`
  query GetActionTarget($sessionId: ID!) {
    aiAssistantActionTarget(sessionId: $sessionId) {
      id
      targetType
      targetId
      status
      pendingChanges
      originalSnapshot
      createdAt
      updatedAt
    }
  }
`

export const GET_COURSE_SKILLS = gql`
  query CourseSkills {
    courseSkills {
      id
      name
    }
  }
`

export const GET_COURSE_CATEGORIES = gql`
  query CourseCategories {
    courseCategories {
      id
      name
    }
  }
`

export const GET_COURSE_FORMATS = gql`
  query CourseFormats {
    courseFormats {
      id
      name
    }
  }
`

export const GET_COURSE_LEVELS = gql`
  query CourseLevels {
    courseLevels {
      id
      name
    }
  }
`

export const GET_COURSE_CONTENT_TYPES = gql`
  query CourseContentTypes {
    courseContentTypes {
      id
      name
    }
  }
`

// =====================================================================
// Roleplay Prompt Optimizer
// =====================================================================

export const GET_TEMPLATE_TASK_OPTIMIZATIONS = gql`
  query TemplateTaskOptimizations($id: ID!) {
    templateTask(id: $id) {
      id
      title
      optimizedInstructions
      optimizedInstructionsUpdatedAt
      optimizedInstructionsStale
      aiRoleplayOptimizations {
        id
        status
        kind
        focusNote
        summaryOfChanges
        finalScores
        appliedAt
        discardedAt
        createdAt
        promptTokens
        completionTokens
        totalTokens
        createdByUser {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export const EMAIL_MESSAGE_DETAIL = gql`
  query EmailMessageDetail($id: ID!) {
    emailMessage(id: $id) {
      id
      sgMessageId
      subject
      fromEmail
      fromName
      replyTo
      toEmails
      ccEmails
      sentAt
      deliveryMethod
      templateId
      templateVersionId
      templateData
      bodyHtml
      eventsSummary
      emailEvents {
        id
        eventType
        timestamp
        recipient
      }
    }
  }
`

export const COURSE_REPORT_STATS = gql`
  query CourseReportStats(
    $templateCategoryId: ID!
    $userIds: [ID!]
    $cohortDates: [CohortDateFilterInput!]
    $enrollmentDateFrom: ISO8601Date
    $enrollmentDateTo: ISO8601Date
    $includeTerminated: Boolean
    $includeAutomationTasks: Boolean
  ) {
    courseReportStats(
      templateCategoryId: $templateCategoryId
      userIds: $userIds
      cohortDates: $cohortDates
      enrollmentDateFrom: $enrollmentDateFrom
      enrollmentDateTo: $enrollmentDateTo
      includeTerminated: $includeTerminated
      includeAutomationTasks: $includeAutomationTasks
    ) {
      activeLearners
      activeLearnersTrend
      avgCompletionRate
      avgCompletionRateTrend
      avgCompletionTimeHours
      excludedTemplateTaskIds
      learnersByStatus {
        label
        count
        percentage
      }
      moduleCompletion {
        templateTaskId
        name
        percentage
        templateTaskPosition
        excludedFromReports
        section {
          id
          name
          rootCategoryId
          position
        }
      }
    }
  }
`

export const GET_TEMPLATE_TASK_OPTIMIZATION = gql`
  query TemplateTaskOptimization($id: ID!) {
    templateTask(id: $id) {
      id
      optimizedInstructions
      optimizedInstructionsUpdatedAt
      optimizedInstructionsStale
      aiRoleplayOptimizations {
        id
        templateTaskId
        status
        kind
        focusNote
        originalInstructions
        proposedInstructions
        summaryOfChanges
        finalScores
        iterations
        traineeScripts
        modelVersions
        promptTokens
        completionTokens
        totalTokens
        errorMessage
        appliedAt
        discardedAt
        createdAt
        updatedAt
        currentStage
        currentIteration
        stageDetail
        stageUpdatedAt
        sources {
          id
          sourceableType
          sourceableId
          label
        }
      }
    }
  }
`

export const EMAIL_MESSAGES = gql`
  query EmailMessages(
    $userId: ID
    $recipient: String
    $templateId: String
    $emailType: String
    $sentAfter: ISO8601DateTime
    $sentBefore: ISO8601DateTime
    $limit: Int
    $offset: Int
  ) {
    emailMessages(
      userId: $userId
      recipient: $recipient
      templateId: $templateId
      emailType: $emailType
      sentAfter: $sentAfter
      sentBefore: $sentBefore
      limit: $limit
      offset: $offset
    ) {
      totalCount
      nodes {
        id
        sgMessageId
        subject
        fromEmail
        toEmails
        sentAt
        deliveryMethod
        templateId
        eventsSummary
      }
    }
  }
`

export const COURSE_REPORT_USER_STATS = gql`
  query CourseReportUserStats(
    $templateCategoryId: ID!
    $search: String
    $userIds: [ID!]
    $cohortDates: [CohortDateFilterInput!]
    $enrollmentDateFrom: ISO8601Date
    $enrollmentDateTo: ISO8601Date
    $includeTerminated: Boolean
    $includeAutomationTasks: Boolean
  ) {
    courseReportUserStats(
      templateCategoryId: $templateCategoryId
      search: $search
      userIds: $userIds
      cohortDates: $cohortDates
      enrollmentDateFrom: $enrollmentDateFrom
      enrollmentDateTo: $enrollmentDateTo
      includeTerminated: $includeTerminated
      includeAutomationTasks: $includeAutomationTasks
    ) {
      userStats {
        id
        userId
        employeeName
        workEmail
        userDates {
          dateTypeId
          date
        }
        enrollmentStatus
        enrollmentDate
        progress
        completionDate
        status
        completed
        cantComplete
        inProgress
        notStarted
        overdue
        absent
        other
        score
      }
      totalCount
    }
  }
`

export const COURSE_TASK_USER_BREAKDOWN = gql`
  query CourseTaskUserBreakdown($templateTaskId: ID!) {
    courseTaskUserBreakdown(templateTaskId: $templateTaskId) {
      templateTaskId
      taskName
      taskType
      buckets {
        bucket
        totalCount
        users {
          id
          userId
          userName
          workEmail
          rawStatus
          completedAt
          scheduledFor
          dueDate
          errorMessage
          otherReason
          cantCompleteReason
        }
      }
    }
  }
`

export const COURSE_USER_TASK_BREAKDOWN = gql`
  query CourseUserTaskBreakdown($templateCategoryId: ID!, $userId: ID!) {
    courseUserTaskBreakdown(
      templateCategoryId: $templateCategoryId
      userId: $userId
    ) {
      userId
      userName
      workEmail
      sections {
        id
        name
        position
        totalCount
        completedCount
        tasks {
          id
          templateTaskId
          name
          taskType
          position
          bucket
          rawStatus
          completedAt
          scheduledFor
          dueDate
          errorMessage
          otherReason
          cantCompleteReason
        }
      }
    }
  }
`

export const COURSE_TASK_STATS = gql`
  query CourseTaskStats(
    $templateCategoryId: ID!
    $search: String
    $userIds: [ID!]
    $cohortDates: [CohortDateFilterInput!]
    $enrollmentDateFrom: ISO8601Date
    $enrollmentDateTo: ISO8601Date
    $includeTerminated: Boolean
    $includeAutomationTasks: Boolean
  ) {
    courseTaskStats(
      templateCategoryId: $templateCategoryId
      search: $search
      userIds: $userIds
      cohortDates: $cohortDates
      enrollmentDateFrom: $enrollmentDateFrom
      enrollmentDateTo: $enrollmentDateTo
      includeTerminated: $includeTerminated
      includeAutomationTasks: $includeAutomationTasks
    ) {
      taskStats {
        id
        templateTaskId
        title
        status
        format
        enrollments
        completed
        cantComplete
        inProgress
        notStarted
        overdue
        absent
        otherTotal
        otherError
        otherNotApplicable
        completionRate
        avgCompletionTimeHours
        excludedFromReports
      }
      totalCount
    }
  }
`

export const TEMPLATES_REPORT_STATS = gql`
  query TemplatesReportStats(
    $templateCategoryIds: [ID!]!
    $userIds: [ID!]
    $cohortDates: [CohortDateFilterInput!]
    $includeTerminated: Boolean
    $includeAutomationTasks: Boolean
  ) {
    templatesReportStats(
      templateCategoryIds: $templateCategoryIds
      userIds: $userIds
      cohortDates: $cohortDates
      includeTerminated: $includeTerminated
      includeAutomationTasks: $includeAutomationTasks
    ) {
      activeLearners
      enrollments
      avgCompletionRate
      avgCompletionTimeHours
      learnersByStatus {
        label
        count
        percentage
      }
      templateCompletion {
        templateCategoryId
        templateName
        enrolledUsers
        percentage
      }
    }
  }
`

export const TEMPLATES_REPORT_USER_STATS = gql`
  query TemplatesReportUserStats(
    $templateCategoryIds: [ID!]!
    $search: String
    $userIds: [ID!]
    $cohortDates: [CohortDateFilterInput!]
    $includeTerminated: Boolean
    $includeAutomationTasks: Boolean
  ) {
    templatesReportUserStats(
      templateCategoryIds: $templateCategoryIds
      search: $search
      userIds: $userIds
      cohortDates: $cohortDates
      includeTerminated: $includeTerminated
      includeAutomationTasks: $includeAutomationTasks
    ) {
      userStats {
        id
        userId
        templateCategoryId
        templateName
        employeeName
        workEmail
        userDates {
          dateTypeId
          date
        }
        enrollmentStatus
        enrollmentDate
        progress
        completionDate
        status
        completed
        cantComplete
        inProgress
        notStarted
        overdue
        other
      }
      totalCount
    }
  }
`

export const TEMPLATES_TASK_STATS = gql`
  query TemplatesTaskStats(
    $templateCategoryIds: [ID!]!
    $search: String
    $userIds: [ID!]
    $cohortDates: [CohortDateFilterInput!]
    $includeTerminated: Boolean
    $includeAutomationTasks: Boolean
  ) {
    templatesTaskStats(
      templateCategoryIds: $templateCategoryIds
      search: $search
      userIds: $userIds
      cohortDates: $cohortDates
      includeTerminated: $includeTerminated
      includeAutomationTasks: $includeAutomationTasks
    ) {
      taskStats {
        id
        templateTaskId
        templateCategoryId
        templateName
        sectionId
        sectionName
        title
        status
        format
        enrollments
        completed
        cantComplete
        inProgress
        notStarted
        overdue
        otherTotal
        otherError
        otherNotApplicable
        completionRate
        avgCompletionTimeHours
        excludedFromReports
      }
      totalCount
    }
  }
`

// ENGG-10340: Cohort filter choices for the Reports tabs — the template
// Analytics cohorts + Start Date dates, merged across the selected templates.
export const REPORT_COHORT_FILTER_OPTIONS = gql`
  query ReportCohortFilterOptions($templateCategoryIds: [ID!]!) {
    reportCohortFilterOptions(templateCategoryIds: $templateCategoryIds) {
      cohorts {
        id
        name
        dateTypeId
        targetDateType {
          id
          name
        }
        dateCohortDates {
          id
          name
          date
          fromDate
          toDate
        }
      }
      dateTypes {
        id
        name
        dates
      }
    }
  }
`

// ENGG-9723: per-template Analytics → Overview tab. One query per card, all
// taking the same filter-bar input so the cards describe the same learners.
const TEMPLATE_ANALYTICS_EXTREME = `
  value
  userId
  userName
`

export const TEMPLATE_ANALYTICS_FILTER_OPTIONS = gql`
  query TemplateAnalyticsFilterOptions($templateCategoryId: ID!) {
    templateAnalyticsFilterOptions(templateCategoryId: $templateCategoryId) {
      taskTypes
      managers {
        id
        name
      }
      cohorts {
        id
        name
        dateTypeId
        targetDateType {
          id
          name
        }
        dateCohortDates {
          id
          name
          date
          fromDate
          toDate
        }
      }
      dateTypes {
        id
        name
        dates
      }
    }
  }
`

export const TEMPLATE_ANALYTICS_SUMMARY = gql`
  query TemplateAnalyticsSummary($filter: TemplateAnalyticsFilterInput!) {
    templateAnalyticsSummary(filter: $filter) {
      totalLearners
      activeLearners
      expiredLearners
      overdueCount
      overduePercent
      overdueHighest { ${TEMPLATE_ANALYTICS_EXTREME} }
      overdueLowest { ${TEMPLATE_ANALYTICS_EXTREME} }
      completedCount
      completionPercent
      completionHighest { ${TEMPLATE_ANALYTICS_EXTREME} }
      completionLowest { ${TEMPLATE_ANALYTICS_EXTREME} }
      attendancePercent
    }
  }
`

export const TEMPLATE_ANALYTICS_SCORES = gql`
  query TemplateAnalyticsScores($filter: TemplateAnalyticsFilterInput!) {
    templateAnalyticsScores(filter: $filter) {
      taskType
      assignedCount
      completedCount
      completedHighest { ${TEMPLATE_ANALYTICS_EXTREME} }
      completedLowest { ${TEMPLATE_ANALYTICS_EXTREME} }
      scorePercent
      scoreHighest { ${TEMPLATE_ANALYTICS_EXTREME} }
      scoreLowest { ${TEMPLATE_ANALYTICS_EXTREME} }
    }
  }
`

const TEMPLATE_ANALYTICS_TEAM_CHART = `
  allTeams
  teams {
    managerId
    managerName
    learnerCount
    value
  }
`

export const TEMPLATE_ANALYTICS_TEAM_PROGRESS = gql`
  query TemplateAnalyticsTeamProgress($filter: TemplateAnalyticsFilterInput!) {
    templateAnalyticsTeamProgress(filter: $filter) { ${TEMPLATE_ANALYTICS_TEAM_CHART} }
  }
`

export const TEMPLATE_ANALYTICS_TEAM_SCORES = gql`
  query TemplateAnalyticsTeamScores($filter: TemplateAnalyticsFilterInput!, $taskType: String) {
    templateAnalyticsTeamScores(filter: $filter, taskType: $taskType) { ${TEMPLATE_ANALYTICS_TEAM_CHART} }
  }
`

export const TEMPLATE_ANALYTICS_COHORT_PROGRESS = gql`
  query TemplateAnalyticsCohortProgress(
    $filter: TemplateAnalyticsFilterInput!
    $dateCohortId: ID
    $groupBy: String
  ) {
    templateAnalyticsCohortProgress(
      filter: $filter
      dateCohortId: $dateCohortId
      groupBy: $groupBy
    ) {
      weekCount
      rows {
        key
        label
        cohortName
        startDate
        learnerCount
        weeks {
          week
          fromDate
          toDate
          total
          completionPercent
          completed
          inProgress
          overdue
          notStarted
          cantComplete
        }
      }
    }
  }
`

export const TEMPLATE_ANALYTICS_EXPORT = gql`
  query TemplateAnalyticsExport($filter: TemplateAnalyticsFilterInput!) {
    templateAnalyticsExport(filter: $filter) {
      filename
      csv
    }
  }
`

export const TEMPLATE_ANALYTICS_TASKS = gql`
  query TemplateAnalyticsTasks(
    $filter: TemplateAnalyticsFilterInput!
    $includeAutomation: Boolean
  ) {
    templateAnalyticsTasks(
      filter: $filter
      includeAutomation: $includeAutomation
    ) {
      tasks {
        templateTaskId
        title
        sectionName
        taskType
        status
        assigned
        completed
        inProgress
        notStarted
        overdue
        cantComplete
        other
        completionPercent
      }
      byType {
        taskType
        assigned
        completed
        completionPercent
      }
    }
  }
`

export const TEMPLATE_ANALYTICS_EMPLOYEES = gql`
  query TemplateAnalyticsEmployees($filter: TemplateAnalyticsFilterInput!) {
    templateAnalyticsEmployees(filter: $filter) {
      hasQuiz
      hasRoleplay
      hasScorecard
      badgesTotal
      employees {
        userId
        name
        email
        assigned
        completed
        progressPercent
        overdue
        overallScore
        badgesEarned
        quiz {
          assigned
          completed
          scorePercent
        }
        roleplay {
          assigned
          completed
          scorePercent
        }
        scorecard {
          assigned
          completed
          scorePercent
        }
      }
    }
  }
`

export const TEMPLATE_ANALYTICS_EMPLOYEE_SCORES = gql`
  query TemplateAnalyticsEmployeeScores(
    $filter: TemplateAnalyticsFilterInput!
    $userId: ID!
  ) {
    templateAnalyticsEmployeeScores(filter: $filter, userId: $userId) {
      templateTaskId
      title
      taskType
      scorePercent
    }
  }
`

export const GET_AI_ROLEPLAY_CHAT_SESSIONS = gql`
  query AiRoleplayChatSessions($aiRoleplayTypeId: ID!, $limit: Int) {
    aiRoleplayChatSessions(aiRoleplayTypeId: $aiRoleplayTypeId, limit: $limit) {
      id
      userId
      createdAt
      transcription
      rating
    }
  }
`

export const COURSE_AVG_COMPLETION_TIMES = gql`
  query CourseAvgCompletionTimes($templateCategoryIds: [ID!]!) {
    courseAvgCompletionTimes(templateCategoryIds: $templateCategoryIds) {
      templateCategoryId
      avgCompletionTimeHours
    }
  }
`

export const BADGES_QUERY = gql`
  query Badges {
    badges {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const BADGE_QUERY = gql`
  query Badge($id: ID!) {
    badge(id: $id) {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const CERTIFICATES_QUERY = gql`
  query Certificates {
    certificates {
      ...CertificateFields
      badge {
        ...BadgeFields
      }
    }
  }
  ${FRAGMENT_CERTIFICATE_FIELDS}
  ${FRAGMENT_BADGE_FIELDS}
`

export const CERTIFICATE_QUERY = gql`
  query Certificate($id: ID!) {
    certificate(id: $id) {
      ...CertificateFields
      awardingRuleNames
      badge {
        ...BadgeFields
      }
    }
  }
  ${FRAGMENT_CERTIFICATE_FIELDS}
  ${FRAGMENT_BADGE_FIELDS}
`

export const ADMIN_CLEANUP_DRY_RUN = gql`
  query AdminCleanupDryRun($email: String!, $replacementEmail: String) {
    adminCleanupDryRun(email: $email, replacementEmail: $replacementEmail) {
      findings {
        key
        count
      }
      counters {
        key
        count
      }
      warnings
      dryRun
      actorEmail
    }
  }
`

// Badges Analytics — pivoted users × badged-course grid (ENGG-8701).
// Columns query returns one entry per badged template_category for the
// current client; rows query returns paginated users with one cell per
// requested column, bucketed via CourseReportBuckets on the backend.
export const BADGES_ANALYTICS_COLUMNS = gql`
  query BadgesAnalyticsColumns {
    badgesAnalyticsColumns {
      id
      templateCategoryId
      courseName
      internalName
      templateTaskId
      taskName
      templateTaskWorkflowId
      certificateId
      certificateName
      badge {
        ...BadgeFields
      }
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const BADGES_ANALYTICS_ROWS = gql`
  query BadgesAnalyticsRows(
    $search: String
    $userIds: [ID!]
    $filters: BadgesAnalyticsFiltersInput
    $columnIds: [ID!]
    $pagination: PaginationInput
  ) {
    badgesAnalyticsRows(
      search: $search
      userIds: $userIds
      filters: $filters
      columnIds: $columnIds
      pagination: $pagination
    ) {
      data {
        id
        userId
        employeeName
        workEmail
        cells {
          columnId
          bucket
          completionPercent
          totalTasks
          completedTasks
          lastEventAt
          certIssuedAt
          badgeIssuedAt
          earnedCount
          earnedOutsideWindowCount
          inProgressCount
          overdueCount
          cantCompleteCount
          notStartedCount
          totalCount
        }
      }
      paginationMeta {
        page
        perPage
        totalPages
        totalCount
        nextPage
        prevPage
      }
    }
  }
`

// ENGG-9010 — the Conditions tab lists conditions, so it reads them directly
// rather than deriving them from the credential catalog. A condition is the
// unit here: it has a name, it grants several credentials, and it owns the rule.
export const AWARD_CONDITIONS = gql`
  query AwardConditions {
    awardConditions {
      ...AwardConditionFields
    }
  }
  ${FRAGMENT_AWARD_CONDITION_FIELDS}
`

// ENGG-9010 — the condition catalogue, general plus per task type. Fetched
// rather than hardcoded so the options the admin offers cannot drift from
// what CredentialConditions validates and the evaluator enforces.
export const AWARD_CONDITION_KINDS = gql`
  query CredentialConditionKinds {
    awardConditionKinds {
      key
      label
      description
      requiresThreshold
      unit
      general
      taskTypes
    }
  }
`

// The task types a "any N tasks of this type" rule may count. Fetched rather
// than duplicated here: the model rejects anything outside this list, and an
// admin offered a type the server refuses finds out only on save.
export const AWARD_QUOTA_TASK_TYPES = gql`
  query AwardQuotaTaskTypes {
    awardQuotaTaskTypes
  }
`

// ENGG-9010 — "View employees" on a badge or certificate card. Newest award
// first, capped server-side; `total` counts people.
export const CREDENTIAL_HOLDERS = gql`
  query CredentialHolders($badgeId: ID, $certificateId: ID, $search: String) {
    credentialHolders(
      badgeId: $badgeId
      certificateId: $certificateId
      search: $search
    ) {
      total
      holders {
        userId
        name
        email
        source
        earnedAt
      }
    }
  }
`

// ENGG-10101 — "Find missing earners": who met a rule (or any rule giving a
// badge or certificate) but does not have the award. Every row, uncapped —
// "Assign" needs all the ids.
export const MISSING_AWARD_EARNERS = gql`
  query MissingAwardEarners($ruleId: ID, $certificateId: ID, $badgeId: ID) {
    missingAwardEarners(
      ruleId: $ruleId
      certificateId: $certificateId
      badgeId: $badgeId
    ) {
      total
      earners {
        userId
        name
        email
        earnedAt
        ruleId
        ruleName
      }
    }
  }
`

// ENGG-9010 — Badges & Certificates table. One entry per badge and per
// certificate, each carrying its unlock requirements normalized across the
// two origins the backend merges: legacy anchors (a badge/cert attached to a
// template, section or task) and credential_requirements rule rows.
export const CREDENTIAL_CATALOG = gql`
  query CredentialCatalog {
    credentialCatalog {
      id
      kind
      badgeId
      certificateId
      name
      enabled
      earnedEmployeeCount
      hasBadge
      hasCertificate
      badge {
        ...BadgeFields
      }
      certificate {
        ...CertificateArtworkFields
      }
      templates {
        id
        name
      }
      requirements {
        id
        origin
        active
        level
        label
        conditionKind
        threshold
        taskType
        requiredCount
        awardConditionId
        awardConditionName
        templateCategoryId
        templateTaskId
        templateId
        templateName
      }
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

// ENGG-8989 — per-source drill-down for a single (user, badge) cell.
// The outer rows query returns only the count strip; this is called
// when the admin opens the modal so per-row payloads stay lean.
export const BADGES_ANALYTICS_CELL_SOURCES = gql`
  query BadgesAnalyticsCellSources(
    $userId: ID!
    $badgeId: ID!
    $filters: BadgesAnalyticsFiltersInput
  ) {
    badgesAnalyticsCellSources(
      userId: $userId
      badgeId: $badgeId
      filters: $filters
    ) {
      level
      route
      label
      status
      assignmentType
      progressPercent
      earnedAt
      assignedAt
      inWindow
      templateCategoryId
      templateTaskId
      templateTaskWorkflowId
      certificateId
    }
  }
`

// Manual badge assignments for a single employee — drives the Badges tab on
// the employee slide drawer (ENGG-8858). Kept separate from the heavy
// GET_EMPLOYEE_INFORMATION query so opening the drawer doesn't refetch the
// list every time and so refetch-after-mutate stays cheap.
export const GET_USER_ASSIGNED_BADGES = gql`
  query GetUserAssignedBadges($userId: ID!) {
    user(userId: $userId) {
      id
      assignedBadges {
        ...AssignedBadgeFields
      }
      userBadges {
        ...UserBadgeFields
      }
    }
  }
  ${FRAGMENT_ASSIGNED_BADGE_FIELDS}
  ${FRAGMENT_USER_BADGE_FIELDS}
`

// Content Hub (ENGG-9032) — admin-side wrappers around ContentBrowserService.
// adminContentSources drives the left rail's "External Connections" section
// (which sources are enabled + their root folder counts). adminContentBrowse
// returns folders + items at a given level; omit parentFolderId for the
// source root.
export const GET_ADMIN_CONTENT_SOURCES = gql`
  query AdminContentSources($filter: AdminContentBrowseFilterInput) {
    adminContentSources(filter: $filter) {
      source
      label
      rootCount
      matchingItemCount
      connected
    }
  }
`

export const GET_ADMIN_CONTENT_OWNERS = gql`
  query AdminContentOwners {
    adminContentOwners
  }
`

export const GET_ADMIN_CONTENT_FOLDERS_WITH_COUNTS = gql`
  query AdminContentFoldersWithCounts(
    $source: String!
    $filter: AdminContentBrowseFilterInput
  ) {
    adminContentFoldersWithCounts(source: $source, filter: $filter) {
      totalCount
      unassignedCount
      folders {
        ...ContentHubFolderNodeFields
        subfolders {
          ...ContentHubFolderNodeFields
          subfolders {
            ...ContentHubFolderNodeFields
            subfolders {
              ...ContentHubFolderNodeFields
              subfolders {
                ...ContentHubFolderNodeFields
                subfolders {
                  ...ContentHubFolderNodeFields
                }
              }
            }
          }
        }
      }
    }
  }

  fragment ContentHubFolderNodeFields on ContentFolderWithCount {
    id
    name
    parentFolderId
    itemCount
    repAccess
    repAccessOverride
    repAccessOverridesInside
    taxonomyTerms {
      id
      title
      key
      taxonomyId
      taxonomy {
        id
        name
      }
    }
    excludedTaxonomyTerms {
      id
      title
      key
      taxonomyId
      taxonomy {
        id
        name
      }
    }
  }
`

// ENGG-10071 — the customers a Content Hub document can be written for (the
// drawer's "Written for" picker). Same list the DSR builder offers.
export const GET_CONTENT_HUB_SCOPE_CUSTOMERS = gql`
  query ContentHubScopeCustomers {
    dsrCustomers {
      id
      name
      domain
    }
  }
`

export const GET_ADMIN_CONTENT_ASSET = gql`
  query AdminContentAsset($id: ID!, $source: String!) {
    adminContentAsset(id: $id, source: $source) {
      id
      title
      url
      thumbnailUrl
      fileType
      source
      lastModified
      ownerEmail
      fileSize
      summary
      tags
      visibility
      repAccess
      taxonomyTerms {
        id
        title
        key
        taxonomyId
        taxonomy {
          id
          name
        }
      }
      excludedTaxonomyTerms {
        id
        title
        key
        taxonomyId
        taxonomy {
          id
          name
        }
      }
      availableToAi
      inBrain
      inSidekick
      vectorizationState
      lastVectorizedAt
      customerId
      customerName
      customerRemoved
      createdAt
      viewCount
      downloadCount
      lastViewedAt
      roomsUsing
      shareLinksCreated
      shareLinkClicks
      roomOpens
      roomDownloads
      pageCount
      pageImages {
        pageNumber
        url
      }
      shareLinks {
        id
        slug
        shareUrl
        recipientName
        recipientEmail
        recipientCompany
        note
        allowDownload
        accessLevel
        clickCount
        lastClickedAt
        expiresAt
        createdAt
        visits {
          id
          visitorName
          visitorEmail
          visitorCompany
          maxPageViewed
          activeSeconds
          createdAt
          lastSeenAt
          downloadedAt
        }
      }
    }
  }
`

export const GET_ADMIN_CONTENT_BROWSE = gql`
  query AdminContentBrowse(
    $source: String!
    $parentFolderId: ID
    $filter: AdminContentBrowseFilterInput
    $recursive: Boolean
    $pagination: PaginationInput
  ) {
    adminContentBrowse(
      source: $source
      parentFolderId: $parentFolderId
      filter: $filter
      recursive: $recursive
      pagination: $pagination
    ) {
      folders {
        id
        name
        source
      }
      items {
        id
        title
        url
        thumbnailUrl
        fileType
        source
        lastModified
        createdAt
        ownerEmail
        summary
        tags
        visibility
        repAccess
        availableToAi
        inBrain
        inSidekick
        vectorizationState
        lastVectorizedAt
        customerId
        customerName
        customerRemoved
      }
      breadcrumb {
        id
        name
      }
      parentFolder {
        id
        name
      }
      paginationMeta {
        page
        perPage
        totalPages
        totalCount
        nextPage
        prevPage
      }
    }
  }
`

// Catalog for the manual-assign dropdown:
//   • assignableCertificates — certs the admin can award now
//   • assignableBadges       — badges the admin can standalone-award
//                              (ENGG-8989). When userId is supplied the
//                              backend filters out badges the user
//                              already holds via UserCertification or
//                              UserBadge, so the picker never offers a
//                              badge that would be silently double-earned.
//   • unassignableBadges     — badges with no enabled certificate, shown
//                              disabled in the dropdown so admins can see
//                              them and understand why they cannot be
//                              assigned (ENGG-8858).
export const GET_BADGE_ASSIGNMENT_OPTIONS = gql`
  query GetBadgeAssignmentOptions($userId: ID) {
    # ENGG-10188 — the artwork fields too: the pickers draw each certificate
    # (CertificateArtwork), not the badge it carries.
    assignableCertificates {
      ...CertificateArtworkFields
      badge {
        ...BadgeFields
      }
    }
    assignableBadges(userId: $userId) {
      ...BadgeFields
    }
    unassignableBadges {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

export const CMS_ASSISTANT_INSTRUCTIONS = gql`
  query CmsAssistantInstructions {
    cmsAssistantInstructions {
      instructions
      defaultInstructions
    }
  }
`

// ENGG-9890 — empty means reps get the employee app's built-in default note.
export const CMS_SHARE_EMAIL_TEMPLATE = gql`
  query CmsShareEmailTemplate {
    cmsShareEmailTemplate
  }
`

export const CURATED_ANSWERS = gql`
  query CuratedAnswers($status: String, $search: String) {
    curatedAnswers(status: $status, search: $search) {
      id
      question
      answer
      kind
      status
      verbatim
      source
      sourceEventId
      createdByName
      indexedAt
      searchable
      updatedAt
    }
  }
`

export const MOST_ASKED_QUESTIONS = gql`
  query MostAskedQuestions(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $limit: Int
  ) {
    mostAskedQuestions(from: $from, to: $to, limit: $limit) {
      label
      totalAsks
      negativeFeedback
      zeroResult
      curatedAnswerId
      variants {
        question
        asks
      }
      askers {
        name
        email
        asks
        lastAskedAt
      }
    }
  }
`

// ENGG-9611 — the "Recent questions" review queue: what reps asked AI Search
// recently, grouped by meaning, each with the assistant's latest answer, the
// documents it cited, rep thumbs, and approved-answer coverage.
export const RECENT_QUESTIONS = gql`
  query RecentQuestions(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $limit: Int
  ) {
    recentQuestions(from: $from, to: $to, limit: $limit) {
      id
      question
      totalAsks
      answer
      covered
      curatedAnswerId
      upCount
      downCount
      lastAskedBy
      lastAskedAt
      sources {
        id
        title
      }
      feedback {
        rep
        positive
        at
      }
      variants {
        question
        asks
      }
      askers {
        name
        email
        asks
        lastAskedAt
      }
    }
  }
`

// ENGG-9611 — this client's Recent-questions email digest config (one per
// client; the resolver returns an unsaved default when none exists).
export const RECENT_QUESTIONS_ALERT = gql`
  query RecentQuestionsAlert {
    recentQuestionsAlert {
      id
      enabled
      intervalValue
      intervalUnit
      includeUncovered
      includeNegative
      newSinceLast
      lastNotifiedAt
      recipients {
        type
        value
        label
      }
    }
  }
`

export const CURATED_ANSWER_RUN = gql`
  query CuratedAnswerRun($id: ID) {
    curatedAnswerRun(id: $id) {
      id
      sourceType
      sourceId
      sourceTitle
      requestedLimit
      state
      error
      candidateCount
      createdAt
      candidates {
        id
        question
        answer
        sourceQuote
        demandAsks
        status
        position
        matchSimilarity
        matchedCuratedAnswer {
          id
          question
          answer
          status
        }
      }
    }
  }
`

export const CONTENT_HUB_ANALYTICS = gql`
  query ContentHubAnalytics(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $granularity: String
    $assetTypes: [String!]
    $userIds: [ID!]
    $sources: [String!]
    $page: Int
    $perPage: Int
    $sort: String
  ) {
    contentHubAnalytics(
      from: $from
      to: $to
      granularity: $granularity
      assetTypes: $assetTypes
      userIds: $userIds
      sources: $sources
      page: $page
      perPage: $perPage
      sort: $sort
    ) {
      summary {
        views
        visitors
        downloads
        internalPct
        externalPct
        viewsDelta
        visitorsDelta
        downloadsDelta
        roomsUsing
        shareLinksCreated
        shareLinkClicks
        roomOpens
        roomDownloads
      }
      timeSeries {
        date
        views
        visitors
        downloads
      }
      leaderboard {
        assetId
        title
        assetType
        thumbnailUrl
        engagementScore
        views
        sends
      }
      contentRows {
        assetId
        title
        assetType
        thumbnailUrl
        source
        dateAdded
        totalViews
        uniqueVisitors
        downloads
        internalPct
        externalPct
        lastUsedAt
        ownerEmail
        url
        roomsUsing
        shareLinksCreated
        shareLinkClicks
        roomOpens
        roomDownloads
      }
      totalRows
      page
      perPage
    }
  }
`

// ENGG-10113 — the file window's Analytics tab: one week's totals plus one row
// per day, bucketed in the admin's own time zone so the bars line up with the
// Monday–Sunday the picker shows.
export const CONTENT_ITEM_DAILY_COUNTS = gql`
  query ContentItemDailyCounts(
    $assetId: ID!
    $source: String
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $timeZone: String
  ) {
    contentItemAnalytics(
      assetId: $assetId
      source: $source
      from: $from
      to: $to
      timeZone: $timeZone
    ) {
      assetId
      totalViews
      uniqueVisitors
      totalDownloads
      roomsUsing
      shareLinksCreated
      shareLinkClicks
      roomOpens
      roomDownloads
      dailyCounts {
        date
        views
        uniqueVisitors
        downloads
      }
    }
  }
`

export const CONTENT_ITEM_ANALYTICS = gql`
  query ContentItemAnalytics(
    $assetId: ID!
    $source: String
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    contentItemAnalytics(
      assetId: $assetId
      source: $source
      from: $from
      to: $to
    ) {
      assetId
      title
      assetType
      thumbnailUrl
      source
      fileSize
      ownerEmail
      summaryText
      totalViews
      uniqueVisitors
      totalDownloads
      sends
      internalPct
      externalPct
      roomsUsing
      shareLinksCreated
      shareLinkClicks
      roomOpens
      roomDownloads
      roomsOpened
      roomEngagement {
        roomId
        roomName
        opens
        downloads
        visitors
        lastOpenedAt
      }
      engagementLog {
        userId
        userName
        userEmail
        views
        downloads
        sends
        shares
        lastActiveAt
        influenceTier
      }
    }
  }
`

// Content Hub Analytics — User Statistics tab. Paginated list of every
// user who engaged with the Content Hub in the window.
export const CONTENT_HUB_USER_STATISTICS = gql`
  query ContentHubUserStatistics(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $page: Int
    $perPage: Int
    $sort: String
  ) {
    contentHubUserStatistics(
      from: $from
      to: $to
      page: $page
      perPage: $perPage
      sort: $sort
    ) {
      rows {
        userId
        userName
        userEmail
        influenceTier
        views
        viewsPercent
        visitors
        downloads
        lastActiveAt
      }
      totalRows
      page
      perPage
    }
  }
`

// Content Hub Analytics — per-user drill-in. KPI band + engagement
// rows by asset.
// Per-user, per-asset engagement timeline shown in the Reports-tab
// user-detail modal. Each row is a view or download event tagged with
// its ISO timestamp — the FE groups these by "Views" / "Downloads" and
// renders them ordered newest-first.
export const CONTENT_USER_ASSET_TIMELINE = gql`
  query ContentUserAssetTimeline(
    $userId: ID!
    $assetId: ID!
    $source: String!
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    contentUserAssetTimeline(
      userId: $userId
      assetId: $assetId
      source: $source
      from: $from
      to: $to
    ) {
      actionType
      occurredAt
      sourceContext
      sourceUrl
      userRole
      visitorName
      visitorEmail
      visitorCompany
    }
  }
`

export const CONTENT_USER_ANALYTICS = gql`
  query ContentUserAnalytics(
    $userId: ID!
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    contentUserAnalytics(userId: $userId, from: $from, to: $to) {
      userId
      userName
      userEmail
      totalViews
      visitors
      totalDownloads
      viewsDelta
      visitorsDelta
      downloadsDelta
      internalPct
      externalPct
      contentRows {
        assetId
        source
        title
        thumbnailUrl
        influenceTier
        views
        viewsPercent
        visitors
        downloads
        lastActiveAt
      }
    }
  }
`

// Self-serve Events (POC): the current partner's events, newest first.
// ENGG-9204: every argument is optional and the resolver ignores the
// partner-scoping ones for anyone but a super admin, so the page can send
// whatever the filter bar holds without deciding who is allowed to widen.
export const GET_PARTNER_EVENTS = gql`
  query PartnerEvents(
    $allPartners: Boolean
    $ownerId: ID
    $status: String
    $search: String
    $tagIds: [ID!]
  ) {
    partnerEvents(
      allPartners: $allPartners
      ownerId: $ownerId
      status: $status
      search: $search
      tagIds: $tagIds
    ) {
      id
      name
      # Template tags (ENGG-9661) — the same ones the Templates page uses.
      templateTags {
        id
        name
        color
      }
      sessionCount
      sessionDates
      audienceName
      audienceDescription
      # The distinct union across the segments — NOT the sum of their counts.
      memberCount
      # Lets the Audience cell open the combined member list.
      dateCohortIds
      # Per-segment chips, descriptions and headcounts (ENGG-9570).
      audienceSegments {
        id
        name
        description
        memberCount
      }
      ownsCalendar
      skipAttendeeInvite
      ownerLabel
      # Qualifies ownerLabel when the name it carries belongs to a calendar
      # rather than to a person.
      ownerCaption
      partnerName
      # Lets the row say "submitted by you" rather than naming the reader, now
      # that a super admin can list another partner's events.
      submittedByViewer
      startTime
      endTime
      timezone
      # Shown alongside the session's own zone so the calendar's
      # owner reads their own clock time without converting.
      calendarTimezone
      # Lets the review drawer say a session is a webinar rather than a meeting.
      conferenceTool
      zoomTemplatePresetId
      zoomTemplateName
      status
      reviewNote
      deployError
      # Set while a throttled deploy waits to run again (ENGG-9896).
      deployRetryAt
      createdAt
      # Path B: lets the partner's read-only review drawer show the same live
      # invite verification the reviewer sees (ENGG-9270).
      externalEventId
      forceProvisionZoom
      # Session links come down with the list: they now resolve from stored
      # calendar events, so asking for them costs a query rather than a call out
      # to the calendar provider.
      sessions {
        id
        date
        position
        joinUrl
        meetingId
        passcode
        linkStatus
        # When a retrying link is created again — Zoom's daily limit reset
        # (ENGG-9863).
        linkRetryAt
      }
    }
  }
`

// Cohort list for the event wizard audience picker: independent cohorts with
// memberCount + taxonomy name per term so cards can show
// "Role: X · Region: Y · N people". Filter to `independent` client-side.
// Who is in an event's audience, for the "view members" modal. Takes either an
// event (the distinct UNION of its segments) or segments directly, which is what
// the picker asks before an event exists. Deliberately not
// GET_SEGMENT_MEMBERSHIP: that one also computes the taxonomy-eligible set for
// the sync comparison, which is expensive and irrelevant here.
export const GET_PARTNER_EVENT_AUDIENCE_MEMBERS = gql`
  query PartnerEventAudienceMembers(
    $dateCohortIds: [ID!]
    $partnerEventId: ID
  ) {
    partnerEventAudienceMembers(
      dateCohortIds: $dateCohortIds
      partnerEventId: $partnerEventId
    ) {
      source
      # Which segment(s) put them here — only differs when the audience spans
      # more than one (ENGG-9570).
      segmentNames
      user {
        id
        fullName
        email
      }
    }
  }
`

export const GET_COHORTS_FOR_PICKER = gql`
  query CohortsForPicker {
    # Segments are opt-in on this resolver (ENGG-9168) — it returns only
    # non-segments unless asked, so that a date-driven picker can't pick one up
    # by accident. This picker wants the opposite, and it is the ONLY thing this
    # document is for, so the argument is a literal rather than a variable a
    # caller could forget to pass. Non-segments still come back and are filtered
    # out client-side.
    dateCohorts(includeSegments: true) {
      id
      name
      # An event's audience is a segment (ENGG-9168) — the cohort type whose
      # membership is an explicit list of people.
      segment
      # A segment's name rarely says who is actually in it.
      description
      memberCount
      taxonomyTerms {
        id
        title
        taxonomy {
          name
        }
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomy {
          name
        }
      }
      # ENGG-9486: a segment is populated by a background reconcile, so a
      # memberCount of 0 can mean "still being matched" rather than "nobody
      # matches". This says which.
      memberSync {
        dateCohortId
        state
        inProgress
        progressPercentage
        phaseLabel
      }
    }
  }
`

// Admin review queue: all partners' events, newest first.
export const GET_PARTNER_EVENT_REVIEW_QUEUE = gql`
  query PartnerEventReviewQueue(
    $status: String
    $ownerId: ID
    $search: String
    $tagIds: [ID!]
  ) {
    partnerEventReviewQueue(
      status: $status
      ownerId: $ownerId
      search: $search
      tagIds: $tagIds
    ) {
      id
      name
      # Template tags (ENGG-9661), read-only here.
      templateTags {
        id
        name
        color
      }
      sessionCount
      sessionDates
      audienceName
      audienceDescription
      # The distinct union across the segments — NOT the sum of their counts.
      memberCount
      dateCohortIds
      # Per-segment chips, descriptions and headcounts (ENGG-9570).
      audienceSegments {
        id
        name
        description
        memberCount
      }
      partnerName
      ownsCalendar
      skipAttendeeInvite
      status
      # Approval is async, so 'approved' alone doesn't mean the artifacts exist.
      # Gates the assign/change-audience action in the review panel.
      deployed
      reviewNote
      deployError
      # Set while a throttled deploy waits to run again (ENGG-9896).
      deployRetryAt
      # Lets the reviewer see that a session is a webinar, not a meeting.
      conferenceTool
      zoomTemplatePresetId
      zoomTemplateName
      startTime
      endTime
      timezone
      # Shown alongside the session's own zone so the calendar's
      # owner reads their own clock time without converting.
      calendarTimezone
      createdAt
      # Path B: lets the reviewer verify the invite still exists and carries its
      # Zoom link before approving (ENGG-9270).
      externalEventId
      forceProvisionZoom
    }
  }
`

// The partners with at least one submitted event, for the filter dropdowns
// (ENGG-9204). Deliberately not every admin in the client: a dropdown full of
// people with nothing in the queue is noise.
export const GET_PARTNER_EVENT_OWNERS = gql`
  query PartnerEventOwners {
    partnerEventOwners {
      id
      name
    }
  }
`

// Full editable shape for one event — used to prefill the edit wizard.
export const GET_PARTNER_EVENT = gql`
  query PartnerEvent($id: ID!) {
    partnerEvent(id: $id) {
      id
      name
      description
      # Prefills the wizard's tag picker on edit (ENGG-9661).
      templateTags {
        id
        name
        color
      }
      ownsCalendar
      externalOwnerEmail
      externalOwnerName
      externalEventId
      skipAttendeeInvite
      forceProvisionZoom
      startTime
      endTime
      timezone
      sessionDates
      # Meeting or webinar, so the wizard reopens on the type it was saved with
      # and offers that type's templates (ENGG-9603).
      conferenceTool
      zoomTemplatePresetId
      zoomTemplateName
      facilitatorEmails
      dateCohortIds
      audienceName
      # Per-segment chips, descriptions and headcounts (ENGG-9570).
      audienceSegments {
        id
        name
        description
        memberCount
      }
    }
  }
`

// Event detail page: header info + the deployed sessions for the switcher.
export const GET_PARTNER_EVENT_DETAIL = gql`
  query PartnerEventDetail($id: ID!) {
    partnerEvent(id: $id) {
      id
      name
      status
      audienceName
      audienceDescription
      # The distinct union across the segments — NOT the sum of their counts.
      memberCount
      # Drives the assign/change-audience action: an approved event may have no
      # audience yet, and the picker needs to tick the current segments.
      dateCohortIds
      # Per-segment chips, descriptions and headcounts (ENGG-9570).
      audienceSegments {
        id
        name
        description
        memberCount
      }
      deployed
      # Why setup has not gone through, and — while a throttled deploy waits to
      # run again — when it will (ENGG-9896).
      deployError
      deployRetryAt
      startTime
      endTime
      timezone
      # Shown alongside the session's own zone so the calendar's
      # owner reads their own clock time without converting.
      calendarTimezone
      # Only an event that owns its invite can be rescheduled from here; a
      # Path B event's date is the external invite's (ENGG-10118).
      ownsCalendar
      sessions {
        id
        date
        position
        joinUrl
        meetingId
        passcode
        linkStatus
        # When a retrying link is created again — Zoom's daily limit reset
        # (ENGG-9863).
        linkRetryAt
        # The session's own schedule — editable per session since ENGG-10118 —
        # and what the calendar invite itself currently says, so a move made
        # directly in Google shows up here.
        startTime
        endTime
        timezone
        calendarStartAt
        calendarEndAt
        # not_created | in_sync | syncing | sync_failed | moved_on_calendar |
        # cancelled
        calendarState
      }
      # Every date/time change, newest first: made here or on the calendar.
      scheduleChanges {
        id
        sessionId
        source
        changedBy
        createdAt
        previous {
          date
          startTime
          endTime
          timezone
        }
        current {
          date
          startTime
          endTime
          timezone
        }
      }
    }
  }
`

// Per-session attendance: summary + per-attendee rows.
export const GET_PARTNER_EVENT_SESSION_ATTENDANCE = gql`
  query PartnerEventSessionAttendance($sessionTaskId: ID!) {
    partnerEventSessionAttendance(sessionTaskId: $sessionTaskId) {
      attendanceState
      attendedCount
      absentCount
      totalCount
      attendanceRate
      avgMinutes
      rows {
        userId
        name
        email
        roleRegion
        joinedAt
        minutes
        status
      }
    }
  }
`

// ENGG-9501: the same attendance, rolled up over every date of the event.
// Attending any one date counts as attending the event, so `status` here is the
// combined verdict and `sessions` carries the per-date cells behind it.
export const GET_PARTNER_EVENT_ATTENDANCE = gql`
  query PartnerEventAttendance($partnerEventId: ID!) {
    partnerEventAttendance(partnerEventId: $partnerEventId) {
      attendanceState
      attendedCount
      absentCount
      totalCount
      attendanceRate
      avgMinutes
      sessionCount
      sessions {
        sessionId
        position
        date
        attendanceState
        attendedCount
        totalCount
        attendanceRate
      }
      rows {
        userId
        name
        email
        roleRegion
        minutes
        sessionCount
        attendedSessionCount
        status
        sessions {
          sessionId
          position
          date
          status
          minutes
          joinedAt
        }
      }
    }
  }
`

// Upcoming events on the client's default calendar, for the Path B event
// wizard (attach an audience to a session someone else owns).
export const GET_EXTERNAL_CALENDAR_EVENTS = gql`
  query ExternalCalendarEvents {
    externalCalendarEvents {
      id
      name
      # ENGG-9722: one of a series. The date below is then only the NEXT
      # occurrence, and is null when the calendar could not be expanded — never
      # the series' own (possibly long-past) start. Ask for the real list via
      # externalCalendarEventOccurrences before publishing.
      recurring
      date
      time
      endTime
      # timezone is the zone the invite was AUTHORED in; time/endTime are wall
      # clock in calendarTimezone. They often disagree — never label one with
      # the other.
      timezone
      calendarTimezone
      ownerEmail
      ownerName
      joinUrl
    }
  }
`

// ENGG-8969: Custom Reports column picker.
//
// The queries below replace the old "load every template task up
// front" model:
//   * GET_TEMPLATE_CATEGORIES_FOR_REPORTS — light skeleton for the tree
//     (roots + section children + task counts). Also drives Templates-tab
//     search via the new `query:` arg.
//   * GET_TEMPLATE_TASKS_BY_CATEGORY_FOR_REPORTS — fetched on template /
//     section expand. Reuses the templates-page resolver.
//   * SEARCH_TEMPLATE_TASKS_FOR_REPORTS — Template-Tasks-tab search,
//     returns a flat list with enough category context to render.
//   * GET_REPORT_SELECTED_COLUMNS — hydrates the right-side "Columns"
//     list from the report's stored config, without needing the full
//     task tree in the client.
export const GET_TEMPLATE_CATEGORIES_FOR_REPORTS = gql`
  query TemplateCategoriesForReports($query: String) {
    templateCategories(query: $query) {
      id
      position
      category
      categoryRaw
      internalCategory
      inactive
      templateTasksCount(showInactive: true)
      children {
        id
        position
        category
        categoryRaw
        internalCategory
        templateTasksCount(showInactive: true)
        parent {
          id
          internalCategory
        }
      }
    }
  }
`

// ENGG-9010 — the task types directly under each template and section, so the
// award-rule target tree can filter by type without loading every task.
export const TEMPLATE_CATEGORY_TASK_TYPES = gql`
  query TemplateCategoryTaskTypes {
    templateCategoryTaskTypes {
      templateCategoryId
      taskTypes
    }
  }
`

export const GET_TEMPLATE_TASKS_BY_CATEGORY_FOR_REPORTS = gql`
  query TemplateTasksByCategoryForReports(
    $templateCategoryId: ID!
    $showInactive: Boolean
  ) {
    templateTasksByCategory(
      templateCategoryId: $templateCategoryId
      showInactive: $showInactive
    ) {
      id
      title
      internalName
      taskType
      position
      templateCategoryId
      questions {
        id
        title
        order
      }
    }
  }
`

export const SEARCH_TEMPLATE_TASKS_FOR_REPORTS = gql`
  query SearchTemplateTasksForReports($query: String, $showInactive: Boolean) {
    allTemplateTasks(query: $query, showInactive: $showInactive) {
      id
      title
      internalName
      taskType
      position
      templateCategory {
        id
        category
        internalCategory
        position
        parent {
          id
          category
          internalCategory
          position
        }
      }
      questions {
        id
        title
        order
      }
    }
  }
`

export const GET_REPORT_SELECTED_COLUMNS = gql`
  query ReportSelectedColumns($reportId: ID!) {
    reportSelectedColumns(reportId: $reportId)
  }
`

// ENGG-8969: fetch-on-bulk-select. Used the two ways the resolver
// is designed for:
//   * clicking a template checkbox → one root id
//   * clicking Select All          → every visible root id at once
export const GET_TEMPLATE_TASKS_UNDER_ROOT_FOR_REPORTS = gql`
  query TemplateTasksUnderRootForReports(
    $rootCategoryIds: [ID!]!
    $showInactive: Boolean
  ) {
    templateTasksUnderRoot(
      rootCategoryIds: $rootCategoryIds
      showInactive: $showInactive
    ) {
      id
      title
      internalName
      taskType
      position
      templateCategoryId
      questions {
        id
        title
        order
      }
    }
  }
`

// ENGG-9270: verify one picked Path B invite instead of asking the partner to
// attest that it exists and carries its Zoom link. A live calendar fetch per
// call, so ask only where it's needed (the wizard's review step, the reviewer's
// approve panel) — never per row in a list.
//
// `status` is "found" | "missing" | "unavailable". Treat "unavailable" as "we
// could not check", NOT as a missing invite: it also covers an unreachable
// calendar, and claiming an invite was deleted on the back of a timeout is worse
// than saying nothing.
export const GET_EXTERNAL_CALENDAR_EVENT = gql`
  query ExternalCalendarEvent($eventId: String!, $onDate: String) {
    externalCalendarEvent(eventId: $eventId, onDate: $onDate) {
      status
      event {
        id
        name
        recurring
        date
        time
        endTime
        timezone
        calendarTimezone
        ownerEmail
        ownerName
        joinUrl
      }
    }
  }
`

// ENGG-9722: the upcoming occurrences of a recurring Path B invite, so the
// partner picks the session they actually mean.
//
// Sourced from the calendar provider's OWN expansion, not from re-expanding the
// recurrence rule — a series can carry moved instances (the HubSpot "LATAM Grow
// Day" series runs on Thursdays; the session that ran was a Tuesday) and only
// the provider knows about those. An empty list means we could not read the
// calendar, not that the series has ended.
//
// A live fetch, so ask it when an invite is picked — never per row in the list.
export const GET_EXTERNAL_CALENDAR_EVENT_OCCURRENCES = gql`
  query ExternalCalendarEventOccurrences($eventId: String!) {
    externalCalendarEventOccurrences(eventId: $eventId) {
      date
      time
    }
  }
`

// ENGG-9007: feeds the CertificateNotificationsModal — walks the template
// hierarchy and returns every level (root template, each section, each
// task) that has a certificate assigned along with its current
// notifyOnCertificate recipient list. Deliberately shallow field
// selection (name + badge + notify recipients) so we don't drag the full
// task payload through the modal.
export const TEMPLATE_CERTIFICATE_NOTIFICATIONS = gql`
  query TemplateCertificateNotifications($id: ID!) {
    templateCategoryHierarchy(id: $id) {
      id
      category
      internalCategory
      certificateId
      certificate {
        # ENGG-10188 — the header draws the certificate itself.
        ...CertificateArtworkFields
        sendEmailOnCompletion
        emailSubject
        emailBody
        badge {
          ...BadgeFields
        }
      }
      notifyOnCertificate {
        type
        value
        label
      }
      sendEmailOnCertificate
      templateTasks {
        id
        title
        internalName
        certificateId
        certificate {
          # ENGG-10188 — the header draws the certificate itself.
          ...CertificateArtworkFields
          sendEmailOnCompletion
          emailSubject
          emailBody
          badge {
            ...BadgeFields
          }
        }
        notifyOnCertificate {
          type
          value
          label
        }
        sendEmailOnCertificate
      }
      children {
        id
        category
        internalCategory
        certificateId
        certificate {
          # ENGG-10188 — the header draws the certificate itself.
          ...CertificateArtworkFields
          sendEmailOnCompletion
          emailSubject
          emailBody
          badge {
            ...BadgeFields
          }
        }
        notifyOnCertificate {
          type
          value
          label
        }
        sendEmailOnCertificate
        templateTasks {
          id
          title
          internalName
          certificateId
          certificate {
            # ENGG-10188 — the header draws the certificate itself.
            ...CertificateArtworkFields
            sendEmailOnCompletion
            emailSubject
            emailBody
            badge {
              ...BadgeFields
            }
          }
          notifyOnCertificate {
            type
            value
            label
          }
          sendEmailOnCertificate
        }
      }
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

// ENGG-9123: connection state of every external meeting source (Avoma today).
// Meeting sources are generally available, so the backend returns every
// registered provider for every client — an EMPTY list means no provider is
// registered at all. There is no feature flag on either side; `connected` alone
// distinguishes "paste a key" from "ready to use".
export const GET_CONNECTED_SOURCES = gql`
  query ConnectedSources {
    connectedSources {
      key
      label
      connected
      verifiedAt
      lastError
      supportsRecordings
      useRecordings
    }
  }
`

// ENGG-10387: the Gong card's "Sales rooms" section (Settings → Call Recorder).
// Its own query, so the field only it reads (lookbackDays) stays out of the
// Avoma card's GET_CONNECTED_SOURCES: an admin bundle live before its backend,
// or pointed at an older one, then fails this section alone, not the Avoma
// card. A superset of GET_CONNECTED_SOURCES on purpose: both cards are on one
// page and read the one cached `connectedSources`, and a subset would make
// each refetch the other in turn.
export const GET_GONG_SALES_ROOM_SOURCE = gql`
  query GongSalesRoomSource {
    connectedSources {
      key
      label
      connected
      verifiedAt
      lastError
      supportsRecordings
      useRecordings
      lookbackDays
    }
  }
`

// ENGG-10423: "Call visibility in sales rooms" (Settings → Integrations and
// Call Recorder). Its own query for the same reason as the Gong section's: an
// admin bundle live before its backend fails this card alone. Only the fields
// the card reads: a ConnectedSource is normalized by `key`
// (cacheTypePolicies), so this merges with the other cards' selections. The
// value is the client's, the same on every source.
export const GET_DSR_CALL_VISIBILITY = gql`
  query DsrCallVisibility {
    connectedSources {
      key
      callVisibility
    }
  }
`

// ENGG-10424: how far the server has listed each meeting source's calls into
// the cache the app's call pickers serve (the Gong and Avoma cards' status
// line). Its own query, like the call visibility card's: an admin bundle live
// before its backend fails this line alone. Merged by `key` with the others.
export const GET_MEETING_SOURCE_LISTING = gql`
  query MeetingSourceListing {
    connectedSources {
      key
      listedSince
      listedAt
      listingComplete
    }
  }
`

// =====================================================================
// Roleplay Text Simulation (ENGG-8927)
// =====================================================================

// Single run — polled while a simulation is in flight so the transcript can
// stream in without refetching the whole task's history.
export const GET_AI_ROLEPLAY_SIMULATION = gql`
  query AiRoleplaySimulation($id: ID!) {
    aiRoleplaySimulation(id: $id) {
      id
      status
      transcript
      endReason
      agentTurnCount
      traineeTurnCount
      currentTurn
      stageUpdatedAt
      maxAgentTurns
      promptTokens
      completionTokens
      totalTokens
      errorMessage
      createdAt
    }
  }
`

export const GET_AI_ROLEPLAY_SIMULATIONS = gql`
  query TemplateTaskSimulations($id: ID!) {
    templateTask(id: $id) {
      id
      title
      aiRoleplaySimulations {
        id
        status
        endReason
        traineePromptSource
        agentTurnCount
        traineeTurnCount
        totalTokens
        criteriaPassed
        # Both are needed to tell "waiting on the judge" apart from "the judge
        # answered and reached no verdict". The judge can return passed: null
        # with a summary (RoleplaySuccessCriteriaJudge, unparseable reply), and
        # without the assessment here criteriaVerdict read that as "Judging…"
        # forever while the run modal, which does fetch it, called the same run
        # not assessed. The blob is small — a summary and one line per criterion.
        successCriteria
        criteriaAssessment
        # Matrix batches show a grid summary in the history rail instead of a
        # pass count, so the rows need to say which kind they are.
        profile
        label
        testCriteria
        coversCriterionIds
        cellResults
        testKind
        batchKey
        runIndex
        runCount
        maxAgentTurns
        currentTurn
        errorMessage
        createdAt
        createdByUser {
          id
          firstName
          lastName
        }
      }
    }
  }
`

// Every run in one test, polled by the run modal — one request covers a 5x
// batch's progress instead of five parallel single-run polls.
// What the roleplay agent is actually minted with (optimized instructions if
// applied, else the roleplay instructions). Not the builder context that
// GET_TEMPLATE_TASK_AI_ROLEPLAY_PROMPT renders: that one carries the task
// title and description, which are written for the employee, not the agent.
export const GET_TEMPLATE_TASK_ROLEPLAY_AGENT_PROMPT = gql`
  query TemplateTaskRoleplayAgentPrompt($templateTaskId: ID!) {
    templateTaskRoleplayAgentPrompt(templateTaskId: $templateTaskId)
  }
`

// The prompt recommendation drafted from one Test Lab set (null until asked
// for). Same shape as AiRoleplayOptimizationFields so the optimizer's diff view
// can render it.
// Polled while PlanAiRoleplayTestSuiteJob runs. `plan` is null until status is
// completed; `errorMessage` is set when it is failed.
export const GET_AI_ROLEPLAY_TEST_SUITE_PLAN_REQUEST = gql`
  query AiRoleplayTestSuitePlanRequest($id: ID!) {
    aiRoleplayTestSuitePlanRequest(id: $id) {
      id
      status
      errorMessage
      plan {
        criteria {
          id
          text
          about
          check
          countRule
          question
          prohibition
          audit
          name
          threshold
          measures
          testKind
        }
        conversations {
          id
          label
          profile
          situation
          coversCriterionIds
          passChecks
          traineePrompt
          testKind
          setupTurns
          testExamples
          validation
        }
      }
    }
  }
`

export const GET_AI_ROLEPLAY_TEST_LAB_RECOMMENDATION = gql`
  query AiRoleplayTestLabRecommendation(
    $templateTaskId: ID!
    $batchKey: ID!
    $criterionId: ID
  ) {
    aiRoleplayTestLabRecommendation(
      templateTaskId: $templateTaskId
      batchKey: $batchKey
      criterionId: $criterionId
    ) {
      id
      templateTaskId
      status
      kind
      criterionId
      focusNote
      originalInstructions
      proposedInstructions
      summaryOfChanges
      finalScores
      iterations
      traineeScripts
      modelVersions
      promptTokens
      completionTokens
      totalTokens
      errorMessage
      appliedAt
      discardedAt
      createdAt
      updatedAt
      currentStage
      currentIteration
      stageDetail
      stageUpdatedAt
      sources {
        id
        sourceableType
        sourceableId
        label
        createdAt
      }
      createdByUser {
        id
        firstName
        lastName
      }
    }
  }
`

export const GET_AI_ROLEPLAY_SIMULATION_BATCH = gql`
  query AiRoleplaySimulationBatch($batchKey: ID!) {
    aiRoleplaySimulationBatch(batchKey: $batchKey) {
      id
      runIndex
      runCount
      status
      transcript
      endReason
      agentTurnCount
      traineeTurnCount
      currentTurn
      stageUpdatedAt
      maxAgentTurns
      totalTokens
      successCriteria
      criteriaPassed
      criteriaAssessment
      profile
      label
      situation
      testCriteria
      coversCriterionIds
      passChecks
      cellResults
      traineePrompt
      testKind
      setupTurns
      testExamples
      validation
      errorMessage
      createdAt
    }
  }
`

// Test Lab moments saved from transcripts. Each runs as a single-reply test
// in every test set on the task.
export const GET_AI_ROLEPLAY_SAVED_TESTS = gql`
  query TemplateTaskSavedTests($id: ID!) {
    templateTask(id: $id) {
      id
      aiRoleplaySavedTests {
        id
        name
        expectation
        originalReply
        criterionId
        setupTurns
        createdAt
      }
    }
  }
`

// ENGG-9007: per-certificate history of cert-award + notify-by-email
// delivery attempts. Backed by the certificate_notification_emails
// audit table (mirrors reports' ReportNotificationEmail pattern) —
// not the shared email_messages audit, so the query is naturally
// scoped and filterable by email_type without leaking unrelated
// traffic.
export const CERTIFICATE_NOTIFICATION_EMAILS = gql`
  query CertificateNotificationEmails(
    $certificateId: ID!
    $emailType: String
    $limit: Int
    $offset: Int
  ) {
    certificateNotificationEmails(
      certificateId: $certificateId
      emailType: $emailType
      limit: $limit
      offset: $offset
    ) {
      totalCount
      nodes {
        id
        certificateId
        userCertificationId
        userId
        emailType
        triggerType
        status
        toEmails
        subject
        error
        sentAt
      }
    }
  }
`

export const GET_CLIENT_DATAWAREHOUSE_REPORT_SYNCS = gql`
  query ClientDatawarehouseReportSyncs {
    clientDatawarehouseReportSyncs {
      id
      frequency
      enabled
      s3Bucket
      s3Region
      s3Prefix
      hasS3Credentials
      s3AuthMode
      s3RoleArn
      hasS3ExternalId
      userId
      userEmail
      userName
      reports {
        id
        cardId
        fileName
      }
      lastRun {
        id
        status
        startedAt
        endedAt
        errorMessage
        reportResults
        createdAt
      }
    }
  }
`

export const GET_CLIENT_DATAWAREHOUSE_REPORT_SYNC_RUNS = gql`
  query ClientDatawarehouseReportSyncRuns($syncId: ID!, $limit: Int) {
    clientDatawarehouseReportSyncRuns(syncId: $syncId, limit: $limit) {
      id
      status
      startedAt
      endedAt
      errorMessage
      reportResults
      createdAt
    }
  }
`

export const GET_METABASE_ACCESSIBLE_REPORTS = gql`
  query MetabaseAccessibleReports($userId: ID!) {
    metabaseAccessibleReports(userId: $userId) {
      cardId
      name
      collectionName
      dashboardNames
    }
  }
`

// Lean user lookup for the Metabase S3 sync user picker — id/email/name only.
export const GET_METABASE_SYNC_USER_OPTIONS = gql`
  query MetabaseSyncUserOptions($query: String) {
    users(query: $query, pagination: { page: 1, perPage: 20 }) {
      data {
        id
        email
        firstName
        lastName
      }
    }
  }
`

// Canvases already built in this client, for the Channel Canvas modal's
// "start from an existing canvas" picker (ENGG-9411). Carries the HTML so a
// pick is a lookup, not a second query.
export const GET_SLACK_CANVAS_SOURCES = gql`
  query SlackCanvasSources($excludeCanvasId: ID) {
    slackCanvasSources(excludeCanvasId: $excludeCanvasId) {
      id
      title
      workflowName
      canvasTitle
      content
      updatedAt
    }
  }
`

// =====================================================================
// CRM connection and stage mappings (ENGG-9746)
// =====================================================================

// The client's connection to one CRM provider. Never carries the token.
export const GET_CRM_ACCOUNT = gql`
  query CrmAccount($provider: String!) {
    crmConnection(provider: $provider) {
      provider
      label
      connected
      externalAccountId
      accountUrl
      status
      lastError
      verifiedAt
      lastPolledAt
    }
  }
`

// The provider's pipelines and stage IDS, read live: mappings are keyed on what
// the CRM actually sends, never on a label.
export const GET_CRM_PIPELINES = gql`
  query CrmPipelines($provider: String!) {
    crmPipelines(provider: $provider) {
      id
      label
      stages {
        id
        label
        position
        closed
      }
    }
  }
`

export const GET_CRM_STAGE_MAPPINGS = gql`
  query CrmStageMappings($provider: String!) {
    crmStageMappings(provider: $provider) {
      id
      externalPipelineId
      externalStageId
      salesStage
      label
    }
  }
`

// =====================================================================
// Sales automation: call ingestion and the default room template (ENGG-9829)
// =====================================================================

// The per-client switch. `available` is the deployment gate
// (SALES_AUTOMATION_ENABLED): false means the feature is dormant, nothing here
// can be changed, and the card must not render at all. `ingestionReady` is the
// model's own answer to "is anything actually being ingested" — enabled AND
// (rooms built with AI OR a default template). The card's status line reads the
// switch, the creation mode and the RESOLVED template rather than that flag, so
// a template deleted after it was chosen still reads as missing. ENGG-10240 adds
// the creation mode and the fallback owner (picked from `fallbackOwnerOptions`).
export const GET_SALES_AUTOMATION_SETTING = gql`
  query SalesAutomationSetting {
    salesAutomationSetting {
      ...SalesAutomationSettingFields
    }
  }
  ${FRAGMENT_SALES_AUTOMATION_SETTING_FIELDS}
`

// ENGG-10296 — the calls the sales-call screen kept from opening a room in the
// last 7 days (the backend's default window), newest first, one row per call
// (its newest delivery), still without a room. A call someone already tried to
// build anyway that was then skipped for another reason is listed again, with
// that reason (`screenOverriddenAt` set). The card's "Skipped calls" list.
export const GET_SKIPPED_CALL_EVENTS = gql`
  query SkippedCallEvents {
    skippedCallEvents {
      id
      subject
      organizerEmail
      occurredAt
      resolvedDomain
      screenReason
      screenConfidence
      screenOverriddenAt
      detail
    }
  }
`

// ENGG-9869 — the admin Sidekick page: its client's setup, and the calls and
// questions behind the analytics tabs over the last `days` days.
export const SIDEKICK_SETTINGS = gql`
  query SidekickSettings {
    sidekickSettings {
      enabled
      botName
      defaultGoals
      competitorNames
      callContext
      answerInstructions
      defaultAnswerInstructions
      followUpInstructions
      defaultFollowUpInstructions
      updatedAt
      roles {
        name
        blurb
        callTypes {
          name
          goals
        }
      }
      keyTerms {
        term
        heardAs
      }
    }
  }
`

export const SIDEKICK_ANALYTICS = gql`
  query SidekickAnalytics($days: Int, $timeZone: String) {
    sidekickAnalytics(days: $days, timeZone: $timeZone) {
      truncated
      callTotal
      questionTotal
      calls {
        id
        title
        contact
        repId
        repName
        role
        callType
        status
        startedAt
        durationSeconds
        goalsCovered
        goalsTotal
        questionCount
        outcome
        listeningSeconds
        followUpDrafted
        followUpSentAt
        followUpEdited
        followUpRating
        repRating
      }
      questions {
        id
        callId
        callTitle
        repId
        repName
        role
        callType
        askedAt
        quote
        searchQuery
        topic
        strength
        outcome
        positiveFeedback
        negativeFeedback
      }
      # ENGG-10114 — usage over every call in the range (test-mode calls are left out server-side).
      usage {
        totals {
          activeReps
          calls
          trackedCalls
          listeningSeconds
          avgListeningSeconds
          viewMinimizedSeconds
          viewMediumSeconds
          viewFullSeconds
          viewHiddenSeconds
          questions
          answered
          opens
          answersCopied
          askNextShown
          askNextUsed
          followUpsDrafted
          followUpsSent
          followUpsEdited
          followUpRatingGood
          followUpRatingBad
          callRatingGood
          callRatingBad
          desktopCalls
          browserCalls
          botCalls
          testCallsExcluded
          repsWithoutCalls
        }
        reps {
          userId
          name
          calls
          trackedCalls
          listeningSeconds
          viewMinimizedSeconds
          viewMediumSeconds
          viewFullSeconds
          questions
          answered
          opens
          followUpsSent
          followUpsEdited
          callRatingGood
          callRatingBad
          lastCallAt
        }
        weeks {
          weekStart
          calls
          activeReps
          listeningSeconds
        }
        # ENGG-10307 — the daily grid: every user of the account, their calls per calendar day in $timeZone.
        days
        dailyReps {
          userId
          name
          signedIn
          daysActive
          cells {
            date
            calls
            listeningSeconds
            questions
          }
        }
      }
    }
  }
`

// ENGG-10315 — one call from the Call success tab, opened in its window: the
// transcript, every question the Sidekick caught as the rep saw them, and
// every run of its classifier (admins only), fired or not.
export const SIDEKICK_CALL_DETAILS = gql`
  query SidekickCallDetails($id: ID!) {
    sidekickCall(id: $id) {
      id
      title
      status
      startedAt
      durationSeconds
      repName
      callType
      transcription
      questions {
        id
        askedAt
        offsetSeconds
        speaker
        quote
        searchQuery
        outcome
        answer
        askNext
        sources
      }
      decisions {
        id
        decidedAt
        offsetSeconds
        speaker
        turn
        trigger
        kind
        reason
        repeat
        repeatOf
        query
        quote
        classifyMs
        questionId
        judgeShouldTrigger
        judgeVerdict
        judgeKindOk
        judgeQuery
        judgeBetterQuery
        judgeRepeatOk
        judgeReason
        judgedAt
      }
      classifierReview
    }
  }
`

// ENGG-9844 — Anonymous Surveys.
//
// The respondent-facing side of this feature is NOT here: the public /s/<token>
// page talks to unauthenticated REST endpoints on the backend
// (src/components/public-survey/publicSurveyApi.ts), deliberately outside the
// authenticated GraphQL schema.
const ANONYMOUS_SURVEY_FIELDS = `
  id
  title
  introText
  thankYouMessage
  status
  # What the link actually does right now — an open survey past its close date
  # reads as closed without anything having run.
  effectiveStatus
  closesAt
  shareToken
  responseCount
  startedCount
  lastResponseOn
  # False once the first response lands: questions are frozen so existing
  # answers keep their meaning.
  structureEditable
  ownerId
  ownerName
  ownedByViewer
  createdAt
`

export const GET_ANONYMOUS_SURVEYS = gql`
  query AnonymousSurveys($status: String, $search: String, $mineOnly: Boolean) {
    anonymousSurveys(status: $status, search: $search, mineOnly: $mineOnly) {
      ${ANONYMOUS_SURVEY_FIELDS}
    }
  }
`

export const GET_ANONYMOUS_SURVEY = gql`
  query AnonymousSurvey($id: ID!) {
    anonymousSurvey(id: $id) {
      ${ANONYMOUS_SURVEY_FIELDS}
      questions {
        id
        position
        questionType
        prompt
        promptText
        required
        options {
          id
          label
        }
        scaleMin
        scaleMax
        scaleMinLabel
        scaleMaxLabel
      }
      # Every link this survey has had, newest first. The active one matches
      # shareToken; the rest are revoked and no longer work.
      shareLinks {
        id
        token
        active
        createdAt
        revokedAt
      }
    }
  }
`

const ANONYMOUS_SURVEY_QUESTION_RESULT_FIELDS = `
  id
  prompt
  promptText
  questionType
  required
  answeredCount
  options {
    id
    label
    count
    percentage
  }
  average
  distribution {
    value
    count
  }
  weeklyTrend {
    weekStart
    average
    count
  }
  # Random order — stored order is submission order, which is attributable.
  textAnswers
`

export const GET_ANONYMOUS_SURVEY_ANALYTICS = gql`
  query AnonymousSurveyAnalytics($id: ID!, $groupByQuestionId: ID) {
    anonymousSurveyAnalytics(id: $id, groupByQuestionId: $groupByQuestionId) {
      responseCount
      startedCount
      completionRate
      responsesOverTime {
        date
        count
        # Opens that day, and submitted/started for it. A bare submission count
        # could not show a day where a reminder went out and nobody finished.
        startedCount
        rate
      }
      # How long people took, in coarse bands. The exact duration is never
      # stored — see the backend migration for why.
      durationBreakdown {
        bucket
        count
        percentage
      }
      questions {
        ${ANONYMOUS_SURVEY_QUESTION_RESULT_FIELDS}
      }
      groups {
        optionId
        label
        responseCount
        # True below five responses: the group's questions list is then empty
        # and the UI says "Not enough responses."
        suppressed
        questions {
          ${ANONYMOUS_SURVEY_QUESTION_RESULT_FIELDS}
        }
      }
    }
  }
`

// Kept OUT of the analytics query on purpose. The aggregates are re-fetched
// every time the breakdown changes, and folding every answer cell into them
// made each of those round trips grow with the survey's whole history. This is
// fetched once, on its own.
export const GET_ANONYMOUS_SURVEY_SUBMISSIONS = gql`
  query AnonymousSurveySubmissions($id: ID!) {
    anonymousSurveyAnalytics(id: $id) {
      # One row per response — the same rows the CSV contains, shuffled, date
      # only. More revealing than the aggregates: a combination identifies
      # where a single answer does not.
      submissions {
        submittedOn
        durationBucket
        cells {
          questionId
          value
        }
      }
    }
  }
`

export const GET_ANONYMOUS_SURVEY_EXPORT = gql`
  query AnonymousSurveyExport($id: ID!) {
    anonymousSurveyExport(id: $id) {
      csv
      filename
    }
  }
`

// ENGG-9888 — the notes format the Field Notes settings page edits. Saved
// with UPDATE_PROMPTS as FIELD_NOTES / NOTES_FORMAT.
export const FIELD_NOTES_FORMAT = gql`
  query FieldNotesFormat {
    fieldNotesFormat {
      format
      defaultFormat
      custom
    }
  }
`

// Polled while DiagnoseRoleplayQuestionJob runs. `diagnosis` is null until
// status is completed; `errorMessage` is set when it is failed.
export const ROLEPLAY_ADVISOR_RUNS = gql`
  query RoleplayAdvisorRuns($templateTaskId: ID!, $limit: Int) {
    roleplayAdvisorRuns(templateTaskId: $templateTaskId, limit: $limit) {
      id
      kind
      feedback
      status
      stage
      result
      errorMessage
      createdByName
      createdAt
      calls
    }
  }
`

export const ROLEPLAY_QUESTION_DIAGNOSIS = gql`
  query RoleplayQuestionDiagnosis($id: ID!) {
    roleplayQuestionDiagnosis(id: $id) {
      id
      status
      attemptCount
      diagnosis
      errorMessage
      reps
    }
  }
`

// ENGG-9979 — Call Scorecards
const CALL_BEHAVIOR_SCORE_FIELDS = `
  id
  code
  callBehaviorId
  score
  repQuote
  repAt
  buyerQuote
  buyerAt
  strongerLine
  quoteVerified
  flaggedSuggestion
`

const RECORDED_CALL_ROW_FIELDS = `
  id
  startedAt
  durationSeconds
  accountName
  buyerName
  buyerTitle
  repId
  repName
  repManagerName
  callType
  callTypeSource
  scorable
  skipReason
  status
  callScore
  stageScores
  behaviorsInScope
  dealKey
  crmStage
  crmStageStatus
  crmOpportunityName
  crmOpportunityAmount
  buyerAttendees
  behaviorScores {
    id
    code
    score
  }
`

export const CALL_SCORECARD_CALLS = gql`
  query CallScorecardCalls(
    $filters: CallScorecardFiltersInput
    $sortKey: String
    $sortDir: String
    $page: Int
    $perPage: Int
  ) {
    callScorecardCalls(filters: $filters, sortKey: $sortKey, sortDir: $sortDir, page: $page, perPage: $perPage) {
      totalCount
      skippedCount
      page
      perPage
      rows {
        ${RECORDED_CALL_ROW_FIELDS}
      }
    }
  }
`

export const CALL_SCORECARD_KPIS = gql`
  query CallScorecardKpis($filters: CallScorecardFiltersInput) {
    callScorecardKpis(filters: $filters) {
      callsScored
      callsScoredPrior
      avgScore
      avgScorePrior
      atBar
      mostMissed {
        code
        avg
        inScope
        zeros
      }
      focus {
        code
        avg
        inScope
        zeros
      }
    }
  }
`

export const CALL_SCORECARD_BEHAVIOR_STATS = gql`
  query CallScorecardBehaviorStats($filters: CallScorecardFiltersInput) {
    callScorecardBehaviorStats(filters: $filters) {
      code
      avg
      inScope
      zeros
    }
  }
`

export const CALL_SCORECARD_REPS = gql`
  query CallScorecardReps($taxonomyTermIds: [ID!]) {
    callScorecardReps(taxonomyTermIds: $taxonomyTermIds) {
      id
      name
      email
      calls
      managerId
      managerName
    }
  }
`

export const RECORDED_CALL = gql`
  query RecordedCall($id: ID!) {
    recordedCall(id: $id) {
      ${RECORDED_CALL_ROW_FIELDS}
      title
      summary
      talkRatio
      questionsAsked
      audioUrl
      sourceName
      lastError
      outcome
      meetingKind
      repTitle
      crmDetails {
        label
        value
      }
      behaviorScores {
        ${CALL_BEHAVIOR_SCORE_FIELDS}
      }
      transcript {
        start
        end
        role
        name
        text
      }
      practiceRoleplays {
        id
        title
        status
        behaviorCodes
        assignments {
          id
          userId
          status
        }
      }
    }
  }
`

const BEHAVIOR_STAT_FIELDS = `
  code
  avg
  inScope
  zeros
`

export const REP_SCORECARD = gql`
  query RepScorecard($userId: ID!, $filters: CallScorecardFiltersInput) {
    repScorecard(userId: $userId, filters: $filters) {
      repId
      repName
      managerName
      teamSize
      callsScored
      scoreLast30
      teamLast30
      companyLast30
      scorePrior60To90
      stageAverages
      behaviorStats { ${BEHAVIOR_STAT_FIELDS} }
      teamBehaviorStats { ${BEHAVIOR_STAT_FIELDS} }
      companyBehaviorStats { ${BEHAVIOR_STAT_FIELDS} }
      behaviorExamples {
        code
        score
        recordedCallId
        accountName
        startedAt
        buyerName
        repQuote
        buyerQuote
        buyerAt
      }
      recentCalls {
        ${RECORDED_CALL_ROW_FIELDS}
      }
      practice {
        code
        status
        scenarioId
        roleplayId
        roleplayTitle
        dueOn
        completedAt
        bestSkillScore
      }
    }
  }
`

export const REP_SKILL_TIMELINE = gql`
  query RepSkillTimeline($userId: ID!, $codes: [String!]!, $weeks: Int) {
    repSkillTimeline(userId: $userId, codes: $codes, weeks: $weeks) {
      code
      weekStarts
      rep
      team
    }
  }
`

export const CALL_BEHAVIOR_LIBRARY = gql`
  query CallBehaviorLibrary {
    callBehaviorLibrary {
      id
      name
      version
      versionLabel
      behaviors {
        ...CallBehaviorFields
      }
      methodology {
        ...CallScorecardMethodologyFields
      }
    }
  }
  ${FRAGMENT_CALL_BEHAVIOR_FIELDS}
  ${FRAGMENT_CALL_SCORECARD_METHODOLOGY_FIELDS}
`

// ENGG-10394 — Scorecard setup › History.
export const CALL_SCORING_HISTORY = gql`
  query CallScoringHistory($before: String) {
    callScoringHistory(before: $before) {
      id
      kind
      at
      cursor
      by
      code
      itemName
      action
      fields
      fieldChanges
      versionLabel
      rescore
      days
      last
      from
      to
      source
      calls
      status
    }
  }
`

// ENGG-10393 — how many scored calls a change to one item would rescore.
export const CALL_SCORECARD_RESCORE_IMPACT = gql`
  query CallScorecardRescoreImpact($scope: CallRescoreScopeInput!) {
    callScorecardRescoreImpact(scope: $scope)
  }
`

export const CALL_SCORING_PROMPT = gql`
  query CallScoringPrompt(
    $callType: Int
    $draft: CallBehaviorInput
    $draftCode: String
  ) {
    callScoringPrompt(
      callType: $callType
      draft: $draft
      draftCode: $draftCode
    ) {
      text
      wordCount
      behaviorsOn
      sections {
        id
        name
        text
      }
    }
  }
`

export const CALL_SAVED_REPORTS = gql`
  query CallSavedReports {
    callSavedReports {
      id
      name
      description
      filters
      view
    }
  }
`

// ENGG-10065 — Call Scorecards v1.2: Stages and Deals
const CALL_TYPE_FIELDS = `
  id
  slot
  position
  name
  shortLabel
  description
  kind
  crmStages
  titleKeywords
  phrases
  useCrmStage
  useAi
  carryGaps
  maxCarry
  behaviorOrder
`

export const CALL_SCORECARD_CALL_TYPES = gql`
  query CallScorecardCallTypes {
    callScorecardCallTypes {
      ${CALL_TYPE_FIELDS}
      matchedLast30
    }
  }
`

export const CALL_SCORECARD_CRM_STAGES = gql`
  query CallScorecardCrmStages {
    callScorecardCrmStages
  }
`

const DEAL_SUMMARY_FIELDS = `
  key
  accountName
  opportunityName
  amount
  stage
  buyerName
  buyerTitle
  repId
  repName
  managerName
  firstCallAt
  latestCallAt
  score
  momentum
  flags
  shrinking
  maxAttendees
  closeDate
  outcome
  forecastCategory
  crmSource
  meddic {
    key
    name
    codes
    from
    value
    code
    atIndex
    status
  }
  calls {
    id
    startedAt
    callType
    durationSeconds
    callScore
    buyerAttendees
  }
`

export const CALL_SCORECARD_DEALS = gql`
  query CallScorecardDeals(
    $filters: CallScorecardFiltersInput
    $view: String
    $search: String
    $stage: String
    $sort: String
    $limit: Int
  ) {
    callScorecardDeals(
      filters: $filters
      view: $view
      search: $search
      stage: $stage
      sort: $sort
      limit: $limit
    ) {
      totalCount
      counts
      stages
      deals {
        ${DEAL_SUMMARY_FIELDS}
      }
      noBuyerCount
      longestNoBuyer {
        ${DEAL_SUMMARY_FIELDS}
      }
    }
  }
`

export const CALL_SCORECARD_DEAL = gql`
  query CallScorecardDeal($key: ID!) {
    callScorecardDeal(key: $key) {
      ${DEAL_SUMMARY_FIELDS}
      pipelineIndex
      expected
      neverRaised
      best
      bestAt
      meddicQuotes
      calls {
        id
        title
        scores
        carriedIn {
          code
          fromIndex
          previous
          now
          status
        }
      }
      nextGaps {
        code
        best
        bestAtIndex
        strongerLine
      }
      plays {
        id
        text
        authorName
        createdAt
      }
    }
  }
`

// ENGG-10235 — the Revenue tab, from the client's Salesforce.
export const CALL_SCORECARDS_REVENUE = gql`
  query CallScorecardsRevenue {
    callScorecardsRevenue {
      connected
      lagging {
        quarter
        closedWon
        closedWonCount
        winRate
        won
        closed
        openPipeline
        openCount
        weightedForecast
      }
      funnel {
        pipeline
        from
        to
        entered
        converted
        conversion
        days
        slowest
      }
      atRisk {
        total
        count
        rows {
          opportunityId
          dealKey
          name
          account
          owner
          amount
          stage
          ageDays
          risks
          signals {
            role
            code
            score
          }
        }
      }
    }
  }
`

export const CALL_SCORECARDS_SYNC_STATUS = gql`
  query CallScorecardsSyncStatus {
    callScorecardsSyncStatus {
      enabled
      nightlySync
      totalCalls
      scoredCalls
      failedCalls
      sources {
        id
        provider
        providerName
        displayName
        settingsPath
        lastCallSyncAt
        callSyncStatus
        callSyncError
      }
      providers {
        key
        name
        settingsPath
      }
      recorder
      connectedProviders
      crm {
        provider
        providerName
        connected
        accountUrl
        opportunities
        openOpportunities
        lastSyncedAt
        syncError
        missingReads
        settingsPath
      }
    }
  }
`

// ENGG-10018 — skill practice built from scored calls
const CALL_PRACTICE_ROLEPLAY_FIELDS = `
  id
  status
  lastError
  title
  source
  difficulty
  format
  timeLimitMinutes
  behaviorCodes
  behaviors {
    ...CallBehaviorFields
  }
  successLooksLike
  personaName
  personaTitle
  personaCompany
  personaDisposition
  triggerLine
  buyerBrief
  primaryRecordedCallId
  assignments {
    id
    userId
    userName
    status
    scenarioStatus
    dueOn
    passingAttempts
    passScore
    trackImpact
    gapScore
    completedAt
    attemptCount
    passedAttemptCount
    bestSkillScore
  }
`

export const CALL_PRACTICE_ROLEPLAY = gql`
  query CallPracticeRoleplay($id: ID!) {
    callPracticeRoleplay(id: $id) {
      ${CALL_PRACTICE_ROLEPLAY_FIELDS}
    }
  }
  ${FRAGMENT_CALL_BEHAVIOR_FIELDS}
`

export const CALL_PRACTICE_SOURCE_CALLS = gql`
  query CallPracticeSourceCalls(
    $userId: ID!
    $code: String!
    $recordedCallId: ID
  ) {
    callPracticeSourceCalls(
      userId: $userId
      code: $code
      recordedCallId: $recordedCallId
    ) {
      recordedCallId
      accountName
      buyerName
      buyerTitle
      callTypeLabel
      startedAt
      durationSeconds
      score
      repQuote
      repAt
      buyerQuote
      buyerAt
      strongerLine
    }
  }
`

export const CALL_PRACTICE_TEAM_GAPS = gql`
  query CallPracticeTeamGaps($userId: ID!, $code: String!) {
    callPracticeTeamGaps(userId: $userId, code: $code) {
      userId
      name
      score
    }
  }
`

// ENGG-10018 — what the admin's Preview call runs a built practice roleplay with.
export const CALL_PRACTICE_PREVIEW = gql`
  query CallPracticePreview($id: ID!) {
    callPracticePreview(id: $id) {
      prompt
      voice
      voiceId
      openingLine
      timeLimitMinutes
    }
  }
`

// "Mock a real call" in the Test Lab, newest first. Polled while any mock is
// running or rendering audio. `transcript` is the mock call; `sourceTurns` the
// pasted real call split by speaker; both scores share one shape (see
// Types::RoleplayTranscriptMockType).
export const ROLEPLAY_TRANSCRIPT_MOCKS = gql`
  query RoleplayTranscriptMocks($templateTaskId: ID!, $limit: Int) {
    roleplayTranscriptMocks(templateTaskId: $templateTaskId, limit: $limit) {
      id
      status
      stage
      errorMessage
      employeeName
      employeeVoice
      sourceTurns
      traineePrompt
      transcript
      endReason
      sourceScore
      mockScore
      scoredAt
      rescoring
      audioStatus
      audioUrl
      audioSegments
      audioError
      voiceOptions
      createdByName
      createdAt
    }
  }
`

// ENGG-10150 — the sales-room MCP server's call log (Settings → MCP tokens →
// Activity), newest first; `beforeId` is the previous page's last row id.
export const GET_MCP_CALLS = gql`
  query McpCalls(
    $roomId: String
    $toolName: String
    $errorsOnly: Boolean
    $beforeId: ID
    $limit: Int
  ) {
    mcpCalls(
      roomId: $roomId
      toolName: $toolName
      errorsOnly: $errorsOnly
      beforeId: $beforeId
      limit: $limit
    ) {
      id
      server
      serverVersion
      createdAt
      userId
      userName
      roomPublicId
      roomName
      credentialKind
      connectorName
      remoteIp
      rpcMethod
      toolName
      arguments
      result
      error
      errorMessage
      durationMs
    }
  }
`

// Roleplay Copilot: an agentic chat about one roleplay. See
// components/location-details/roleplay/copilot.
const ROLEPLAY_COPILOT_TURN_FIELDS = `
  id
  status
  stage
  errorMessage
  stopRequested
  startedByName
  steps
  inputTokens
  outputTokens
  cacheReadTokens
  createdAt
  finishedAt
`

export const ROLEPLAY_COPILOT_CHANGE_FIELDS = `
  id
  threadId
  kind
  title
  rationale
  evidence
  before
  after
  status
  errorMessage
  decidedByName
  decidedAt
  createdAt
`

export const GET_ROLEPLAY_COPILOT = gql`
  query RoleplayCopilot($templateTaskId: ID!) {
    roleplayCopilot(templateTaskId: $templateTaskId) {
      templateTaskId
      configured
      canEdit
      threads {
        id
        title
        createdByName
        updatedAt
        activeTurn { id status }
      }
      documents {
        id
        name
        status
        characters
        errorMessage
        uploadedByName
        createdAt
      }
      notes {
        id
        body
        createdByName
        updatedAt
      }
      openChanges { ${ROLEPLAY_COPILOT_CHANGE_FIELDS} }
    }
  }
`

export const GET_ROLEPLAY_COPILOT_THREAD = gql`
  query RoleplayCopilotThread($id: ID!) {
    roleplayCopilotThread(id: $id) {
      id
      templateTaskId
      title
      updatedAt
      activeTurn { ${ROLEPLAY_COPILOT_TURN_FIELDS} }
      lastTurn { ${ROLEPLAY_COPILOT_TURN_FIELDS} }
      messages {
        id
        role
        text
        status
        turnId
        authorName
        createdAt
        toolCalls
        toolResults
      }
      changes { ${ROLEPLAY_COPILOT_CHANGE_FIELDS} }
    }
  }
`

// ENGG-10202 — the Internal rooms page's "Import Resources tab": what an
// import would copy (offered only when there is a link to copy), and the room
// of the last import, read on page load.
export const RESOURCES_TAB_IMPORT_PREVIEW = gql`
  query ResourcesTabImportPreview {
    resourcesTabImportPreview {
      sections
      links
      skipped
      restricted
      documents
      items {
        resourceId
        title
        section
        outcome
        reason
      }
      room {
        id
        name
        status
      }
    }
  }
`

// ENGG-10202 — the internal rooms the import can write into, read each time
// the dialog opens (the embedded builder changes them in its own cache).
export const INTERNAL_ROOMS_FOR_IMPORT = gql`
  query InternalRoomsForImport {
    rooms(kind: INTERNAL) {
      id
      name
      status
    }
  }
`

// ENGG-10243 — the public employee-app host (the backend's APP_BASE_URL, the
// host of the app's emails and of a room's "Copy app link"); null when unset.
// The admin's own REACT_APP_APP_URL is the host its embeds are signed in on,
// which on production is not the one employees use.
export const GET_APP_BASE_URL = gql`
  query AppBaseUrl {
    appBaseUrl
  }
`

// ENGG-10243 — the admin's "Room settings" dialog for an internal room: every
// internal room of the client, read fresh each time it opens (the embedded
// builder keeps its own cache). The dialog takes its room by id and counts
// the nav positions from the same answer. The audience goes to admins of the
// client only (null otherwise); its term objects are the ones a template
// category gives the Edit Template modal (live terms only), the id lists also
// name terms deleted since.
export const INTERNAL_ROOM_SETTINGS = gql`
  query InternalRoomSettings {
    rooms(kind: INTERNAL) {
      id
      name
      kind
      status
      navEnabled
      navPosition
      landingPage
      audience {
        taxonomyTerms {
          id
          title
          key
          taxonomyId
        }
        excludedTaxonomyTerms {
          id
          title
          key
          taxonomyId
        }
        taxonomyTermIds
        excludedTaxonomyTermIds
        preboarding
        offboarding
      }
    }
  }
`

// ENGG-10291 — one internal room's tabs with who reads each, for the tab's
// "Who sees" dialog, and the room's own flags (which states see the room at
// all). `audience` is sent to admins of the room's client only.
export const INTERNAL_ROOM_TAB_AUDIENCE = gql`
  query InternalRoomTabAudience($id: ID!) {
    room(id: $id) {
      id
      name
      kind
      audience {
        preboarding
        offboarding
      }
      roomTabs {
        id
        name
        parentId
        audience {
          taxonomyTerms {
            id
            title
            key
            taxonomyId
          }
          excludedTaxonomyTerms {
            id
            title
            key
            taxonomyId
          }
          taxonomyTermIds
          excludedTaxonomyTermIds
          preboarding
          offboarding
        }
      }
    }
  }
`

// ENGG-9613 — the current client's Zoom cloud recordings, newest first, for
// the Meeting Recordings page.
export const GET_REMOTE_MEETING_RECORDINGS = gql`
  query RemoteMeetingRecordings($limit: Int, $offset: Int) {
    remoteMeetingRecordings(limit: $limit, offset: $offset) {
      totalCount
      nodes {
        id
        meetingUuid
        topic
        startedAt
        durationMinutes
        hostEmail
        recordingUrl
        passcode
        createdAt
      }
    }
  }
`

// Report Copilot: one conversation, polled while the Copilot answers.
export const GET_REPORT_COPILOT_SESSION = gql`
  query ReportCopilotSession($id: ID!) {
    reportCopilotSession(id: $id) {
      id
      status
      error
      transcript
      draft
    }
  }
`

// ENGG-8024 — Settings > Public Pages. The public form itself is served by
// unauthenticated REST (src/components/public-employee-form), not GraphQL.
export const GET_PUBLIC_EMPLOYEE_FORMS = gql`
  query PublicEmployeeForms {
    publicEmployeeForms {
      ${PUBLIC_EMPLOYEE_FORM_FIELDS}
    }
  }
`

// Enrollable content: whether a Content Library item is listed in the course
// catalog, plus the hidden template + task behind it (null until enrollment was
// turned on once). The Enrollment tab edits both with the regular template
// mutations.
export const GET_CONTENT_ENROLLMENT = gql`
  query ContentEnrollment(
    $contentType: ContentEnrollmentType!
    $contentId: ID!
  ) {
    contentEnrollment(contentType: $contentType, contentId: $contentId) {
      enrollable
      templateCategory {
        ...TemplateCategoryListFields
      }
      templateTask {
        ...TemplateTaskFields
      }
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS}
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

// ENGG-10064 — super-admin Zoom daily-quota usage
export const ZOOM_API_USAGE = gql`
  query ZoomApiUsage(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $hostEmail: String
    $trigger: String
    $endpoint: String
    $status: String
    $limit: Int
    $offset: Int
  ) {
    zoomApiUsage(
      from: $from
      to: $to
      hostEmail: $hostEmail
      trigger: $trigger
      endpoint: $endpoint
      status: $status
      limit: $limit
      offset: $offset
    ) {
      totalCalls
      failedCalls
      rateLimitedCalls
      hosts {
        hostEmail
        calls
        rateLimitLimit
        rateLimitRemaining
        lastCallAt
        retryAfter
      }
      daily {
        date
        calls
        failed
        rateLimited
      }
      byEndpoint {
        key
        calls
        failed
      }
      byTrigger {
        key
        calls
        failed
      }
      calls {
        totalCount
        nodes {
          id
          createdAt
          httpMethod
          endpoint
          resourceId
          statusCode
          durationMs
          hostEmail
          rateLimitType
          rateLimitLimit
          rateLimitRemaining
          retryAfter
          trigger
          taskId
          groupTaskId
          templateTaskId
          calendarEventId
          partnerEventId
          templateTaskTitle
          templateCategoryId
          taskOwnerName
          groupTaskDueDate
        }
      }
    }
  }
`

// ENGG-10357: the rubric the "Add scorecard" modal scores against — the task's
// SCORECARD questions and their scored options — plus the thresholds for the
// live pass/fail hint.
export const ROLEPLAY_SCORECARD_RUBRIC = gql`
  query RoleplayScorecardRubric($taskId: ID!) {
    task(id: $taskId) {
      id
      chatMaxScore
      chatPassingScore
      questions {
        id
        title
        order
        quizType
        options {
          id
          title
          score
          order
        }
      }
    }
  }
`

// ENGG-10402: the task's events, for "Edit an existing scorecard" in the
// employee's task drawer. Finished roleplay attempts carry a
// questionsSubmission whose answers pre-fill the rubric.
export const ROLEPLAY_TASK_ATTEMPTS = gql`
  query RoleplayTaskAttempts($taskId: ID!) {
    taskEvents(taskId: $taskId) {
      id
      eventType
      createdAt
      chatSession {
        id
      }
      questionsSubmission {
        id
        score
        totalScore
        questions {
          id
          templateTaskQuestion {
            id
          }
          answers {
            id
            answer
            comment
            templateTaskQuestionOption {
              id
            }
          }
        }
      }
    }
  }
`
